import {api} from '../../api.js';
import {CONTRACT_CLASSIFICATION_VERSION, contractClassificationPreview, isCurrentContractClassification, pendingContractClassification, type ContractClassification} from '../../../common/legalContractClassification.js';
import type {AuthSnapshot} from '../../auth.js';
import {apiUrl} from '../../config.js';
import {downloadCloudFile} from '../../download.js';
import {escapeHtml, renderLayout} from '../../layout.js';
import {navigate} from '../../router.js';
import {activeLegalReviewTaskId, activeLegalReviewTaskType, activeTaskId, rememberLegalReviewTaskSelection} from '../../core/system-pages/index.js';
import {archiveActions, archivePageShell, mountArchivePage} from './archivePage.js';
import {
  buildContractTypePreviewParts,
  CONSTRUCTION_SERIES_CONTRACT_TYPE,
  CONTRACT_TYPE_SERIES_OPTIONS,
  formatContractTypePreviewLabel,
  isConstructionContractTypeLabel,
  isSaleContractSeriesLabel,
  isLeaseContractSeriesLabel,
  isServiceContractSeriesLabel,
  isMandateContractSeriesLabel,
  isFinanceContractSeriesLabel,
  isLogisticsContractSeriesLabel,
  isSpecialLawContractSeriesLabel,
  isOtherMixedContractSeriesLabel,
  isNonContractDocumentSeriesLabel,
  isEquityGovernanceSeriesLabel,
  LEGACY_CONTRACT_TYPE_TO_SERIES,
  resolveContractTypeSeries,
  SYSTEM_AUTO_CONTRACT_TYPE,
} from '../../../common/legalContractTypeSeries.js';
import {formatPartyOwnershipBrief, summarizePartyRelations} from '../../../common/legalPartyStateOwnership.js';
import {
  isPoorPreviewMainContent,
  PREVIEW_MAIN_CONTENT_UNAVAILABLE,
  sanitizePreviewContractAmount,
  shouldRefreshPreviewContractAmount,
  shouldRefreshPreviewMainContent,
  shouldRefreshPreviewTypeAnalysis,
} from '../../../common/legalPreviewSummary.js';
import {isTeamOrganization} from '../../../common/organizationKind.js';
import {splitCombinedPartyEntries} from '../../../common/legalPartyNameSplit.js';
import {legalPartyNamesLikelyMatch,matchLegalReviewPositionToPartyNames} from '../../../common/legalReviewPositionMatch.js';
import {isLegalFeatureEnabled} from '../../core/moduleAccess.js';

type CloudFile={id:string;originalName:string;kind:string;status:string;sizeBytes:number;mimeType?:string;createdAt?:string};
type ReviewScopes={legal:boolean;business:boolean;compliance:boolean};
type ScopeAnnotationStyle={color:string;font:string};
type ScopeAnnotationStyles={legal:ScopeAnnotationStyle;business:ScopeAnnotationStyle;compliance:ScopeAnnotationStyle};
type ComplianceLibraryFolderRef={folderId:string;folderPath:string};
type ComplianceLibraryFolders={personal:ComplianceLibraryFolderRef|null;enterprise:ComplianceLibraryFolderRef|null};
type WorkspaceFolder={id:string;name:string;parentId?:string;systemKey?:string};
type LegalRiskUserDecision='accepted'|'dismissed';
type LegalRisk={id:string;severity:'high'|'medium'|'low';reviewType?:'legal'|'business'|'compliance';category:string;description:string;suggestedRevision:string;originalText?:string;userDecision?:LegalRiskUserDecision;userSuggestedRevision?:string;annotateIncludeDescription?:boolean;annotateIncludeSuggestion?:boolean};
type WritingGuidanceItem={id:string;aspect:string;category:string;description:string;suggestedRevision:string;originalText?:string;shortcomings?:string};
type LegalReviewSpecialtyTrace={kind:'sale'|'governance'|'lease'|'construction'|'service'|'mandate'|'finance'|'logistics'|'special-law'|'other-mixed';checklistVersion:string;ruleEngineVersion:string;contractFamily:string;contractFamilyLabel:string;resolvedContractType:string;reviewPositionRole:string};
type LegalReviewRun={id:string;sourceFileId:string;sourceFileName:string;attachmentFileIds?:string[];annotatedFileId:string;annotatedFileName:string;annotatedCommentCount:number;revisedFileId?:string;revisedFileName?:string;revisedChangeCount?:number;riskCount:number;reviewPositionCompany:string;reviewerName:string;contractType?:string;reviewDepth?:'standard'|'deep';specialtyReview?:LegalReviewSpecialtyTrace;reviewFocus?:string[];scopes?:ReviewScopes;status:'completed'|'failed'|'in_progress';reviewProgress?:number;reviewPhase?:string;warnings:string[];fillReminders?:string[];error:string;writingGuidance?:WritingGuidanceItem[];nonContractDocumentFamily?:string;nonContractDocumentFamilyLabel?:string;createdAt:string;completedAt:string;userReviewCompletedAt?:string};
type ReviewFiles={mainFiles:CloudFile[];attachmentFiles:CloudFile[];annotatedFiles:CloudFile[];previewFiles?:CloudFile[];revisedFiles?:CloudFile[]};
type CompliancePolicyRecommendation={fileId:string;fileName:string;folderPath:string;score:number;reason?:string;source?:'ai'|'keyword'};
type QuickProfile={classification?:ContractClassification;fileId:string;sourceFileName:string;status:'ready'|'needs-manual-review'|'failed';contractTitle:string;contractType:string;typeConfidence:number;parties:Array<{name:string;role:string;ownershipKind?:string;ownershipLabel?:string;ownershipTag?:string;ownershipBasis?:string;econKind?:string;actualController?:string;creditCode?:string;shareholders?:Array<{name:string;ratio?:number}>;relatedNames?:string[]}>;partyRelation?:string;suggestedReviewPosition:string;evidence:string[];warnings:string[];fillReminders?:string[];detectedAt:string;recognitionMethod?:'ai'|'local-fallback';previewFileId?:string;previewFileName?:string;recommendedCompliancePolicies?:CompliancePolicyRecommendation[]};
const CONTRACT_FILL_REMINDER_SUFFIX='，请补充完善';
function isContractFillReminderLine(message:string):boolean{return String(message??'').trim().endsWith(CONTRACT_FILL_REMINDER_SUFFIX)}
function mergeContractFillReminders(...groups:Array<string[]|undefined>):string[]{const seen=new Set<string>();const out:string[]=[];for(const group of groups){for(const item of group??[]){const value=String(item??'').trim();if(!value||seen.has(value))continue;seen.add(value);out.push(value)}}return out}
function renderContractFillRemindersPanel(items:string[],heading='待补充信息'):string{const list=mergeContractFillReminders(items);if(!list.length)return'';return`<section class="cr-fill-reminder-panel"><div class="cr-fill-reminder-head"><span class="cr-section-kicker">填写提醒</span><h3>${escapeHtml(heading)}</h3><p>以下缺填项已列入风险清单，并会写入 Word 批注（以补全提示为主）；复核时可采纳、编辑或标记不采纳。</p></div><ul class="cr-fill-reminder-list">${list.map(item=>`<li>${escapeHtml(item)}</li>`).join('')}</ul></section>`}
type ProjectPreferenceOverrides=Partial<Pick<Preferences,'reviewPositionCompany'|'reviewerName'|'contractType'|'reviewDepth'|'reviewFocus'|'scopes'|'scopeAnnotationStyles'|'complianceLibraryScope'|'complianceLibraryFolders'|'selectedCompliancePolicyFileIds'|'enhancedFeatures'|'defaultAnnotateIncludeDescription'|'defaultAnnotateIncludeSuggestion'>>;
type ProjectPreferencesRecord={projectId:string;overrides:ProjectPreferenceOverrides;savedAt?:string;contractTypeConfirmation?:{fileId:string;contractType:string}};
type ReviewStatus={review:LegalReviewRun|null;risks:LegalRisk[];writingGuidance?:WritingGuidanceItem[];protectedFileIds?:string[];quickProfiles?:QuickProfile[];taskScheme?:TaskSchemeRecord|null;projectPreferences?:ProjectPreferencesRecord|null;preferences?:EffectivePreferences|null;preferencesConfigured?:boolean;files:ReviewFiles};
type ComplianceLibraryScope='personal'|'enterprise';
type Preferences={reviewPositionCompany:string;reviewerName:string;contractType:string;reviewDepth:'standard'|'deep';reviewFocus:string[];scopes:ReviewScopes;scopeAnnotationStyles:ScopeAnnotationStyles;complianceLibraryScope:ComplianceLibraryScope;complianceLibraryFolders:ComplianceLibraryFolders;selectedCompliancePolicyFileIds:string[];enhancedFeatures:{enterpriseOwnershipLookup:boolean};defaultAnnotateIncludeDescription?:boolean;defaultAnnotateIncludeSuggestion?:boolean};
type TaskSchemeRecord=Preferences&{savedAt?:string;savedBy?:string;savedByName?:string};
type EffectivePreferences=Preferences&{savedAt?:string;savedBy?:string;savedByName?:string;usesTaskScheme?:boolean;hasProjectOverrides?:boolean};
type Stage='overview'|'materials'|'settings'|'analysis'|'risks'|'deliverables';
type LegalReviewProject={id:string;name:string;createdAt:string;updatedAt:string;archived?:boolean;archivedAt?:string;archivedBy?:string;archivedByName?:string};
type ProjectSnapshot={project:LegalReviewProject;files:ReviewFiles;review:LegalReviewRun|null;risks:LegalRisk[];writingGuidance?:WritingGuidanceItem[];main:CloudFile|null;quickProfile:QuickProfile|null;protectedIds:Set<string>;preferences:EffectivePreferences|null;preferencesConfigured:boolean;taskScheme:TaskSchemeRecord|null;projectPreferences:ProjectPreferencesRecord|null};
type UserOrganization={id:string;name:string;organizationType?:'personal'|'team';organizationCode?:string;status?:string};
const legalReviewEnterpriseNamesByTask=new Map<string,string[]>();
const legalReviewHasEnterpriseByTask=new Map<string,boolean>();
/** 总览页缓存的制度库上下文，供行内项目设置复用。 */
let overviewComplianceContext:ComplianceContext|null=null;

/**
 * 判断名称是否像个人空间名称（不能作为审核立场「本企业」主体）。
 * @param name 待判断名称
 */
function isPersonalWorkspaceName(name:string):boolean{
  const trimmed=String(name??'').trim();
  if(!trimmed)return false;
  if(trimmed==='已注销个人空间')return true;
  return trimmed.endsWith('的个人空间');
}

/**
 * 判断组织是否为可用的团队空间。
 * @param organization 组织摘要
 */
function isActiveEnterpriseOrganization(organization:UserOrganization):boolean{
  return isTeamOrganization(organization)&&organization.status!=='closed'&&organization.status!=='deleted';
}

/**
 * 解析可用于团队空间制度库的工商主体名称列表。
 * @param task 当前法务任务
 * @param auth 当前登录上下文
 * @param organizations 用户可访问的全部组织
 */
function resolveLegalReviewEnterpriseNames(_task:{companyName?:string;organizationCode?:string},auth:AuthSnapshot,organizations:UserOrganization[]):string[]{
  const seen=new Set<string>();
  const names:string[]=[];
  const push=(value:string)=>{const trimmed=value.trim();if(!trimmed||isPersonalWorkspaceName(trimmed)||seen.has(trimmed))return;seen.add(trimmed);names.push(trimmed)};
  // 审核立场只来源于用户实际加入的企业；任务上记录的公司名称不能额外生成一个企业主体。
  organizations.filter(isActiveEnterpriseOrganization).forEach(organization=>push(organization.name));
  if(isTeamOrganization(auth.organization))push(auth.organization.name);
  return names;
}

/** 缓存当前任务可用的企业主体名称，供设置面板复用。 */
function rememberLegalReviewEnterpriseNames(taskId:string,names:string[]):void{
  legalReviewEnterpriseNamesByTask.set(taskId,names);
}
/** 缓存当前任务是否可用团队空间制度库。 */
function rememberLegalReviewHasEnterprise(taskId:string,hasEnterprise:boolean):void{
  legalReviewHasEnterpriseByTask.set(taskId,hasEnterprise);
}
function legalReviewHasEnterprise(taskId:string):boolean{
  return legalReviewHasEnterpriseByTask.get(taskId)??false;
}

/** 读取当前任务可用的企业主体名称。 */
function legalReviewEnterpriseNames(taskId:string,fallbackCompanyName=''):string[]{
  const cached=legalReviewEnterpriseNamesByTask.get(taskId);
  if(cached?.length)return cached;
  if(fallbackCompanyName&&!isPersonalWorkspaceName(fallbackCompanyName))return [fallbackCompanyName];
  return [];
}

type SettingsFormMode='task'|'project';
type ComplianceLibrarySummary={scope:'personal'|'enterprise';folderId:string;folderName:string;folderPath?:string;fileCount:number;organizationName:string};
type ComplianceContext={hasEnterprise:boolean;enterpriseOrgId:string;personal:ComplianceLibrarySummary|null;enterprise:ComplianceLibrarySummary|null};
type TaskSchemeStats={totalProjects:number;customizedProjects:number;inheritedProjects:number;schemeConfigured:boolean;schemeSavedAt?:string};

/** 任务方案「自定义立场」单选项的内部取值，真正主体名称写在隐藏域。 */
const TASK_CUSTOM_POSITION_VALUE='__custom__';

/**
 * 解析任务级默认审核立场：仅本人；未填写姓名时回退为「本人」。
 * @param userDisplayName 用户显示名
 */
function defaultTaskReviewPosition(userDisplayName:string):string{
  return String(userDisplayName??'').trim()||'本人';
}

/**
 * 当前保存的立场是否应视为自定义（不是用户本人）。
 */
function isTaskCustomReviewPosition(currentValue:string,userDisplayName:string):boolean{
  const current=String(currentValue??'').trim();
  const self=String(userDisplayName??'').trim();
  if(!current||current===TASK_CUSTOM_POSITION_VALUE)return Boolean(current);
  return current!==self&&current!=='本人';
}

/** 规范化批注颜色（6 位十六进制，不含 #）。 */
function normalizeAnnotationColor(value:string):string{
  const raw=String(value??'').trim().replace(/^#/,'').toUpperCase();
  return/^([0-9A-F]{6})$/.test(raw)?raw:DEFAULT_ANNOTATION_COLOR;
}
/** 规范化批注字体。 */
function normalizeAnnotationFont(value:string):string{
  const font=String(value??'').trim();
  return ANNOTATION_FONT_OPTIONS.includes(font)?font:DEFAULT_ANNOTATION_FONT;
}
/** 默认三类审核范围批注样式（初始一致）。 */
function defaultScopeAnnotationStyles():ScopeAnnotationStyles{
  const base={color:DEFAULT_ANNOTATION_COLOR,font:DEFAULT_ANNOTATION_FONT};
  return{legal:{...base},business:{...base},compliance:{...base}};
}
/** 规范化单类审核范围批注样式。 */
function normalizeScopeAnnotationStyle(value:Partial<ScopeAnnotationStyle>|null|undefined,fallback:ScopeAnnotationStyle):ScopeAnnotationStyle{
  return{color:normalizeAnnotationColor(value?.color??fallback.color),font:normalizeAnnotationFont(value?.font??fallback.font)};
}
/** 规范化三类审核范围批注样式。 */
function normalizeScopeAnnotationStyles(value:Partial<ScopeAnnotationStyles>|null|undefined):ScopeAnnotationStyles{
  const fallback=defaultScopeAnnotationStyles();
  return{
    legal:normalizeScopeAnnotationStyle(value?.legal,fallback.legal),
    business:normalizeScopeAnnotationStyle(value?.business,fallback.business),
    compliance:normalizeScopeAnnotationStyle(value?.compliance,fallback.compliance),
  };
}
/** 规范化制度库文件夹引用。 */
function normalizeComplianceLibraryFolderRef(value:Partial<ComplianceLibraryFolderRef>|null|undefined):ComplianceLibraryFolderRef|null{
  const folderId=String(value?.folderId??'').trim();
  if(!folderId)return null;
  const folderPath=String(value?.folderPath??'').trim().slice(0,500);
  return{folderId,folderPath:folderPath||'制度文件夹'};
}
/** 规范化个人/企业制度库路径。 */
function normalizeComplianceLibraryFolders(value:Partial<ComplianceLibraryFolders>|null|undefined):ComplianceLibraryFolders{
  return{
    personal:normalizeComplianceLibraryFolderRef(value?.personal??null),
    enterprise:normalizeComplianceLibraryFolderRef(value?.enterprise??null),
  };
}
/** 根据文件夹列表构建路径标签。 */
function buildFolderPathLabel(folders:WorkspaceFolder[],folderId:string):string{
  const segments:string[]=[];
  let cursor=folderId;
  const guard=new Set<string>();
  while(cursor&&!guard.has(cursor)){
    guard.add(cursor);
    const folder=folders.find(item=>item.id===cursor);
    if(!folder)break;
    segments.unshift(folder.name);
    cursor=folder.parentId||'';
  }
  return segments.join(' / ')||'根目录';
}
/** 加载空间文件夹列表。 */
async function loadWorkspaceFolders(scope:'personal'|'enterprise',enterpriseOrgId=''):Promise<WorkspaceFolder[]>{
  const url=scope==='enterprise'&&enterpriseOrgId
    ?apiUrl(`/api/files/workspace/folders?enterpriseOrganizationId=${encodeURIComponent(enterpriseOrgId)}`)
    :apiUrl('/api/files/workspace/folders');
  const payload=await api.get<{folders:WorkspaceFolder[]}>(url);
  return payload.folders??[];
}
/** 根据色号解析颜色名称；未命中预设时为「自定义」。 */
function annotationColorLabel(hex:string):string{
  return PRESET_ANNOTATION_COLORS.find(item=>item.hex===hex)?.name??'自定义';
}
/** 色块上的文字颜色，浅色底用深字。 */
function annotationChipTextColor(hex:string):string{
  const value=parseInt(hex,16);
  const r=(value>>16)&255,g=(value>>8)&255,b=value&255;
  return(r*299+g*587+b*114)/1000>168?'#1A3540':'#ffffff';
}
/**
 * 生成审核范围批注样式控件：小巧精致圆角色标 + 行内可直接编辑 HEX 色号 + 字体下拉。
 * @param scope 审核维度（legal | business | compliance）
 * @param style 批注样式设置（颜色与字体）
 * @returns HTML 模板字符串
 */
function scopeAnnotationStyleControls(scope:'legal'|'business'|'compliance',style:ScopeAnnotationStyle):string{
  const hex=normalizeAnnotationColor(style.color);
  const fontOptions=ANNOTATION_FONT_OPTIONS.map(font=>`<option value="${escapeHtml(font)}" ${style.font===font?'selected':''}>${escapeHtml(font)}</option>`).join('');
  const presetItems=PRESET_ANNOTATION_COLORS.map(item=>`<button type="button" class="cr-color-preset-pill${hex===item.hex?' is-selected':''}" data-color-select="${item.hex}" title="${item.name} (#${item.hex})">
      <span class="cr-color-dot${item.id==='white'?' is-white':''}" style="background:#${item.hex}"></span>
      <span class="cr-color-item-name">${item.name}</span>
    </button>`).join('');
  return`<div class="cr-scope-annotation-style" data-scope-style="${scope}">
    <div class="cr-color-picker-widget" data-color-picker-widget="${scope}">
      <button type="button" class="cr-color-swatch-btn" data-color-trigger="${scope}" title="点击展开调色盘" aria-expanded="false" aria-label="${scope}批注颜色" style="background-color:#${hex}">
      </button>
      <div class="cr-color-hex-box" title="直接输入 6 位 HEX 色号（例如 C04000、339CFF）">
        <span class="cr-color-hex-prefix">#</span>
        <input type="text" class="cr-color-inline-input" data-color-inline-input="${scope}" value="${hex}" maxlength="6" placeholder="C04000" autocomplete="off" spellcheck="false" aria-label="${scope}批注色号">
      </div>
      <input type="hidden" data-settings-scope-color="${scope}" value="${hex}">
      <div class="cr-color-popover-menu" data-color-menu="${scope}" hidden>
        <div class="cr-color-popover-title">预设批注颜色</div>
        <div class="cr-color-preset-grid">${presetItems}</div>
      </div>
    </div>
    <select class="cr-select cr-scope-style-font" data-settings-scope-font="${scope}" aria-label="${scope}批注字体">${fontOptions}</select>
  </div>`;
}
/** 生成制度库文件夹路径选择区 HTML。 */
function complianceFolderPathHtml(scope:'personal'|'enterprise',folderRef:ComplianceLibraryFolderRef|null,summary:ComplianceLibrarySummary|null):string{
  const folderId=folderRef?.folderId||summary?.folderId||'';
  const folderPath=folderRef?.folderPath||summary?.folderPath||summary?.folderName||'';
  const display=folderPath||'点击选择制度文件夹路径';
  return`<div class="cr-compliance-folder-path" data-compliance-folder-display="${scope}">
    <span class="cr-compliance-folder-path-label" title="${escapeHtml(display)}">${escapeHtml(display)}</span>
    <button type="button" class="cr-button cr-button-quiet cr-scheme-btn-sm" data-pick-compliance-folder="${scope}">选择路径</button>
    <input type="hidden" data-compliance-folder-id="${scope}" value="${escapeHtml(folderId)}">
    <input type="hidden" data-compliance-folder-path="${scope}" value="${escapeHtml(folderPath)}">
  </div>`;
}
/** 加载合规管理制度库摘要。 */
async function loadComplianceLibrarySummary(scope:'personal'|'enterprise',folderId=''):Promise<ComplianceLibrarySummary|null>{
  try{
    const query=new URLSearchParams({scope});
    if(folderId)query.set('folderId',folderId);
    const payload=await api.get<{scope:'personal'|'enterprise';folder:{id:string;name:string}|null;fileCount:number;organizationName?:string}>(apiUrl(`/api/files/workspace/compliance-library?${query}`));
    if(!payload.folder?.id)return null;
    return{scope:payload.scope,folderId:payload.folder.id,folderName:payload.folder.name||'合规管理制度',folderPath:payload.folder.name||'合规管理制度',fileCount:Number(payload.fileCount??0),organizationName:String(payload.organizationName??'')};
  }catch{return null}
}
type CompliancePolicyBrowseResult={
  libraryRootId:string;
  currentFolderId:string;
  currentFolderName:string;
  currentFolderPath:string;
  parentFolderId:string|null;
  childFolders:Array<{id:string;name:string}>;
  files:Array<{id:string;fileName:string;folderPath:string}>;
  folderName:string;
  organizationName:string;
  warnings:string[];
};
/** 解析制度库浏览请求参数。 */
function resolveCompliancePolicyBrowseQuery(container:HTMLElement,complianceContext?:ComplianceContext,currentFolderId=''):{scope:'personal'|'enterprise';libraryFolderId:string;currentFolderId:string}{
  const scope=container.querySelector<HTMLInputElement>('input[name="complianceLibraryScope"]:checked')?.value==='enterprise'?'enterprise':'personal';
  const summary=scope==='enterprise'?complianceContext?.enterprise:complianceContext?.personal;
  const libraryFolderId=resolveComplianceFolderId(container,scope,summary??null);
  return{scope,libraryFolderId,currentFolderId};
}
/** 列出当前制度库路径下可添加的合规制度文件（全量，供名称回填）。 */
async function loadCompliancePolicyCatalog(container:HTMLElement,complianceContext?:ComplianceContext):Promise<PolicyCatalogItem[]>{
  const {scope,libraryFolderId}=resolveCompliancePolicyBrowseQuery(container,complianceContext);
  const query=new URLSearchParams({scope});
  if(libraryFolderId)query.set('folderId',libraryFolderId);
  try{
    const payload=await api.get<{files:Array<{id:string;fileName:string;folderPath:string}>}>(apiUrl(`/api/files/workspace/compliance-library/files?${query}`));
    return(payload.files??[]).map(file=>({id:file.id,fileName:file.fileName,folderPath:file.folderPath}));
  }catch{return[]}
}
/** 逐级浏览制度库当前文件夹。 */
async function browseCompliancePolicyFolder(container:HTMLElement,complianceContext?:ComplianceContext,currentFolderId=''):Promise<CompliancePolicyBrowseResult|null>{
  const {scope,libraryFolderId,currentFolderId:currentId}=resolveCompliancePolicyBrowseQuery(container,complianceContext,currentFolderId);
  const query=new URLSearchParams({scope});
  if(libraryFolderId)query.set('folderId',libraryFolderId);
  if(currentId)query.set('currentFolderId',currentId);
  try{
    return await api.get<CompliancePolicyBrowseResult>(apiUrl(`/api/files/workspace/compliance-library/browse?${query}`));
  }catch{return null}
}

/** 汇总任务默认方案影响范围。 */
function computeTaskSchemeStats(taskId:string,snapshots:ProjectSnapshot[]):TaskSchemeStats{
  const customizedProjects=snapshots.filter(snapshot=>Boolean(snapshot.preferences?.hasProjectOverrides)).length;
  const scheme=taskSchemeCache.get(taskId);
  return{
    totalProjects:snapshots.length,
    customizedProjects,
    inheritedProjects:Math.max(0,snapshots.length-customizedProjects),
    schemeConfigured:Boolean(scheme?.savedAt),
    schemeSavedAt:scheme?.savedAt,
  };
}

const BASE='/tax-assistant/legal-review';
const activeProjectKey=(taskId:string)=>`tax-assistant-legal-review-active-project:${taskId}`;
const expandedProjectsKey=(taskId:string)=>`tax-assistant-legal-review-expanded-projects:${taskId}`;
const previewExpandedProjectsKey=(taskId:string)=>`tax-assistant-legal-review-preview-expanded:${taskId}`;
/** 工作台行内「审核设置」展开状态（仅当前页面会话，刷新后默认收起）。 */
const settingsExpandedByTask=new Map<string,Set<string>>();
type ProjectExpandMode='attachment'|'preview'|'settings';
const overviewFocusKey=(taskId:string)=>`tax-assistant-legal-review-overview-focus:${taskId}`;
/** 合同审查模块页内主标题（与工作台任务名解耦）。 */
const LEGAL_REVIEW_WORKBENCH_TITLE='合同文件审查工作台';
/** 工作台正文上传建议（文字版）。 */
const LEGAL_REVIEW_TEXT_UPLOAD_HINT='文字版（可复制文字的 Word / PDF）建议单份不超过 30 MB、200 页，建设工程等长合同按此准备即可较完整审核。';
/** 工作台扫描件上传建议（正文与附件共用）。 */
const LEGAL_REVIEW_SCAN_UPLOAD_HINT='扫描件或图片建议单份不超过 8 MB、12 页；超过 24 页只识别前 24 页。';
/** 工作台新建项目旁的上传体积与页数建议。 */
const LEGAL_REVIEW_UPLOAD_SIZE_HINT='建议单次上传的文字版文件在 30 MB / 200 页以内，扫描件在 8 MB / 12 页以内';
/** 工作台「新建项目」下方提示：先说明怎么建项目，再写文件大小建议。 */
const LEGAL_REVIEW_CREATE_PROJECT_HINT=`点创建新任务，或者将合同正文拖拽至“新建项目”按钮上，${LEGAL_REVIEW_UPLOAD_SIZE_HINT}`;
/** 空间存储上限说明，避免把 120 MB 当成审核能力。 */
const LEGAL_REVIEW_STORAGE_UPLOAD_HINT='空间允许上传至 120 MB，超出上述建议可能导致识别不全或审核失败。';

/**
 * 附件展开区的输入建议。
 * @returns 提示段落 HTML
 */
function legalReviewAttachmentUploadHintHtml():string{
  return`<p class="cr-project-attachments-hint">附件用于核对正文，不单独出批注。文字版同样建议不超过 30 MB、200 页；${LEGAL_REVIEW_SCAN_UPLOAD_HINT} 大份扫描材料请先压缩、拆页，或转成可复制文字后再传。也可不传附件，直接审核正文。</p>`;
}
/** 顶部流程条不展示的步骤（路由仍保留，供「查看结果」等深链使用）。 */
const NAV_HIDDEN_STAGES=new Set<Stage>(['overview','materials','analysis','risks']);
const STAGES:Array<{id:Stage;index:string;label:string}>=[
  {id:'overview',index:'总览',label:'工作台'},
  {id:'materials',index:'01',label:'材料准备'},
  {id:'settings',index:'01',label:'默认任务方案'},
  {id:'analysis',index:'03',label:'智能审查'},
  {id:'risks',index:'04',label:'风险复核'},
  {id:'deliverables',index:'02',label:'归档查询'},
];
const PROJECT_CONTRACT_TYPE_OPTIONS=[...CONTRACT_TYPE_SERIES_OPTIONS];
const TASK_SCHEME_CONTRACT_TYPES=[SYSTEM_AUTO_CONTRACT_TYPE,...PROJECT_CONTRACT_TYPE_OPTIONS];

/**
 * 将预览识别或历史保存的类型收敛为下拉框中的标准选项。
 */
function normalizeContractTypeForSelect(type:string):string{
  const value=String(type??'').trim();
  if(!value)return value;
  if(value===SYSTEM_AUTO_CONTRACT_TYPE)return value;
  const mapped=LEGACY_CONTRACT_TYPE_TO_SERIES[value];
  if(mapped)return mapped;
  const series=resolveContractTypeSeries(value);
  if((CONTRACT_TYPE_SERIES_OPTIONS as readonly string[]).includes(series))return series;
  if((CONTRACT_TYPE_SERIES_OPTIONS as readonly string[]).includes(value))return value;
  return value;
}

/** 根据快速预审结果推荐项目设置中的合同类型系列。 */
function recommendedContractTypeSeriesFromProfile(profile:QuickProfile|null):string{
  const c=profile?.classification;
  return c&&isCurrentContractClassification(c)&&c.status==='accepted'?c.series:'';
}

/** 用户是否在项目设置中手动保存过合同类型。 */
function projectContractTypeManuallyOverridden(snapshot:ProjectSnapshot):boolean{
  return snapshot.projectPreferences?.overrides?.contractType!==undefined
    ||Boolean(snapshot.main&&snapshot.projectPreferences?.contractTypeConfirmation?.fileId===snapshot.main.id);
}

/**
 * 合同类型下拉选项：不展开建设工程细分类型，仅保留「建设工程系列合同」。
 */
function buildContractTypeSelectOptions(currentValue:string,recognizedType:string,includeSystemAuto=false):string[]{
  const base=includeSystemAuto?TASK_SCHEME_CONTRACT_TYPES:PROJECT_CONTRACT_TYPE_OPTIONS;
  const candidates=[...base,normalizeContractTypeForSelect(currentValue),normalizeContractTypeForSelect(recognizedType)].filter(Boolean);
  return[...new Set(candidates)].filter(item=>{
    if(item==='公司治理协议系列'||item==='股权交易与公司投融资系列'||item==='合伙合同系列')return false;
    if(item===CONSTRUCTION_SERIES_CONTRACT_TYPE)return true;
    if(includeSystemAuto&&isConstructionContractTypeLabel(item)&&item!==CONSTRUCTION_SERIES_CONTRACT_TYPE)return false;
    return true;
  });
}

/**
 * 项目审核设置：未保存过项目级方案时，合同类型默认采用快速预审识别结果；
 * 审核立场在识别主体与默认方案立场一致或高度相近时推荐正文主体。
 */
/** 用户是否在项目设置中手动保存过审核立场。 */
function projectReviewPositionManuallyOverridden(snapshot:ProjectSnapshot):boolean{
  return snapshot.projectPreferences?.overrides?.reviewPositionCompany!==undefined;
}

function resolveProjectSettingsPreferences(taskId:string,projectId:string,companyName:string,snapshot:ProjectSnapshot):Preferences{
  const loaded=loadPreferences(taskId,projectId,companyName,snapshot);
  const normalizedLoaded=normalizeContractTypeForSelect(loaded.contractType);
  const recommendedSeries=recommendedContractTypeSeriesFromProfile(snapshot.quickProfile);
  const next={...loaded,contractType:!projectContractTypeManuallyOverridden(snapshot)&&recommendedSeries?normalizeContractTypeForSelect(recommendedSeries):normalizedLoaded};
  if(!projectReviewPositionManuallyOverridden(snapshot)){
    const partyNames=(snapshot.quickProfile?.parties??[]).map(item=>item.name);
    const matched=matchLegalReviewPositionToPartyNames(loaded.reviewPositionCompany,partyNames);
    if(matched)next.reviewPositionCompany=matched;
  }
  return normalizePreferences(next);
}

/** 下拉选项展示文案：有具体识别名时显示「系列 · 具体识别名」。 */
function contractTypeSelectOptionLabel(option:string,recognizedType:string,mode:SettingsFormMode):string{
  const value=String(option??'').trim();
  if(!value||mode==='task')return value;
  const recognized=String(recognizedType??'').trim();
  if(value===recognized){
    const parts=buildContractTypePreviewParts(recognized);
    if(parts.series&&parts.specific)return formatContractTypePreviewLabel(parts.series,parts.specific);
  }
  const parts=buildContractTypePreviewParts(value);
  if(parts.series&&parts.specific&&value===parts.specific)return parts.label;
  if((CONTRACT_TYPE_SERIES_OPTIONS as readonly string[]).includes(value))return value;
  return parts.label||value;
}
const FOCUS_OPTIONS=['付款与结算','交付与验收','违约责任','解除与终止','争议解决','知识产权','保密与数据','担保与增信'];
const CR_ICON_FOLDER_OPEN='/assets/icons/folder-open.svg';
const CR_ICON_FOLDER_CLOSED='/assets/icons/folder-closed.svg';
const CR_ICON_DELETE='/assets/icons/delete.svg';
const CR_ICON_ATTACHMENT_EMPTY='/assets/icons/attachment-empty.svg';
const CR_ICON_ATTACHMENT_UPLOADED='/assets/icons/attachment-uploaded.svg';
const CR_ICON_SETTING_IDLE='/assets/icons/setting-idle.svg';
const CR_ICON_SETTING_ACTIVE='/assets/icons/setting.svg';
const CR_ICON_REVIEW_START_DISABLED='/assets/icons/review-start-disabled.svg';
const CR_ICON_REVIEW_START_READY='/assets/icons/review-start-ready.svg';
const CR_ICON_PREVIEW_CLOSED='/assets/icons/preview-closed.svg';
const CR_ICON_PREVIEW_OPEN='/assets/icons/preview.svg';
const CR_ICON_ARCHIVE_READY='/assets/icons/archive-ready.svg';
const CR_ICON_ARCHIVE_DISABLED='/assets/icons/archive-disabled.svg';
const ANNOTATION_FONT_OPTIONS=['楷体','宋体','黑体','微软雅黑','仿宋'];
const DEFAULT_ANNOTATION_COLOR='C04000';
const DEFAULT_ANNOTATION_FONT='楷体';

const PRESET_ANNOTATION_COLORS=[
  {id:'default',name:'默认',hex:'C04000'},
  {id:'blue',name:'蓝色',hex:'339CFF'},
  {id:'green',name:'绿色',hex:'16A34A'},
  {id:'yellow',name:'黄色',hex:'D97706'},
  {id:'pink',name:'粉色',hex:'EC4899'},
  {id:'orange',name:'橙色',hex:'EA580C'},
  {id:'purple',name:'紫色',hex:'7C3AED'},
  {id:'white',name:'白色',hex:'FFFFFF'},
] as const;

const pathFor=(stage:Stage)=>stage==='overview'?BASE:`${BASE}/${stage}`;
const prefKey=(taskId:string,projectId:string)=>`tax-assistant-legal-review-preferences:${taskId}:${projectId}`;
const defaultPrefKey=(taskId:string)=>`tax-assistant-legal-review-default-preferences:${taskId}`;
const mainKey=(taskId:string,projectId:string)=>`tax-assistant-legal-review-main-file:${taskId}:${projectId}`;
const preferencesCacheKey=(taskId:string,projectId:string)=>`${taskId}:${projectId}`;
const cloudPreferencesCache=new Map<string,EffectivePreferences|null>();
const taskSchemeCache=new Map<string,TaskSchemeRecord|null>();
const workbenchReviewPollers=new Map<string,number>();
const workbenchReviewTickLocks=new Set<string>();
const workbenchReviewingKeys=new Set<string>();
const workbenchReviewPollerKey=(taskId:string,projectId:string)=>`${taskId}:${projectId}`;
function isWorkbenchProjectReviewing(taskId:string,projectId:string):boolean{
  return workbenchReviewingKeys.has(workbenchReviewPollerKey(taskId,projectId));
}
function markWorkbenchProjectReviewing(taskId:string,projectId:string):void{
  workbenchReviewingKeys.add(workbenchReviewPollerKey(taskId,projectId));
}
function clearWorkbenchProjectReviewing(taskId:string,projectId:string):void{
  workbenchReviewingKeys.delete(workbenchReviewPollerKey(taskId,projectId));
}
function stopWorkbenchReviewPoller(taskId:string,projectId:string):void{
  const key=workbenchReviewPollerKey(taskId,projectId);
  const timer=workbenchReviewPollers.get(key);
  if(timer!==undefined){window.clearInterval(timer);workbenchReviewPollers.delete(key)}
}
function stopAllWorkbenchReviewPollers():void{
  workbenchReviewPollers.forEach(timer=>window.clearInterval(timer));
  workbenchReviewPollers.clear();
  workbenchReviewingKeys.clear();
}
function rememberProjectPreferences(taskId:string,projectId:string,preferences:EffectivePreferences|null|undefined):void{cloudPreferencesCache.set(preferencesCacheKey(taskId,projectId),preferences??null)}
function rememberTaskScheme(taskId:string,scheme:TaskSchemeRecord|null|undefined):void{taskSchemeCache.set(taskId,scheme??null)}
function hasSavedPreferences(taskId:string,projectId:string,snapshot?:ProjectSnapshot|null):boolean{if(snapshot?.preferencesConfigured)return true;const cached=cloudPreferencesCache.get(preferencesCacheKey(taskId,projectId));if(cached?.savedAt)return true;if(taskSchemeCache.get(taskId)?.savedAt)return true;return Boolean(localStorage.getItem(prefKey(taskId,projectId))||localStorage.getItem(defaultPrefKey(taskId)))}
function normalizeCompliancePolicyFileIds(value:unknown):string[]{if(!Array.isArray(value))return[];return[...new Set(value.map(item=>String(item??'').trim()).filter(Boolean))].slice(0,20)}
function preferencesFromRecord(record:Partial<Preferences>|null|undefined,companyName:string,hasEnterprise=false,userDisplayName=''):Preferences{const fallback=defaultPreferences(companyName,userDisplayName,hasEnterprise);if(!record)return fallback;return normalizePreferences({reviewPositionCompany:String(record.reviewPositionCompany??fallback.reviewPositionCompany).trim()||fallback.reviewPositionCompany,reviewerName:String(record.reviewerName??fallback.reviewerName).trim().slice(0,80)||fallback.reviewerName,contractType:String(record.contractType??fallback.contractType).trim().slice(0,80)||fallback.contractType,reviewDepth:record.reviewDepth==='standard'?'standard':'deep',reviewFocus:fallback.reviewFocus,scopes:{legal:record.scopes?.legal!==false,business:record.scopes?.business!==false,compliance:record.scopes?.compliance!==false},scopeAnnotationStyles:normalizeScopeAnnotationStyles(record.scopeAnnotationStyles??fallback.scopeAnnotationStyles),complianceLibraryScope:record.complianceLibraryScope==='enterprise'&&hasEnterprise?'enterprise':record.complianceLibraryScope==='personal'?'personal':fallback.complianceLibraryScope,complianceLibraryFolders:normalizeComplianceLibraryFolders(record.complianceLibraryFolders??fallback.complianceLibraryFolders),selectedCompliancePolicyFileIds:normalizeCompliancePolicyFileIds(record.selectedCompliancePolicyFileIds??fallback.selectedCompliancePolicyFileIds),enhancedFeatures:{enterpriseOwnershipLookup:record.enhancedFeatures?.enterpriseOwnershipLookup===true},defaultAnnotateIncludeDescription:record.defaultAnnotateIncludeDescription!==false,defaultAnnotateIncludeSuggestion:record.defaultAnnotateIncludeSuggestion!==false})}
function loadDefaultPreferences(taskId:string,companyName:string):Preferences{const hasEnterprise=legalReviewHasEnterprise(taskId);const cached=taskSchemeCache.get(taskId);if(cached?.savedAt)return preferencesFromRecord(cached,companyName,hasEnterprise);try{const data=JSON.parse(localStorage.getItem(defaultPrefKey(taskId))??'{}') as Partial<Preferences>;return preferencesFromRecord(data,companyName,hasEnterprise)}catch{return defaultPreferences(companyName,'',hasEnterprise)}}
async function loadTaskSchemeFromCloud(taskId:string,companyName:string):Promise<Preferences>{const hasEnterprise=legalReviewHasEnterprise(taskId);try{const payload=await api.get<{scheme:TaskSchemeRecord|null}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/scheme`));rememberTaskScheme(taskId,payload.scheme??null);if(payload.scheme?.savedAt){localStorage.setItem(defaultPrefKey(taskId),JSON.stringify(payload.scheme));return preferencesFromRecord(payload.scheme,companyName,hasEnterprise)}}catch{/* 离线时回退本地缓存 */}return loadDefaultPreferences(taskId,companyName)}
/** 工作台不展示的审核阶段说明（进度百分比仍更新）。 */
const HIDDEN_WORKBENCH_REVIEW_PHASES=new Set(['正在由大模型通读合同并生成意见']);

/**
 * 过滤工作台可见的阶段说明。
 * @param phase 接口返回的审核阶段
 */
function visibleWorkbenchReviewPhase(phase?:string):string{
  const text=String(phase??'').trim();
  if(!text||HIDDEN_WORKBENCH_REVIEW_PHASES.has(text))return'';
  return text;
}

/** 工作台「审核中」状态文案（含进度百分比）。 */
function formatWorkbenchReviewProgressLabel(percent:number):string{
  const value=Math.max(0,Math.min(100,Math.round(percent)));
  return`审核中 ${value}%`;
}
function setProjectRowReviewProgress(root:HTMLElement,projectId:string,percent:number,phase=''):void{
  const statusBadge=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-row-status`);
  if(!statusBadge)return;
  statusBadge.textContent=formatWorkbenchReviewProgressLabel(percent);
  statusBadge.className='cr-row-status is-running';
  if(phase)statusBadge.setAttribute('title',phase);
  else statusBadge.removeAttribute('title');
}

/**
 * 将工作台行切到进行中：状态百分比、阶段说明，并暂时关掉查看结果与归档。
 * @param root 工作台根节点
 * @param projectId 项目 ID
 * @param percent 审核进度 0–100
 * @param phase 当前阶段说明
 */
function setProjectRowReviewingUi(root:HTMLElement,projectId:string,percent:number,phase=''):void{
  const phaseText=visibleWorkbenchReviewPhase(phase);
  setProjectRowReviewProgress(root,projectId,percent,phaseText);
  const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  if(!row)return;
  const wrap=row.querySelector('.cr-row-status-wrap');
  let note=wrap?.querySelector<HTMLParagraphElement>('.cr-row-status-note');
  if(wrap&&phaseText){
    if(!note){
      note=document.createElement('p');
      note.className='cr-row-status-note is-running';
      wrap.appendChild(note);
    }
    note.classList.add('is-running');
    note.textContent=phaseText;
    note.title=phaseText;
  }else if(note&&note.classList.contains('is-running')){
    note.remove();
  }
  const resultBtn=row.querySelector<HTMLButtonElement>(`[data-project-result="${projectId}"]`);
  if(resultBtn){
    resultBtn.disabled=true;
    resultBtn.classList.remove('is-viewable','is-completed','is-partial');
    resultBtn.title='正在审核，完成后可查看结果';
    const label=resultBtn.querySelector('.cr-result-label');
    if(label)label.textContent='审核中';
  }
  const archiveBtn=row.querySelector<HTMLButtonElement>(`[data-project-archive="${projectId}"]`);
  if(archiveBtn){
    archiveBtn.disabled=true;
    archiveBtn.classList.add('is-archive-locked');
    const img=archiveBtn.querySelector('img');
    if(img)img.src=CR_ICON_ARCHIVE_DISABLED;
    archiveBtn.title='正在审核，暂不可归档';
  }
  const reviewBtn=row.querySelector<HTMLButtonElement>(`[data-project-review="${projectId}"]`);
  if(reviewBtn){
    reviewBtn.disabled=true;
    reviewBtn.classList.add('is-running');
    reviewBtn.classList.remove('is-rerun');
    const img=reviewBtn.querySelector('img');
    if(img)img.src=CR_ICON_REVIEW_START_DISABLED;
    reviewBtn.title='审核进行中';
  }
}
function applyFinishedReviewToRow(root:HTMLElement,projectId:string,review:LegalReviewRun,risks:LegalRisk[]):void{
  const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  if(!row)return;
  const statusBadge=row.querySelector('.cr-row-status');
  if(statusBadge){
    const finished=reviewFinishedSuccessfully(review);
    const failedPartial=!finished&&review.status==='failed'&&(review.riskCount>0||risks.length>0);
    statusBadge.textContent=finished?'审核完成':failedPartial?'审核失败·有部分意见':'审核失败';
    statusBadge.className=`cr-row-status ${finished?'is-done':'is-failed'}`;
    statusBadge.removeAttribute('title');
  }
  row.querySelectorAll('.cr-row-status-note.is-running').forEach(item=>item.remove());
  const resultBtn=row.querySelector<HTMLButtonElement>(`[data-project-result="${projectId}"]`);
  if(resultBtn){
    const canView=reviewHasVisibleResults(review,risks);
    const label=resultBtn.querySelector('.cr-result-label');
    if(label)label.textContent='查看结果';
    resultBtn.disabled=!canView;
    resultBtn.classList.toggle('is-viewable',canView);
    resultBtn.classList.toggle('is-completed',reviewFinishedSuccessfully(review));
    resultBtn.classList.toggle('is-partial',review.status==='failed'&&canView&&!reviewFinishedSuccessfully(review));
    resultBtn.title=canView?'查看审核结果（风险复核）':'暂无可用审核意见';
  }
  const archiveBtn=row.querySelector<HTMLButtonElement>(`[data-project-archive="${projectId}"]`);
  if(archiveBtn){
    const ready=reviewFinishedSuccessfully(review);
    archiveBtn.disabled=!ready;
    archiveBtn.classList.toggle('is-archive-locked',!ready);
    const img=archiveBtn.querySelector('img');
    if(img)img.src=ready?CR_ICON_ARCHIVE_READY:CR_ICON_ARCHIVE_DISABLED;
    archiveBtn.title=ready?'成果归档':'审核未完成，暂不可归档';
  }
  const reviewBtn=row.querySelector<HTMLButtonElement>(`[data-project-review="${projectId}"]`);
  if(reviewBtn){
    reviewBtn.disabled=false;
    reviewBtn.classList.remove('is-running');
    reviewBtn.classList.toggle('is-rerun',reviewFinishedSuccessfully(review));
    const img=reviewBtn.querySelector('img');
    if(img)img.src=CR_ICON_REVIEW_START_READY;
    reviewBtn.title=reviewFinishedSuccessfully(review)?'重新智能审核':'开始智能审核';
  }
}
function projectStubFromRow(root:HTMLElement,projectId:string):LegalReviewProject|null{
  const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  if(!row)return null;
  const name=row.querySelector<HTMLInputElement>('.cr-project-name')?.value.trim()||'合同审核项目';
  return{id:projectId,name,createdAt:'',updatedAt:''};
}
async function executeProjectReviewWithWorkbenchProgress(root:HTMLElement,taskId:string,projectId:string,auth:AuthSnapshot,companyName:string,previousReviewId=''):Promise<void>{
  startWorkbenchReviewPolling(root,taskId,projectId,companyName,auth,previousReviewId);
  await executeProjectReview(taskId,projectId,auth,companyName);
}
/** 旧结果曾因意见条数不足被记成失败。这不是审核失败，页面上按已完成处理。 */
function isOpinionCountQuotaError(review:LegalReviewRun|null):boolean{
  return /未生成足够的实质性审核意见|实质性审核意见仅/.test(String(review?.error??''));
}
function reviewFinishedSuccessfully(review:LegalReviewRun|null):boolean{
  if(!review)return false;
  if(review.status==='completed')return true;
  return review.status==='failed'&&isOpinionCountQuotaError(review);
}
function reviewHasVisibleResults(review:LegalReviewRun|null,risks:LegalRisk[]):boolean{
  if(!review)return false;
  if(review.status==='completed')return true;
  return review.status==='failed'&&(review.riskCount>0||risks.length>0);
}
function reviewFailureNoteText(review:LegalReviewRun|null):string{
  if(isOpinionCountQuotaError(review))return'';
  const message=String(review?.error??'').trim();
  if(!message||review?.status!=='failed')return'';
  return message.length>56?`${message.slice(0,56)}…`:message;
}
function projectRowStatus(taskId:string,snapshot:ProjectSnapshot):{label:string;className:string}{
  if(reviewFinishedSuccessfully(snapshot.review))return{label:'审核完成',className:'is-done'};
  if(snapshot.review?.status==='in_progress')return{label:formatWorkbenchReviewProgressLabel(snapshot.review.reviewProgress??50),className:'is-running'};
  if(snapshot.review?.status==='failed')return{label:snapshot.review.riskCount>0||snapshot.risks.length>0?'审核失败·有部分意见':'审核失败',className:'is-failed'};
  if(!snapshot.main)return{label:'等上传合同文件',className:'is-pending'};
  if(!previewReadyForMain(snapshot))return{label:'等待预览',className:'is-preview-pending'};
  if(!hasSavedPreferences(taskId,snapshot.project.id,snapshot))return{label:'等待确定审核设置',className:'is-settings'};
  return{label:'等待审核',className:'is-ready'};
}
/** 项目方案继承状态标签（工作台列表不展示）。 */
function projectSchemeBadge(_snapshot:ProjectSnapshot,_taskId:string):string{return''}
/** 项目是否已完成审核、可进入成果归档。 */
function isProjectArchiveReady(snapshot:ProjectSnapshot):boolean{return snapshot.review?.status==='completed'}
function isProjectReviewReady(snapshot:ProjectSnapshot,taskId:string):boolean{return Boolean(snapshot.main)&&hasSavedPreferences(taskId,snapshot.project.id,snapshot)}
function currentStage():Stage{const value=window.location.pathname.replace(/\/$/,'').split('/').pop()??'';return STAGES.some(item=>item.id===value)?value as Stage:'overview'}
function defaultPreferences(_companyName:string,userDisplayName='',hasEnterprise=false):Preferences{
  const reviewPositionCompany=defaultTaskReviewPosition(userDisplayName);
  const reviewerName=String(userDisplayName??'').trim().slice(0,80);
  return{reviewPositionCompany,reviewerName,contractType:SYSTEM_AUTO_CONTRACT_TYPE,reviewDepth:'deep',reviewFocus:[...FOCUS_OPTIONS],scopes:{legal:true,business:true,compliance:true},scopeAnnotationStyles:defaultScopeAnnotationStyles(),complianceLibraryScope:hasEnterprise?'enterprise':'personal',complianceLibraryFolders:{personal:null,enterprise:null},selectedCompliancePolicyFileIds:[],enhancedFeatures:{enterpriseOwnershipLookup:false},defaultAnnotateIncludeDescription:true,defaultAnnotateIncludeSuggestion:true};
}
function normalizePreferences(value:Preferences):Preferences{return{...value,reviewerName:String(value.reviewerName??'').trim().slice(0,80),reviewFocus:[...FOCUS_OPTIONS],scopeAnnotationStyles:normalizeScopeAnnotationStyles(value.scopeAnnotationStyles),complianceLibraryFolders:normalizeComplianceLibraryFolders(value.complianceLibraryFolders),selectedCompliancePolicyFileIds:normalizeCompliancePolicyFileIds(value.selectedCompliancePolicyFileIds),enhancedFeatures:{enterpriseOwnershipLookup:value.enhancedFeatures?.enterpriseOwnershipLookup===true},defaultAnnotateIncludeDescription:value.defaultAnnotateIncludeDescription!==false,defaultAnnotateIncludeSuggestion:value.defaultAnnotateIncludeSuggestion!==false}}
function loadPreferences(taskId:string,projectId:string,companyName:string,snapshot?:ProjectSnapshot|null):Preferences{const hasEnterprise=legalReviewHasEnterprise(taskId);const cloud=snapshot?.preferences??cloudPreferencesCache.get(preferencesCacheKey(taskId,projectId));if(cloud)return preferencesFromRecord(cloud,companyName,hasEnterprise);const fallback=loadDefaultPreferences(taskId,companyName);try{const data=JSON.parse(localStorage.getItem(prefKey(taskId,projectId))??'{}') as Partial<Preferences>;return preferencesFromRecord({...fallback,...data},companyName,hasEnterprise)}catch{return fallback}}
async function savePreferencesToCloud(taskId:string,projectId:string,preferences:Preferences):Promise<EffectivePreferences>{const payload=await api.put<{effective:EffectivePreferences}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/preferences`),preferences);rememberProjectPreferences(taskId,projectId,payload.effective);localStorage.setItem(prefKey(taskId,projectId),JSON.stringify(preferences));return payload.effective}
async function saveTaskSchemeToCloud(taskId:string,preferences:Preferences,syncInheritedProjects=false):Promise<{scheme:TaskSchemeRecord;syncSummary:{clearedProjectCount:number}}>{const payload=await api.put<{scheme:TaskSchemeRecord;syncSummary:{clearedProjectCount:number}}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/scheme`),{...preferences,syncInheritedProjects});rememberTaskScheme(taskId,payload.scheme);localStorage.setItem(defaultPrefKey(taskId),JSON.stringify(payload.scheme));return payload}
async function restorePreferencesToCloud(taskId:string,projectId:string):Promise<EffectivePreferences>{const payload=await api.post<{effective:EffectivePreferences}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/preferences/restore`),{});rememberProjectPreferences(taskId,projectId,payload.effective);localStorage.removeItem(prefKey(taskId,projectId));return payload.effective}
async function migrateLocalPreferencesIfNeeded(taskId:string,projectId:string,companyName:string,cloud:EffectivePreferences|null|undefined,configured?:boolean):Promise<EffectivePreferences|null>{if(configured||cloud?.savedAt)return cloud??null;const raw=localStorage.getItem(prefKey(taskId,projectId));if(!raw)return null;try{const local=normalizePreferences(JSON.parse(raw) as Preferences);return await savePreferencesToCloud(taskId,projectId,local)}catch{return null}}
function mimeFromName(name:string):string{const lower=name.toLowerCase();if(lower.endsWith('.docx'))return'application/vnd.openxmlformats-officedocument.wordprocessingml.document';if(lower.endsWith('.doc'))return'application/msword';if(lower.endsWith('.pdf'))return'application/pdf';if(lower.endsWith('.txt'))return'text/plain';return'application/octet-stream'}
const LEGAL_MAIN_FILE_PATTERN=/\.(docx?|pdf|txt|png|jpe?g)$/iu;
const LEGAL_MAIN_DROP_MAX=40;
const LEGAL_CREATE_PROJECT_CLICK_SUPPRESS_MS=600;
let legalCreateProjectClickSuppressUntil=0;
/** 是否为从系统拖入的文件。 */
function isFileDragEvent(event:DragEvent):boolean{
  const types=event.dataTransfer?.types;
  if(!types)return false;
  return Array.from(types).includes('Files');
}
/** 当前拖放目标是否允许接收文件（新建项目或附件区）。 */
function isLegalReviewFileDropTarget(target:EventTarget|null):boolean{
  const el=target as HTMLElement|null;
  return Boolean(el?.closest?.('#createLegalProject,[data-attachment-dropzone]'));
}
/**
 * 在指定区域接收拖入的多份文件，高亮放置区。
 */
function bindFileDropZone(zone:HTMLElement,onFiles:(files:File[])=>void):void{
  let dragDepth=0;
  const setDragHighlight=(active:boolean)=>{zone.classList.toggle('is-drop-target',active)};
  const resetDrag=()=>{dragDepth=0;setDragHighlight(false)};
  zone.addEventListener('dragenter',event=>{
    if(!isFileDragEvent(event))return;
    event.preventDefault();
    event.stopPropagation();
    dragDepth+=1;
    setDragHighlight(true);
  });
  zone.addEventListener('dragover',event=>{
    if(!isFileDragEvent(event))return;
    event.preventDefault();
    event.stopPropagation();
    if(event.dataTransfer)event.dataTransfer.dropEffect='copy';
  });
  zone.addEventListener('dragleave',event=>{
    const related=event.relatedTarget as Node|null;
    if(related&&zone.contains(related))return;
    dragDepth=Math.max(0,dragDepth-1);
    if(dragDepth===0)setDragHighlight(false);
  });
  zone.addEventListener('drop',event=>{
    if(!isFileDragEvent(event))return;
    event.preventDefault();
    event.stopPropagation();
    resetDrag();
    const dataTransfer=event.dataTransfer;
    if(!dataTransfer)return;
    const files=collectMainFilesFromDataTransfer(dataTransfer);
    if(!files.length){
      window.alert('请拖入 DOC、DOCX、PDF、TXT 或 PNG/JPG 文件。');
      return;
    }
    onFiles(files);
  });
}
/** 判断是否为可上传的合同正文扩展名。 */
function isLegalReviewMainFile(file:File):boolean{return LEGAL_MAIN_FILE_PATTERN.test(String(file.name??'').trim())}
/** 从拖放数据中提取去重后的合同文件（正文与附件共用扩展名）。 */
function collectMainFilesFromDataTransfer(dataTransfer:DataTransfer):File[]{
  const seen=new Set<string>();
  const out:File[]=[];
  const push=(file:File)=>{
    if(!isLegalReviewMainFile(file))return;
    const key=`${file.name}\0${file.size}`;
    if(seen.has(key))return;
    seen.add(key);
    out.push(file);
  };
  if(dataTransfer.items?.length){
    for(const item of dataTransfer.items){
      if(item.kind!=='file')continue;
      const file=item.getAsFile();
      if(file)push(file);
    }
  }
  if(!out.length){
    for(const file of dataTransfer.files)push(file);
  }
  return out.slice(0,LEGAL_MAIN_DROP_MAX);
}
function iconFor(name:string):string{const lower=name.toLowerCase();if(lower.endsWith('.doc')||lower.endsWith('.docx'))return'/assets/icons/word.svg';if(lower.endsWith('.pdf'))return'/assets/icons/pdf.svg';return'/assets/icons/file.svg'}
function tableIconButton(className:string,icon:string,label:string,attrs:string):string{return`<button type="button" class="cr-table-icon-btn ${className}" ${attrs} aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}"><img src="${icon}" alt=""></button>`}
/** 预览列图标：未展开显示关闭预览，展开后显示预览。 */
function previewIcon(expanded:boolean):string{return expanded?CR_ICON_PREVIEW_OPEN:CR_ICON_PREVIEW_CLOSED}
/** 设置列图标：未展开显示 idle，展开设置后显示 active。 */
function settingsIcon(active:boolean):string{return active?CR_ICON_SETTING_ACTIVE:CR_ICON_SETTING_IDLE}
/** 设置列按钮：项目已保存专属设置时显示角标圆点。 */
function tableSettingsButton(projectId:string,expanded:boolean,hasCustomOverrides:boolean):string{
  const icon=settingsIcon(expanded);
  const label=hasCustomOverrides?'审核设置（已保存本项目专属设置）':'审核设置';
  const badge=hasCustomOverrides?'<span class="cr-settings-custom-badge" aria-hidden="true"></span>':'';
  const btnClass=`cr-table-icon-btn cr-table-icon-btn-badge cr-project-settings${expanded?' is-active':''}${hasCustomOverrides?' is-customized':''}`;
  return`<button type="button" class="${btnClass}" data-project-settings="${escapeHtml(projectId)}" aria-expanded="${expanded?'true':'false'}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}"><img src="${icon}" alt="">${badge}</button>`;
}
/** 行内「开始审核」按钮：按当前项目是否具备条件呈现对应状态图标。 */
function tableReviewButton(projectId:string,enabled:boolean,running:boolean,title:string,emphasize=false):string{
  const icon=enabled&&!running?CR_ICON_REVIEW_START_READY:CR_ICON_REVIEW_START_DISABLED;
  const label=title||'开始智能审核';
  const btnClass=`cr-table-icon-btn cr-project-review${running?' is-running':''}${emphasize?' is-rerun':''}${enabled?'':' is-disabled'}`;
  return`<button type="button" class="${btnClass}" data-project-review="${escapeHtml(projectId)}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}"${enabled?'':' disabled'}><img src="${icon}" alt=""></button>`;
}
/** 判断当前行是否可点击「审核」（已完成的项目也可重新审核）。 */
function projectReviewActionMeta(taskId:string,snapshot:ProjectSnapshot):{enabled:boolean;running:boolean;title:string;emphasize:boolean}{
  if(!isLegalFeatureEnabled('legal-ai-review-v1'))return{enabled:false,running:false,title:'智能审核已关闭',emphasize:false};
  if(snapshot.review?.status==='in_progress')return{enabled:false,running:true,title:'审核进行中',emphasize:false};
  if(!snapshot.main)return{enabled:false,running:false,title:'请先上传合同正文',emphasize:false};
  if(!hasSavedPreferences(taskId,snapshot.project.id,snapshot))return{enabled:false,running:false,title:'请先保存审核设置',emphasize:false};
  if(snapshot.review?.status==='completed')return{enabled:true,running:false,title:'重新智能审核',emphasize:true};
  return{enabled:true,running:false,title:'开始智能审核',emphasize:false};
}
/** 附件列图标：已上传时显示数量角标。 */
function tableAttachmentButton(projectId:string,count:number,expanded:boolean,label:string):string{
  const icon=count>0?CR_ICON_ATTACHMENT_UPLOADED:CR_ICON_ATTACHMENT_EMPTY;
  const badge=count>0?`<span class="cr-icon-badge" aria-hidden="true">${count>99?'99+':String(count)}</span>`:'';
  return`<button type="button" class="cr-table-icon-btn cr-table-icon-btn-badge cr-project-expand${expanded?' is-active':''}" data-project-expand="${escapeHtml(projectId)}" aria-expanded="${expanded?'true':'false'}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}"><img src="${icon}" alt="">${badge}</button>`;
}
function formatSize(bytes:number):string{return bytes<1024?`${bytes} B`:bytes<1048576?`${(bytes/1024).toFixed(1)} KB`:`${(bytes/1048576).toFixed(1)} MB`}
function formatDate(value?:string):string{if(!value)return'刚刚';const date=new Date(value);return Number.isNaN(date.getTime())?'时间未知':date.toLocaleString('zh-CN',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'})}
function selectedMain(taskId:string,projectId:string,files:CloudFile[]):CloudFile|null{const file=files.find(item=>item.id===localStorage.getItem(mainKey(taskId,projectId)))??files[0]??null;if(file)localStorage.setItem(mainKey(taskId,projectId),file.id);return file}
const defaultProjectNamePattern=/^(?:合同审核项目-\d{3}|项目\d{3})$/;
function isDefaultProjectName(name:string):boolean{return defaultProjectNamePattern.test(name.trim())}
function defaultProjectLabel(index:number):string{return `项目${String(index+1).padStart(3,'0')}`}
function projectDisplayName(project:LegalReviewProject,index:number):string{return isDefaultProjectName(project.name)?defaultProjectLabel(index):project.name}
function reviewTitleFromFileName(fileName:string):string{const base=fileName.replace(/\.[^.]+$/,'').trim()||fileName.trim();return `${base.slice(0, 110)}审核`}
function loadExpandedProjectIds(taskId:string):Set<string>{try{return new Set(JSON.parse(localStorage.getItem(expandedProjectsKey(taskId))??'[]') as string[])}catch{return new Set()}}
function saveExpandedProjectIds(taskId:string,ids:Set<string>):void{localStorage.setItem(expandedProjectsKey(taskId),JSON.stringify([...ids]))}
function loadPreviewExpandedProjectIds(taskId:string):Set<string>{try{return new Set(JSON.parse(localStorage.getItem(previewExpandedProjectsKey(taskId))??'[]') as string[])}catch{return new Set()}}
function savePreviewExpandedProjectIds(taskId:string,ids:Set<string>):void{localStorage.setItem(previewExpandedProjectsKey(taskId),JSON.stringify([...ids]))}
function loadSettingsExpandedProjectIds(taskId:string):Set<string>{
  let ids=settingsExpandedByTask.get(taskId);
  if(!ids){ids=new Set();settingsExpandedByTask.set(taskId,ids)}
  return ids;
}
function saveSettingsExpandedProjectIds(taskId:string,ids:Set<string>):void{settingsExpandedByTask.set(taskId,new Set(ids))}
function rememberOverviewFocus(taskId:string,projectId:string):void{localStorage.setItem(overviewFocusKey(taskId),projectId)}
function overviewFocusProjectId(taskId:string):string{return localStorage.getItem(overviewFocusKey(taskId))??''}
function rememberActiveProject(taskId:string,projectId:string):void{localStorage.setItem(activeProjectKey(taskId),projectId)}
function activeLegalReviewProjectId(taskId:string):string{return localStorage.getItem(activeProjectKey(taskId))??''}
async function loadLegalReviewProjects(taskId:string):Promise<LegalReviewProject[]>{const payload=await api.get<{projects:LegalReviewProject[]}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects`));return(payload.projects??[]).sort((left,right)=>left.createdAt.localeCompare(right.createdAt))}
async function loadArchivedLegalReviewProjects(taskId:string):Promise<LegalReviewProject[]>{const payload=await api.get<{projects:LegalReviewProject[]}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/archived-projects`));return(payload.projects??[]).sort((left,right)=>String(right.archivedAt??right.updatedAt).localeCompare(String(left.archivedAt??left.updatedAt)))}
async function loadProjectSnapshot(taskId:string,project:LegalReviewProject,companyName='',archived=false):Promise<ProjectSnapshot>{const status=await api.get<ReviewStatus>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(project.id)}/status${archived?'?archived=1':''}`));const files=status.files??{mainFiles:[],attachmentFiles:[],annotatedFiles:[]},main=selectedMain(taskId,project.id,files.mainFiles);rememberTaskScheme(taskId,status.taskScheme??null);rememberProjectPreferences(taskId,project.id,status.preferences??null);const migrated=!archived&&companyName?await migrateLocalPreferencesIfNeeded(taskId,project.id,companyName,status.preferences??null,status.preferencesConfigured):status.preferences??null;const preferences=migrated??cloudPreferencesCache.get(preferencesCacheKey(taskId,project.id))??status.preferences??null;if(preferences)rememberProjectPreferences(taskId,project.id,preferences);const preferencesConfigured=Boolean(status.preferencesConfigured||migrated?.savedAt||status.preferences?.savedAt);const writingGuidance=status.writingGuidance??status.review?.writingGuidance??[];return{project,files,review:status.review,risks:status.risks??[],writingGuidance,main,quickProfile:status.quickProfiles?.find(item=>item.fileId===main?.id)??null,protectedIds:new Set(status.protectedFileIds??[]),preferences,preferencesConfigured,taskScheme:status.taskScheme??taskSchemeCache.get(taskId)??null,projectPreferences:status.projectPreferences??null}}
async function renameProjectIfDefault(taskId:string,projectId:string,currentName:string,fileName:string):Promise<string>{if(!isDefaultProjectName(currentName))return currentName;const nextName=reviewTitleFromFileName(fileName);await api.patch(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}`),{name:nextName});return nextName}
/**
 * 正文已上传且项目仍为默认名时，立刻用「文件名+审核」更新列表展示并写回服务端。
 * 行刷新会优先读表格输入框里的旧名称，不能只依赖整页刷新。
 */
function syncProjectNameFromMainFile(taskId:string,snapshot:ProjectSnapshot):void{
  const fileName=snapshot.main?.originalName??'';
  if(!fileName||!isDefaultProjectName(snapshot.project.name))return;
  const currentName=snapshot.project.name;
  snapshot.project={...snapshot.project,name:reviewTitleFromFileName(fileName)};
  void renameProjectIfDefault(taskId,snapshot.project.id,currentName,fileName).catch(()=>{/** 服务端上传时可能已改名，列表仍以正文文件名为准 */});
}
async function quickInspect(taskId:string,projectId:string,fileId:string):Promise<QuickProfile>{
  const payload=await api.post<{profile:QuickProfile}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/quick-inspect`),{fileId},{timeoutMs:180000});
  return payload.profile;
}
const previewInspectInFlight=new Map<string,Promise<QuickProfile>>();
function previewInspectKey(taskId:string,projectId:string,fileId:string):string{return`${taskId}:${projectId}:${fileId}`}
async function ensurePreviewInspect(taskId:string,projectId:string,fileId:string,options?:{force?:boolean}):Promise<QuickProfile>{
  const key=previewInspectKey(taskId,projectId,fileId);
  if(!options?.force){
    const running=previewInspectInFlight.get(key);
    if(running)return running;
  }
  const work=quickInspect(taskId,projectId,fileId).finally(()=>{previewInspectInFlight.delete(key)});
  previewInspectInFlight.set(key,work);
  return work;
}
function severityLabel(value:string):string{return value==='high'?'高风险':value==='medium'?'中风险':'低风险'}
function typeLabel(value?:string):string{return value==='business'?'商业':value==='compliance'?'合规':'法律'}
function profileClass(profile:QuickProfile|null):string{return!profile?'is-pending':profile.status==='ready'?'is-ready':profile.status==='failed'?'is-failed':'is-review'}

async function uploadFile(taskId:string,projectId:string,file:File,role:'main'|'attachment'):Promise<CloudFile>{
  const signed=await api.post<{file:CloudFile;uploadUrl:string}>(apiUrl('/api/files/upload-url'),{taskId,kind:'legal-review',legalReviewRole:role,legalReviewProjectId:projectId,fileName:file.name,sizeBytes:file.size,mimeType:file.type||mimeFromName(file.name)});
  await api.upload(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/content`),file,file.type||mimeFromName(file.name));
  return (await api.post<{file:CloudFile}>(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/complete`))).file;
}
/** 上传合同正文并汇报进度。 */
async function uploadMainFile(taskId:string,projectId:string,file:File,onProgress?:(percent:number)=>void):Promise<CloudFile>{
  const mime=file.type||mimeFromName(file.name);
  const signed=await api.post<{file:CloudFile;uploadUrl:string}>(apiUrl('/api/files/upload-url'),{taskId,kind:'legal-review',legalReviewRole:'main',legalReviewProjectId:projectId,fileName:file.name,sizeBytes:file.size,mimeType:mime});
  await uploadFileContentWithProgress(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/content`),file,mime,onProgress);
  return (await api.post<{file:CloudFile}>(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/complete`))).file;
}
/** 渲染工作台「正文」列单元格（附件按钮紧贴「更换」）。 */
function renderProjectMainBodyCell(projectId:string,main:CloudFile|null,attachSuffix=''):string{
  const input=`<input class="cr-project-main-input" data-project-main-input="${escapeHtml(projectId)}" type="file" accept=".doc,.docx,.pdf,.txt,.png,.jpg,.jpeg" hidden>`;
  if(!main){
    return`<div class="cr-table-body is-empty"><button type="button" class="cr-button cr-button-text cr-project-main-btn" data-project-main="${escapeHtml(projectId)}">上传正文</button>${attachSuffix}${input}</div>`;
  }
  return`<div class="cr-table-body"><span class="cr-table-filename" title="${escapeHtml(main.originalName)}">${escapeHtml(main.originalName)}</span><button type="button" class="cr-button cr-button-text cr-project-main-btn" data-project-main="${escapeHtml(projectId)}">更换</button>${attachSuffix}${input}</div>`;
}
/** 正文上传过程中更新列表展示。 */
function setProjectMainBodyUploading(root:HTMLElement,projectId:string,text='上传中'):void{
  const bodyCell=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-col-body`);
  if(!bodyCell)return;
  bodyCell.innerHTML=`<div class="cr-table-body is-uploading"><span class="cr-table-filename">${escapeHtml(text)}</span></div>`;
}
/** 带进度回调上传文件内容。 */
function uploadFileContentWithProgress(url:string,file:Blob,mimeType:string,onProgress?:(percent:number)=>void):Promise<void>{
  return new Promise((resolve,reject)=>{
    const xhr=new XMLHttpRequest();
    xhr.open('PUT',url);
    xhr.withCredentials=true;
    xhr.setRequestHeader('Content-Type',mimeType);
    xhr.upload.onprogress=event=>{
      if(!onProgress||!event.lengthComputable)return;
      onProgress(Math.min(100,Math.round((event.loaded/event.total)*100)));
    };
    xhr.onload=()=>{if(xhr.status>=200&&xhr.status<300)resolve();else reject(new Error(`上传失败（${xhr.status}）`))};
    xhr.onerror=()=>reject(new Error('上传失败'));
    xhr.send(file);
  });
}
/** 批量上传合同附件并汇报整体进度百分比。 */
async function uploadAttachmentFiles(taskId:string,projectId:string,files:File[],onProgress:(percent:number)=>void):Promise<void>{
  const totalBytes=files.reduce((sum,file)=>sum+file.size,0)||1;
  let doneBytes=0;
  for(const file of files){
    const mime=file.type||mimeFromName(file.name);
    const signed=await api.post<{file:CloudFile;uploadUrl:string}>(apiUrl('/api/files/upload-url'),{taskId,kind:'legal-review',legalReviewRole:'attachment',legalReviewProjectId:projectId,fileName:file.name,sizeBytes:file.size,mimeType:mime});
    let lastPercent=-1;
    await uploadFileContentWithProgress(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/content`),file,mime,filePercent=>{
      const loaded=doneBytes+Math.round(file.size*(filePercent/100));
      const overall=Math.min(100,Math.round((loaded/totalBytes)*100));
      if(overall!==lastPercent){lastPercent=overall;onProgress(overall);}
    });
    doneBytes+=file.size;
    onProgress(Math.min(100,Math.round((doneBytes/totalBytes)*100)));
    await api.post(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/complete`));
  }
}

function header(pageTitle:string,_companyName:string,_review:LegalReviewRun|null):string{
  return`<header class="cr-page-header"><div><h1>${escapeHtml(pageTitle)}</h1></div></header>`;
}
/**
 * 合同审核流程入口。工作台里与「新建项目」并排时使用紧凑样式。
 * @param inline 是否收成工具栏里的一排按钮
 */
function nav(hasMain:boolean,hasSettings:boolean,review:LegalReviewRun|null,inline=false):string{
  const active=currentStage();
  const state=(id:Stage)=>id===active?'current':id==='overview'||(id==='materials'&&hasMain)||(id==='settings'&&hasSettings)||((id==='analysis'||id==='risks'||id==='deliverables')&&review?.status==='completed')?'complete':id==='materials'&&!hasMain||id==='analysis'&&review?.status==='failed'?'attention':'upcoming';
  const visibleStages=STAGES.filter(item=>!NAV_HIDDEN_STAGES.has(item.id));
  return`<nav class="cr-workflow cr-workflow-compact${inline?' cr-workflow-inline':''}" aria-label="合同审核流程">${visibleStages.map(item=>{const value=state(item.id);const index=inline?'':`<span class="cr-workflow-index">${item.index}</span>`;return`<a class="cr-workflow-item is-${value}" href="${pathFor(item.id)}" data-review-route="${item.id}">${index}<span><b>${item.label}</b></span></a>`}).join('')}</nav>`;
}
function pageHeading(step:string,title:string,copy:string,action=''):string{return`<section class="cr-stage-heading"><div><span class="cr-step-label">${step}</span><h2>${title}</h2><p>${copy}</p></div>${action}</section>`}

function attachmentExpandHtml(snapshot:ProjectSnapshot):string{
  const {project,files}=snapshot;
  const list=files.attachmentFiles.length?files.attachmentFiles.map((file,index)=>`<div class="cr-project-attachment-item"><span class="cr-attachment-index" aria-hidden="true">${index+1}</span><img src="${iconFor(file.originalName)}" alt=""><span class="cr-attachment-name" title="${escapeHtml(file.originalName)}">${escapeHtml(file.originalName)}</span><span class="cr-attachment-size">${escapeHtml(formatSize(file.sizeBytes))}</span><button type="button" class="cr-icon-button legal-review-file-delete" data-file-id="${escapeHtml(file.id)}" data-project-id="${escapeHtml(project.id)}">删除</button></div>`).join(''):'<button type="button" class="cr-empty-compact cr-attachment-dropzone-empty" data-attachment-drop-open><b>点击或拖入多份附件</b><span>可拖入多份 · 扫描件请控制在 8 MB、12 页以内</span></button>';
  return`<div class="cr-project-attachments" data-attachment-dropzone="${escapeHtml(project.id)}"><div class="cr-project-attachments-head"><div class="cr-project-attachments-title"><b class="cr-expand-head-title">合同附件</b><span class="cr-attachment-upload-progress" data-attachment-upload-progress="${escapeHtml(project.id)}" hidden></span></div><button type="button" class="cr-button cr-button-secondary" data-project-attachment="${escapeHtml(project.id)}">添加附件</button><input class="cr-project-attachment-input" data-project-attachment-input="${escapeHtml(project.id)}" type="file" multiple accept=".doc,.docx,.pdf,.txt,.png,.jpg,.jpeg" hidden></div>${legalReviewAttachmentUploadHintHtml()}<div class="cr-project-attachments-list">${list}</div></div>`;
}
function previewExpandHtml(snapshot:ProjectSnapshot):string{
  const {project,main}=snapshot;
  const fileName=main?.originalName??'';
  const title=fileName?`${escapeHtml(fileName)} 预览`:'合同正文预览';
  return`<div class="cr-project-attachments-head"><div class="cr-project-attachments-title"><b class="cr-expand-head-title"${fileName?` title="${escapeHtml(fileName)}"`:''}>${title}</b></div><button type="button" class="cr-icon-button" data-project-preview-close="${escapeHtml(project.id)}">关闭</button></div><div class="cr-project-preview-body" data-project-preview-body="${escapeHtml(project.id)}"></div>`;
}
type ReviewPositionOption={value:string;label:string;group:string};
/** 构建项目级审核立场下拉选项：识别当事方、本人、自定义。 */
function buildProjectReviewPositionOptions(profile:QuickProfile|null,userDisplayName:string,currentValue:string):{options:ReviewPositionOption[];selected:string}{
  const options:ReviewPositionOption[]=[];
  const seen=new Set<string>();
  const push=(value:string,label:string,group:string)=>{const key=value.trim();if(!key||seen.has(key)||key===TASK_CUSTOM_POSITION_VALUE)return;seen.add(key);options.push({value:key,label,group})};
  (profile?.parties??[]).forEach(party=>push(party.name,`${party.role} · ${party.name}`,'识别当事方'));
  const selfName=userDisplayName.trim();
  if(selfName)push(selfName,`本人 · ${selfName}`,'我的组织');
  const preset=options.some(item=>item.value===currentValue);
  const selected=preset?currentValue:!currentValue?'':TASK_CUSTOM_POSITION_VALUE;
  return{options,selected};
}
/** 构建任务级审核立场选项：仅本人与自定义。 */
function buildTaskReviewPositionOptions(userDisplayName:string,currentValue:string):{options:ReviewPositionOption[];selected:string;customDraft:string}{
  const selfName=userDisplayName.trim();
  const options:ReviewPositionOption[]=[];
  if(selfName)options.push({value:selfName,label:'本人',group:'self'});
  options.push({value:TASK_CUSTOM_POSITION_VALUE,label:'自定义',group:'custom'});
  const custom=isTaskCustomReviewPosition(currentValue,selfName);
  const selected=custom||!selfName?TASK_CUSTOM_POSITION_VALUE:(selfName||TASK_CUSTOM_POSITION_VALUE);
  return{options,selected,customDraft:custom?String(currentValue??'').trim():''};
}
/** 任务级审核立场：卡片式单选，仅本人/自定义。 */
function taskPositionFieldHtml(options:ReviewPositionOption[],selected:string,customDraft:string):string{
  const items=options.map(item=>{
    const isCustom=item.value===TASK_CUSTOM_POSITION_VALUE;
    const title=isCustom?'自定义':'本人';
    const subtitle=isCustom?(customDraft||'填写立场主体名称'):item.value;
    const active=selected===item.value;
    return`<label class="cr-settings-position-option${active?' is-active':''}"><input type="radio" name="taskReviewPosition" data-settings-position-radio value="${escapeHtml(item.value)}" ${active?'checked':''}><span><b>${escapeHtml(title)}</b><small>${escapeHtml(subtitle)}</small></span></label>`;
  }).join('');
  const customOpen=selected===TASK_CUSTOM_POSITION_VALUE;
  const hiddenValue=customOpen?customDraft:selected;
  return`<div class="cr-settings-field cr-settings-field-position"><span class="cr-settings-label">默认审核立场</span><div class="cr-settings-position-options" role="radiogroup" aria-label="默认审核立场">${items}</div><div class="cr-settings-custom${customOpen?' is-open':''}" data-settings-custom ${customOpen?'':'hidden'}><input class="cr-input cr-settings-custom-input" data-settings-position-draft maxlength="200" value="${escapeHtml(customDraft)}" placeholder="输入立场主体名称并点击确认"><button type="button" class="cr-button cr-button-secondary cr-settings-custom-btn" data-settings-position-confirm>确认</button></div><input type="hidden" data-settings-position value="${escapeHtml(hiddenValue)}"></div>`;
}
function projectPositionFieldHtml(positionOptions:ReviewPositionOption[],positionSelected:string,customSelected:boolean,customDraft:string,positionHint:string,customBlock:string,customOption:string,currentValue:string):string{
  const positionSelectOptions=positionOptions.map(item=>`<option value="${escapeHtml(item.value)}" ${positionSelected===item.value?'selected':''}>${escapeHtml(item.label)}</option>`).join('');
  return`<label class="cr-settings-label" for="cr-settings-position-select">审核立场</label><select class="cr-select cr-settings-select" id="cr-settings-position-select" data-settings-position-select><option value="" ${!positionSelected?'selected':''}>请选择立场主体</option>${positionSelectOptions}${customOption}</select>${customBlock}<input type="hidden" data-settings-position value="${escapeHtml(positionSelected||currentValue)}">${positionHint}`;
}
type PolicyCatalogItem={id:string;fileName:string;folderPath:string;folderId?:string};
/** 从预览推荐构建初始制度目录。 */
function buildInitialPolicyCatalog(profile:QuickProfile|null):PolicyCatalogItem[]{
  return(profile?.recommendedCompliancePolicies??[]).map(item=>({id:item.fileId,fileName:item.fileName,folderPath:item.folderPath}));
}
/** 合并多份制度目录并去重。 */
function mergePolicyCatalog(...groups:Array<PolicyCatalogItem[]|CompliancePolicyRecommendation[]>):PolicyCatalogItem[]{
  const map=new Map<string,PolicyCatalogItem>();
  for(const group of groups){
    for(const item of group){
      const id=String((item as PolicyCatalogItem).id??(item as CompliancePolicyRecommendation).fileId??'').trim();
      if(!id)continue;
      const fileName=String((item as PolicyCatalogItem).fileName??(item as CompliancePolicyRecommendation).fileName??'').trim();
      const folderPath=String((item as PolicyCatalogItem).folderPath??(item as CompliancePolicyRecommendation).folderPath??'').trim();
      if(!map.has(id)||fileName)map.set(id,{id,fileName:fileName||map.get(id)?.fileName||'制度文件',folderPath:folderPath||map.get(id)?.folderPath||''});
    }
  }
  return[...map.values()];
}
/** 合并预览推荐与已选制度，构建文件名索引。 */
function compliancePolicyMetaMap(profile:QuickProfile|null,selectedIds:string[],catalog:PolicyCatalogItem[]):Map<string,{fileName:string;folderPath:string}>{
  const known=new Map<string,{fileName:string;folderPath:string}>();
  mergePolicyCatalog(profile?.recommendedCompliancePolicies??[],catalog).forEach(item=>known.set(item.id,{fileName:item.fileName,folderPath:item.folderPath}));
  selectedIds.forEach(id=>{if(!known.has(id))known.set(id,{fileName:'正在加载制度名称…',folderPath:''})});
  return known;
}
/** 渲染项目设置中的合规制度参照列表。 */
function buildCompliancePolicyRows(selectedIds:string[],known:Map<string,{fileName:string;folderPath:string}>):string{
  if(!selectedIds.length)return'<div class="cr-compliance-policy-empty">暂未选择参照制度，可点击下方「添加制度」或采纳预览推荐</div>';
  return selectedIds.map(id=>{
    const meta=known.get(id);
    const fileName=meta?.fileName??'制度文件';
    const folderPath=meta?.folderPath??'';
    return`<div class="cr-compliance-policy-item" data-compliance-policy-row data-compliance-policy-id="${escapeHtml(id)}"><div class="cr-compliance-policy-meta"><span class="cr-policy-item-name">${escapeHtml(fileName)}</span>${folderPath?`<small class="cr-policy-item-path">${escapeHtml(folderPath)}</small>`:''}</div><button type="button" class="cr-button cr-button-quiet cr-policy-remove-btn" data-remove-compliance-policy>移除</button></div>`;
  }).join('');
}
function compliancePolicySelectionPanelHtml(value:Preferences,profile:QuickProfile|null,catalog:PolicyCatalogItem[]):string{
  const initialCatalog=mergePolicyCatalog(buildInitialPolicyCatalog(profile),catalog);
  const known=compliancePolicyMetaMap(profile,value.selectedCompliancePolicyFileIds,initialCatalog);
  const rows=buildCompliancePolicyRows(value.selectedCompliancePolicyFileIds,known);
  const hasRecommendations=Boolean(profile?.recommendedCompliancePolicies?.length);
  const recommendedPayload=encodeURIComponent(JSON.stringify(profile?.recommendedCompliancePolicies??[]));
  const catalogPayload=encodeURIComponent(JSON.stringify(initialCatalog));
  return`<div class="cr-compliance-policy-selection${value.scopes.compliance?' is-open':''}" data-compliance-policy-selection data-recommended-policies="${recommendedPayload}" data-policy-catalog="${catalogPayload}" ${value.scopes.compliance?'':'hidden'}>
    <div class="cr-settings-field">
      <div class="cr-compliance-policy-header">
        <span class="cr-settings-label">参照制度</span>
        <div class="cr-compliance-policy-actions">
          ${hasRecommendations?'<button type="button" class="cr-button cr-button-quiet cr-scheme-btn-sm" data-apply-recommended-policies>采纳预览推荐</button>':''}
          <button type="button" class="cr-button cr-button-secondary cr-scheme-btn-sm" data-add-compliance-policy>添加制度</button>
        </div>
      </div>
      <div class="cr-compliance-policy-list" data-compliance-policy-list>${rows}</div>
    </div>
  </div>`;
}
/** 将制度目录写入设置面板并刷新列表展示。 */
function writeCompliancePolicyCatalog(panel:HTMLElement,catalog:PolicyCatalogItem[]):void{
  panel.setAttribute('data-policy-catalog',encodeURIComponent(JSON.stringify(catalog)));
}
/** 从设置面板读取制度目录。 */
function readCompliancePolicyCatalog(panel:HTMLElement):PolicyCatalogItem[]{
  try{return JSON.parse(decodeURIComponent(panel.getAttribute('data-policy-catalog')??'%5B%5D')) as PolicyCatalogItem[]}catch{return[]}
}
/** 加载制度库文件并刷新项目设置中的制度名称。 */
async function hydrateCompliancePolicyCatalog(container:HTMLElement,complianceContext?:ComplianceContext):Promise<void>{
  const panel=container.querySelector<HTMLElement>('[data-compliance-policy-selection]');
  if(!panel)return;
  let recommendations:CompliancePolicyRecommendation[]=[];
  try{recommendations=JSON.parse(decodeURIComponent(panel.getAttribute('data-recommended-policies')??'%5B%5D')) as CompliancePolicyRecommendation[]}catch{/** ignore */}
  try{
    const files=await loadCompliancePolicyCatalog(container,complianceContext);
    writeCompliancePolicyCatalog(panel,mergePolicyCatalog(readCompliancePolicyCatalog(panel),files,recommendations));
  }catch{
    writeCompliancePolicyCatalog(panel,mergePolicyCatalog(readCompliancePolicyCatalog(panel),recommendations));
  }
  refreshCompliancePolicyList(container);
}
/** 收集制度库根目录下的全部子文件夹。 */
function collectComplianceLibrarySubtreeFolders(allFolders:WorkspaceFolder[],rootId:string):WorkspaceFolder[]{
  const ids=new Set<string>([rootId]);
  const walk=(parentId:string)=>{
    for(const folder of allFolders){
      if(String(folder.parentId??'')===parentId&&!ids.has(folder.id)){
        ids.add(folder.id);
        walk(folder.id);
      }
    }
  };
  walk(rootId);
  return allFolders.filter(folder=>ids.has(folder.id));
}
/** 渲染资源管理器式文件夹树节点（含当前节点与子级，Windows 资源管理器式折叠展开）。 */
function renderCompliancePolicyTreeNode(folder:WorkspaceFolder,folders:WorkspaceFolder[],selectedFolderId:string,expandedIds:Set<string>,pickedFolderCounts:Map<string,number>,depth:number):string{
  const childFolders=folders
    .filter(item=>String(item.parentId??'')===folder.id)
    .sort((left,right)=>left.name.localeCompare(right.name,'zh-CN'));
  const hasChildren=childFolders.length>0;
  const expanded=expandedIds.has(folder.id);
  const selected=selectedFolderId===folder.id;
  const chevronSvg=hasChildren
    ?`<button type="button" class="cr-explorer-tree-toggle${expanded?' is-expanded':''}" data-policy-tree-toggle="${escapeHtml(folder.id)}" aria-label="${expanded?'收起':'展开'} ${escapeHtml(folder.name)}">
        <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4l4 4-4 4"/></svg>
      </button>`
    :'<span class="cr-explorer-tree-spacer" aria-hidden="true"></span>';
  
  // 区分展开与闭合文件夹系统图标
  const folderIconSrc=expanded?CR_ICON_FOLDER_OPEN:CR_ICON_FOLDER_CLOSED;

  // 该文件夹下已勾选的文件数量气泡
  const pickedCount=pickedFolderCounts.get(folder.id)||0;
  const countBadgeHtml=pickedCount>0
    ?`<span class="cr-explorer-tree-badge" title="该文件夹下已选 ${pickedCount} 份制度">${pickedCount}</span>`
    :'';

  const subtree=hasChildren&&expanded?childFolders.map(child=>renderCompliancePolicyTreeNode(child,folders,selectedFolderId,expandedIds,pickedFolderCounts,depth+1)).join(''):'';
  return`<div class="cr-explorer-tree-node" data-policy-tree-node="${escapeHtml(folder.id)}">
    <div class="cr-explorer-tree-row${selected?' is-selected':''}" style="--tree-depth:${depth}">
      ${chevronSvg}
      <button type="button" class="cr-explorer-tree-item" data-policy-tree-folder="${escapeHtml(folder.id)}">
        <span class="cr-explorer-tree-icon" aria-hidden="true">
          <img src="${folderIconSrc}" alt="" class="cr-folder-icon-img" loading="lazy">
        </span>
        <span class="cr-explorer-tree-name" title="${escapeHtml(folder.name)}">${escapeHtml(folder.name)}</span>
        ${countBadgeHtml}
      </button>
    </div>
    ${subtree}
  </div>`;
}
/**
 * 打开合规制度多选弹窗：左侧文件夹树 + 右侧文件列表（资源管理器布局）。
 * @param container 项目设置表单容器
 * @param complianceContext 制度库上下文
 * @param onConfirm 确认添加回调
 */
async function openCompliancePolicyPickerModal(container:HTMLElement,complianceContext?:ComplianceContext,onConfirm?:(ids:string[])=>void):Promise<void>{
  const panel=container.querySelector<HTMLElement>('[data-compliance-policy-selection]');
  if(!panel)return;
  const scope=container.querySelector<HTMLInputElement>('input[name="complianceLibraryScope"]:checked')?.value==='enterprise'?'enterprise':'personal';
  const scopeBadgeText=scope==='enterprise'?'团队空间制度库':'个人空间制度库';
  const alreadySelected=new Set(collectSelectedCompliancePolicyIds(container));
  const picked=new Map<string,PolicyCatalogItem>();
  const expandedIds=new Set<string>();
  let selectedFolderId='';
  let libraryRootId='';
  let libraryFolders:WorkspaceFolder[]=[];
  const overlay=document.createElement('div');
  overlay.className='cr-folder-modal-overlay';
  overlay.innerHTML=`<div class="cr-folder-modal-card cr-compliance-policy-modal-card" role="dialog" aria-modal="true" aria-labelledby="crPolicyModalTitle">
    <header class="cr-folder-modal-header">
      <div class="cr-folder-modal-header-text">
        <span class="cr-folder-modal-header-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
        </span>
        <h3 id="crPolicyModalTitle" class="cr-folder-modal-title">选择合规管理制度</h3>
        <span class="cr-folder-scope-pill">${escapeHtml(scopeBadgeText)}</span>
      </div>
      <button type="button" class="cr-folder-modal-close" data-policy-modal-close aria-label="关闭" title="关闭窗口">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </header>
    <div class="cr-compliance-policy-explorer">
      <aside class="cr-compliance-policy-explorer-tree-wrap">
        <div class="cr-compliance-policy-explorer-tree" data-policy-tree><div class="cr-preview-loading">正在加载…</div></div>
      </aside>
      <section class="cr-compliance-policy-explorer-main">
        <div class="cr-compliance-policy-explorer-toolbar">
          <div class="cr-compliance-policy-explorer-path" data-policy-modal-path>正在加载…</div>
        </div>
        <div class="cr-compliance-policy-explorer-files-head">
          <span class="cr-explorer-col-check"></span>
          <span class="cr-explorer-col-name">名称</span>
        </div>
        <div class="cr-compliance-policy-explorer-files" data-compliance-policy-modal-list><div class="cr-preview-loading">请选择左侧文件夹</div></div>
      </section>
    </div>
    <footer class="cr-folder-modal-footer">
      <div class="cr-folder-modal-current">
        <span class="cr-folder-modal-current-lbl">已勾选：</span>
        <div class="cr-folder-modal-current-chip" title="">
          <span class="cr-folder-chip-text" data-policy-modal-selected-count>0 份</span>
        </div>
      </div>
      <div class="cr-folder-modal-footer-btns">
        <button type="button" class="cr-button cr-folder-btn-cancel" data-policy-modal-close>取消</button>
        <button type="button" class="cr-button cr-folder-btn-confirm" data-policy-modal-confirm disabled>确认添加</button>
      </div>
    </footer>
  </div>`;
  document.body.appendChild(overlay);
  const treeEl=overlay.querySelector<HTMLElement>('[data-policy-tree]');
  const listEl=overlay.querySelector<HTMLElement>('[data-compliance-policy-modal-list]');
  const pathEl=overlay.querySelector<HTMLElement>('[data-policy-modal-path]');
  const countEl=overlay.querySelector<HTMLElement>('[data-policy-modal-selected-count]');
  const confirmBtn=overlay.querySelector<HTMLButtonElement>('[data-policy-modal-confirm]');
  const close=()=>overlay.remove();
  overlay.querySelectorAll<HTMLButtonElement>('[data-policy-modal-close]').forEach(button=>button.addEventListener('click',close));
  overlay.addEventListener('click',event=>{if(event.target===overlay)close()});
  const updateCount=()=>{
    if(countEl)countEl.textContent=`${picked.size} 份`;
    if(confirmBtn)confirmBtn.disabled=picked.size<=0;
  };
  const getPickedFolderCounts=():Map<string,number>=>{
    const counts=new Map<string,number>();
    picked.forEach(item=>{
      if(item.folderId){
        counts.set(item.folderId,(counts.get(item.folderId)||0)+1);
      }
    });
    return counts;
  };
  const togglePick=(file:PolicyCatalogItem,checked:boolean)=>{
    if(checked)picked.set(file.id,file);
    else picked.delete(file.id);
    updateCount();
    renderTree();
  };
  const renderTree=()=>{
    if(!treeEl||!libraryFolders.length||!libraryRootId)return;
    const treeRoot=libraryFolders.find(folder=>folder.id===libraryRootId);
    if(!treeRoot)return;
    const pickedFolderCounts=getPickedFolderCounts();
    treeEl.innerHTML=renderCompliancePolicyTreeNode(treeRoot,libraryFolders,selectedFolderId,expandedIds,pickedFolderCounts,0);
    treeEl.querySelectorAll<HTMLButtonElement>('[data-policy-tree-toggle]').forEach(button=>{
      button.addEventListener('click',event=>{
        event.stopPropagation();
        const folderId=button.dataset.policyTreeToggle??'';
        if(expandedIds.has(folderId))expandedIds.delete(folderId);
        else expandedIds.add(folderId);
        renderTree();
      });
    });
    treeEl.querySelectorAll<HTMLButtonElement>('[data-policy-tree-folder]').forEach(button=>{
      button.addEventListener('click',()=>{
        selectedFolderId=button.dataset.policyTreeFolder??selectedFolderId;
        renderTree();
        void renderFiles();
      });
    });
  };
  const renderFiles=async()=>{
    if(!listEl||!selectedFolderId)return;
    listEl.innerHTML='<div class="cr-preview-loading">正在加载…</div>';
    const browse=await browseCompliancePolicyFolder(container,complianceContext,selectedFolderId);
    if(!browse){
      listEl.innerHTML='<div class="cr-compliance-policy-empty">制度文件加载失败，请稍后重试。</div>';
      return;
    }
    if(pathEl){
      pathEl.textContent=browse.currentFolderPath||browse.currentFolderName||browse.folderName;
      pathEl.title=browse.currentFolderPath||browse.currentFolderName;
    }
    const currentSubFolders=libraryFolders
      .filter(item=>String(item.parentId??'')===selectedFolderId)
      .sort((left,right)=>left.name.localeCompare(right.name,'zh-CN'));
    const files=browse.files.filter(file=>!alreadySelected.has(file.id));

    if(!currentSubFolders.length&&!files.length){
      listEl.innerHTML='<div class="cr-compliance-policy-empty">当前文件夹下没有子文件夹或可添加的制度文件。</div>';
      return;
    }

    const foldersHtml=currentSubFolders.map(childFolder=>`
      <div class="cr-explorer-folder-row" data-policy-open-subfolder="${escapeHtml(childFolder.id)}" title="双击或点击进入 ${escapeHtml(childFolder.name)}">
        <span class="cr-explorer-file-check"></span>
        <span class="cr-explorer-file-icon"><img src="${CR_ICON_FOLDER_CLOSED}" alt="" class="cr-folder-icon-img" loading="lazy"></span>
        <span class="cr-explorer-folder-name">${escapeHtml(childFolder.name)}</span>
        <span class="cr-explorer-folder-tag">文件夹</span>
      </div>
    `).join('');

    const filesHtml=files.map(file=>{
      const checked=picked.has(file.id);
      return`<label class="cr-explorer-file-row${checked?' is-checked':''}">
        <span class="cr-explorer-file-check"><input type="checkbox" data-compliance-policy-pick value="${escapeHtml(file.id)}" ${checked?'checked':''}></span>
        <span class="cr-explorer-file-icon"><img src="${iconFor(file.fileName)}" alt="" loading="lazy"></span>
        <span class="cr-explorer-file-name" title="${escapeHtml(file.fileName)}">${escapeHtml(file.fileName)}</span>
      </label>`;
    }).join('');

    listEl.innerHTML=`${foldersHtml}${filesHtml}`;

    listEl.querySelectorAll<HTMLElement>('[data-policy-open-subfolder]').forEach(folderRow=>{
      folderRow.addEventListener('click',()=>{
        const targetId=folderRow.dataset.policyOpenSubfolder;
        if(!targetId)return;
        selectedFolderId=targetId;
        expandedIds.add(targetId);
        renderTree();
        void renderFiles();
      });
    });

    listEl.querySelectorAll<HTMLInputElement>('input[data-compliance-policy-pick]').forEach(input=>{
      input.addEventListener('change',()=>{
        const file=files.find(item=>item.id===input.value);
        if(!file)return;
        togglePick({id:file.id,fileName:file.fileName,folderPath:file.folderPath,folderId:selectedFolderId},input.checked);
        const row=input.closest('.cr-explorer-file-row');
        if(row)row.classList.toggle('is-checked',input.checked);
      });
    });
  };
  const bootstrap=async()=>{
    const browse=await browseCompliancePolicyFolder(container,complianceContext);
    if(!browse||!browse.libraryRootId){
      if(treeEl)treeEl.innerHTML='<div class="cr-compliance-policy-empty">制度库加载失败，请检查制度库路径配置。</div>';
      if(listEl)listEl.innerHTML='';
      return;
    }
    libraryRootId=browse.libraryRootId;
    selectedFolderId=browse.currentFolderId||browse.libraryRootId;
    expandedIds.add(libraryRootId);
    const enterpriseOrgId=complianceContext?.enterpriseOrgId??'';
    const allFolders=await loadWorkspaceFolders(scope,enterpriseOrgId);
    libraryFolders=collectComplianceLibrarySubtreeFolders(allFolders,browse.libraryRootId);
    if(!libraryFolders.some(folder=>folder.id===browse.libraryRootId)){
      libraryFolders.unshift({id:browse.libraryRootId,name:browse.folderName||browse.currentFolderName||'合规管理制度'});
    }
    renderTree();
    await renderFiles();
  };
  confirmBtn?.addEventListener('click',()=>{
    if(!picked.size)return;
    const items=[...picked.values()];
    writeCompliancePolicyCatalog(panel,mergePolicyCatalog(readCompliancePolicyCatalog(panel),items));
    onConfirm?.(items.map(item=>item.id));
    close();
  });
  void bootstrap();
  updateCount();
}
/** 刷新项目设置中的合规制度列表 DOM。 */
function refreshCompliancePolicyList(container:HTMLElement):void{
  const panel=container.querySelector<HTMLElement>('[data-compliance-policy-selection]');
  const list=panel?.querySelector<HTMLElement>('[data-compliance-policy-list]');
  if(!panel||!list)return;
  const selectedIds=collectSelectedCompliancePolicyIds(container);
  let recommendations:CompliancePolicyRecommendation[]=[];
  try{recommendations=JSON.parse(decodeURIComponent(panel.getAttribute('data-recommended-policies')??'%5B%5D')) as CompliancePolicyRecommendation[]}catch{/** ignore */}
  const catalog=readCompliancePolicyCatalog(panel);
  const profileStub:QuickProfile|null=recommendations.length?{fileId:'',sourceFileName:'',status:'ready',contractTitle:'',contractType:'',typeConfidence:0,parties:[],suggestedReviewPosition:'',evidence:[],warnings:[],detectedAt:'',recommendedCompliancePolicies:recommendations}:null;
  const known=compliancePolicyMetaMap(profileStub,selectedIds,catalog);
  list.innerHTML=buildCompliancePolicyRows(selectedIds,known);
}
function collectSelectedCompliancePolicyIds(container:HTMLElement):string[]{
  const list=container.querySelector('[data-compliance-policy-selection]');
  if(!list)return[];
  return[...list.querySelectorAll<HTMLElement>('[data-compliance-policy-row]')].map(row=>row.dataset.compliancePolicyId??'').filter(Boolean);
}
/** 校验项目设置：开启合规管理时必须至少选择一份参照制度。 */
function validateCompliancePolicySelection(preferences:Preferences):string|null{
  if(!preferences.scopes.compliance)return null;
  if(preferences.selectedCompliancePolicyFileIds.length)return null;
  return'已开启合规管理审核，请至少选择一份参照的合规制度，或取消勾选合规管理。';
}
function complianceLibraryPanelHtml(hasEnterprise:boolean,personalSummary:ComplianceLibrarySummary|null,enterpriseSummary:ComplianceLibrarySummary|null,complianceEnabled:boolean,selectedScope:ComplianceLibraryScope,folders:ComplianceLibraryFolders):string{
  const personalCount=personalSummary?.fileCount??0;
  const enterpriseCount=enterpriseSummary?.fileCount??0;
  const scope=selectedScope==='enterprise'&&hasEnterprise?'enterprise':'personal';
  return`<div class="cr-compliance-library${complianceEnabled?' is-open':''}" data-compliance-library ${complianceEnabled?'':'hidden'}>
    <div class="cr-compliance-library-head">
      <span class="cr-compliance-library-title">合规制度库</span>
    </div>
    <div class="cr-compliance-library-options">
      <label class="cr-compliance-library-option${scope==='personal'?' is-active':''}">
        <input type="radio" name="complianceLibraryScope" value="personal" ${scope==='personal'?'checked':''} data-compliance-scope>
        <span class="cr-compliance-opt-name">个人制度库 <small>${personalCount?`${personalCount}份`:'暂无'}</small></span>
        ${complianceFolderPathHtml('personal',folders.personal,personalSummary)}
      </label>
      ${hasEnterprise?`<label class="cr-compliance-library-option${scope==='enterprise'?' is-active':''}">
        <input type="radio" name="complianceLibraryScope" value="enterprise" ${scope==='enterprise'?'checked':''} data-compliance-scope>
        <span class="cr-compliance-opt-name">企业制度库 <small>${enterpriseCount?`${enterpriseCount}份`:'暂无'}</small></span>
        ${complianceFolderPathHtml('enterprise',folders.enterprise,enterpriseSummary)}
      </label>`:''}
    </div>
    <div class="cr-compliance-library-actions">
      <button type="button" class="cr-button cr-button-secondary cr-scheme-btn-sm" data-open-compliance-library>打开目录</button>
      <button type="button" class="cr-button cr-button-quiet cr-scheme-btn-sm" data-upload-compliance-library>上传文件</button>
    </div>
  </div>`;
}
/**
 * 任务方案：批注内容构成默认勾选（审核结果页仍可逐条调整）。
 */
function taskSchemeAnnotateDefaultsMarkup(value:Preferences):string{
  const desc=value.defaultAnnotateIncludeDescription!==false;
  const sugg=value.defaultAnnotateIncludeSuggestion!==false;
  return`<div class="cr-scheme-rule-block cr-scheme-annotate-parts-block">
    <span class="cr-scheme-rule-label">批注内容构成</span>
    <div class="cr-scheme-annotate-parts-row">
      <label class="cr-result-annotate-part"><input type="checkbox" data-settings-annotate-include-description ${desc?'checked':''}>风险说明</label>
      <label class="cr-result-annotate-part"><input type="checkbox" data-settings-annotate-include-suggestion ${sugg?'checked':''}>修改意见</label>
    </div>
    <p class="cr-scheme-annotate-parts-hint">新建审查时，批注默认包含以上所选内容。</p>
  </div>`;
}
function settingsFieldsHtml(_taskId:string,value:Preferences,profile:QuickProfile|null,_companyName:string,userDisplayName:string,mode:SettingsFormMode,complianceContext?:{hasEnterprise:boolean;personal:ComplianceLibrarySummary|null;enterprise:ComplianceLibrarySummary|null}):string{
  const recognizedType=profile?.contractType||'';
  const typeOptions=buildContractTypeSelectOptions(value.contractType,recognizedType,mode==='task');
  const isTaskMode=mode==='task';
  const taskPosition=isTaskMode?buildTaskReviewPositionOptions(userDisplayName,value.reviewPositionCompany):null;
  const projectPosition=isTaskMode?null:buildProjectReviewPositionOptions(profile,userDisplayName,value.reviewPositionCompany);
  const positionOptions=(isTaskMode?taskPosition?.options:projectPosition?.options)??[];
  const positionSelected=(isTaskMode?taskPosition?.selected:projectPosition?.selected)??'';
  const customSelected=!isTaskMode&&positionSelected===TASK_CUSTOM_POSITION_VALUE;
  const customDraft=isTaskMode?(taskPosition?.customDraft??''):(customSelected?value.reviewPositionCompany:'');
  const positionHint='';
  const customBlock=`<div class="cr-settings-custom${customSelected?' is-open':''}" data-settings-custom><input class="cr-input cr-settings-custom-input" data-settings-position-draft maxlength="200" value="${escapeHtml(customDraft)}" placeholder="输入主体全称并点击确认"><button type="button" class="cr-button cr-button-secondary cr-settings-custom-btn" data-settings-position-confirm>确认</button></div>`;
  const customOption=`<option value="__custom__" ${customSelected?'selected':''}>自定义主体…</option>`;
  const positionField=isTaskMode
    ?taskPositionFieldHtml(positionOptions,positionSelected,customDraft)
    :projectPositionFieldHtml(positionOptions,positionSelected,customSelected,customDraft,positionHint,customBlock,customOption,value.reviewPositionCompany);
  const compliancePanel=complianceContext?complianceLibraryPanelHtml(complianceContext.hasEnterprise,complianceContext.personal,complianceContext.enterprise,value.scopes.compliance,value.complianceLibraryScope,value.complianceLibraryFolders):'';
  const policySelectionPanel=!isTaskMode&&value.scopes.compliance?compliancePolicySelectionPanelHtml(value,profile,buildInitialPolicyCatalog(profile)):'';
  const scopeItems=[['legal','法律风险'],['business','商业风险'],['compliance','合规管理']].map(item=>{
    const scopeKey=item[0] as keyof ReviewScopes;
    const style=value.scopeAnnotationStyles[scopeKey];
    return`<label class="cr-settings-scope-item"><input type="checkbox" data-settings-scope value="${item[0]}" ${value.scopes[scopeKey]?'checked':''}><span>${item[1]}</span>${scopeAnnotationStyleControls(scopeKey,style)}</label>`;
  }).join('');
  return`<div class="cr-settings-panel-body" data-settings-mode="${mode}">
    <div class="cr-settings-grid-main">
      <div class="cr-settings-field">
        ${positionField}
      </div>
      <div class="cr-settings-field">
        <label class="cr-settings-label" for="cr-settings-reviewer-name">审阅人员</label>
        <input class="cr-input cr-settings-input" id="cr-settings-reviewer-name" data-settings-reviewer-name maxlength="80" value="${escapeHtml(value.reviewerName)}" placeholder="您的姓名">
      </div>
      <div class="cr-settings-field">
        <label class="cr-settings-label" for="cr-settings-contract-type">合同类型</label>
        <select class="cr-select cr-settings-select" id="cr-settings-contract-type" data-settings-contract-type>
          ${typeOptions.map(item=>`<option value="${escapeHtml(item)}" ${value.contractType===item?'selected':''}>${escapeHtml(contractTypeSelectOptionLabel(item,recognizedType,mode))}</option>`).join('')}
        </select>
      </div>
      <div class="cr-settings-field cr-settings-field-depth">
        <span class="cr-settings-label">审查深度</span>
        <div class="cr-settings-segmented" role="radiogroup" aria-label="审查深度">
          <label class="cr-settings-segment${value.reviewDepth==='standard'?' is-active':''}"><input type="radio" name="settingsReviewDepth" data-settings-depth value="standard" ${value.reviewDepth==='standard'?'checked':''}><span>标准</span></label>
          <label class="cr-settings-segment${value.reviewDepth==='deep'?' is-active':''}"><input type="radio" name="settingsReviewDepth" data-settings-depth value="deep" ${value.reviewDepth==='deep'?'checked':''}><span>深度</span></label>
        </div>
      </div>
    </div>
    <div class="cr-settings-scope-section">
      <span class="cr-settings-label">审核范围与批注样式</span>
      <div class="cr-settings-scope">${scopeItems}</div>
      ${compliancePanel}
      ${policySelectionPanel}
    </div>
  </div>`;
}
function settingsExpandHtml(taskId:string,snapshot:ProjectSnapshot,companyName:string,auth:AuthSnapshot):string{
  const {project}=snapshot;
  const value=resolveProjectSettingsPreferences(taskId,project.id,companyName,snapshot);
  const userDisplayName=auth.user.displayName||auth.user.email||'';
  const restoreBtn=snapshot.preferences?.hasProjectOverrides?`<button type="button" class="cr-button cr-button-quiet cr-settings-action-btn" data-project-settings-restore="${escapeHtml(project.id)}">恢复默认</button>`:'';
  return`<div class="cr-settings-panel">
    <div class="cr-settings-panel-toolbar">
      <div class="cr-settings-header-left">
        <span class="cr-settings-panel-title">项目审核设置</span>
      </div>
      <span class="cr-expand-settings-message" data-settings-message="${escapeHtml(project.id)}" hidden></span>
      <div class="cr-expand-settings-actions">
        ${restoreBtn}
        <button type="button" class="cr-button cr-button-primary cr-settings-action-btn cr-settings-save-btn" data-project-settings-save="${escapeHtml(project.id)}">保存设置</button>
      </div>
    </div>
    ${settingsFieldsHtml(taskId,value,snapshot.quickProfile,companyName,userDisplayName,'project',overviewComplianceContext??undefined)}
  </div>`;
}
function projectExpandInnerHtml(snapshot:ProjectSnapshot,mode:ProjectExpandMode|null,taskId:string,companyName:string,auth:AuthSnapshot):string{
  if(mode==='preview')return previewExpandHtml(snapshot);
  if(mode==='settings')return settingsExpandHtml(taskId,snapshot,companyName,auth);
  if(mode==='attachment')return attachmentExpandHtml(snapshot);
  return'';
}
function projectExpandMode(taskId:string,projectId:string,attachmentExpanded:boolean,previewExpanded:boolean,settingsExpanded:boolean):ProjectExpandMode|null{
  if(previewExpanded||loadPreviewExpandedProjectIds(taskId).has(projectId))return'preview';
  if(settingsExpanded||loadSettingsExpandedProjectIds(taskId).has(projectId))return'settings';
  if(attachmentExpanded||loadExpandedProjectIds(taskId).has(projectId))return'attachment';
  return null;
}
function projectTableRows(taskId:string,snapshot:ProjectSnapshot,index:number,attachmentExpanded:boolean,previewExpanded:boolean,settingsExpanded:boolean,active:boolean,selected:boolean,companyName:string,auth:AuthSnapshot):string{
  const {project,files,main}=snapshot;
  const displayName=projectDisplayName(project,index);
  const status=projectRowStatus(taskId,snapshot);
  const attachmentCount=files.attachmentFiles.length;
  const attachmentLabel=attachmentCount?`管理附件（${attachmentCount}）`:'管理附件';
  const nameCell=`<input class="cr-input cr-project-name" data-project-name="${escapeHtml(project.id)}" value="${escapeHtml(displayName)}" maxlength="120" aria-label="项目名称">`;
  const attachBtn=tableAttachmentButton(project.id,attachmentCount,attachmentExpanded,attachmentLabel);
  const bodyCell=renderProjectMainBodyCell(project.id,main);
  const archiveReady=status.className==='is-done';
  const archiveIcon=archiveReady?CR_ICON_ARCHIVE_READY:CR_ICON_ARCHIVE_DISABLED;
  const archiveLabel=archiveReady?'成果归档':'审核未完成，暂不可归档';
  const archiveBtnClass=archiveReady?'cr-project-archive':'cr-project-archive is-archive-locked';
  const archiveAttrs=archiveReady?`data-project-archive="${escapeHtml(project.id)}"`:`data-project-archive="${escapeHtml(project.id)}" disabled`;
  const previewEnabled=isLegalFeatureEnabled('legal-quick-inspect-v1');
  const previewAttrs=previewEnabled&&main?`data-project-preview="${escapeHtml(project.id)}" aria-expanded="${previewExpanded?'true':'false'}"`:`data-project-preview="${escapeHtml(project.id)}" disabled`;
  const previewBtnClass=`cr-project-preview${previewExpanded?' is-active':''}`;
  const previewBtn=tableIconButton(previewBtnClass,previewIcon(previewExpanded),previewEnabled?'快速预览':'合同预审已关闭',previewAttrs);
  const settingsBtn=tableSettingsButton(project.id,settingsExpanded,Boolean(snapshot.preferences?.hasProjectOverrides));
  const reviewMeta=projectReviewActionMeta(taskId,snapshot);
  const reviewBtn=tableReviewButton(project.id,reviewMeta.enabled,reviewMeta.running,reviewMeta.title,reviewMeta.emphasize);
  const archiveEnabled=isLegalFeatureEnabled('legal-archive-v1');
  const archiveBtn=tableIconButton(archiveBtnClass,archiveIcon,archiveEnabled?archiveLabel:'成果归档已关闭',archiveEnabled?archiveAttrs:`data-project-archive="${escapeHtml(project.id)}" disabled`);
  const canViewResults=reviewHasVisibleResults(snapshot.review,snapshot.risks);
  const failureNote=reviewFailureNoteText(snapshot.review);
  const statusNote=failureNote?`<p class="cr-row-status-note" title="${escapeHtml(snapshot.review?.error??'')}">${escapeHtml(failureNote)}</p>`:'';
  const resultCell=`<button type="button" class="cr-button cr-button-text cr-project-result-btn${canViewResults?' is-viewable':''}${reviewFinishedSuccessfully(snapshot.review)?' is-completed':snapshot.review?.status==='failed'&&canViewResults?' is-partial':''}" data-project-result="${escapeHtml(project.id)}" title="${canViewResults?'查看审核结果（风险复核）':'暂无可用审核意见'}" ${canViewResults?'':'disabled'}><span class="cr-result-label">查看结果</span></button>`;
  const expandMode=projectExpandMode(taskId,project.id,attachmentExpanded,previewExpanded,settingsExpanded);
  const expandInner=projectExpandInnerHtml(snapshot,expandMode,taskId,companyName,auth);
  const schemeBadge=projectSchemeBadge(snapshot,taskId);
  return`<tr class="cr-project-table-row${active?' is-active':''}${expandMode==='attachment'?' is-expanded':''}${expandMode==='preview'?' is-preview-expanded':''}${expandMode==='settings'?' is-settings-expanded':''}" data-project-id="${escapeHtml(project.id)}"><td class="cr-col-check"><input type="checkbox" class="cr-project-select" data-project-select="${escapeHtml(project.id)}" ${selected?'checked':''} aria-label="选择项目"></td><td class="cr-col-index">${String(index+1).padStart(2,'0')}</td><td class="cr-col-name">${nameCell}</td><td class="cr-col-body">${bodyCell}</td><td class="cr-col-project-actions"><div class="cr-project-action-icons">${attachBtn}${previewBtn}${settingsBtn}${reviewBtn}</div></td><td class="cr-col-status"><div class="cr-row-status-wrap"><span class="cr-row-status ${status.className}">${status.label}</span>${statusNote}${schemeBadge}</div></td><td class="cr-col-result">${resultCell}</td><td class="cr-col-archive">${archiveBtn}</td></tr><tr class="cr-project-expand-row" data-project-id="${escapeHtml(project.id)}" data-expand-mode="${expandMode??''}" ${expandMode?'':'hidden'}><td colspan="8"><div class="cr-project-expand-inner">${expandInner}</div></td></tr>`;
}
function settingsDrawerShell():string{
  return`<div class="cr-settings-drawer" id="crSettingsDrawer" hidden><button type="button" class="cr-settings-drawer-backdrop" id="crSettingsDrawerBackdrop" aria-label="关闭设置"></button><div class="cr-settings-drawer-panel" role="dialog" aria-labelledby="crSettingsDrawerTitle"><header class="cr-settings-drawer-head"><h3 id="crSettingsDrawerTitle">批量应用设置</h3><button type="button" class="cr-icon-button" id="crSettingsDrawerClose">关闭</button></header><div class="cr-settings-drawer-body cr-expand-settings-body" id="crSettingsDrawerBody"></div><footer class="cr-settings-drawer-foot"><span id="crSettingsDrawerMessage"></span><div><button type="button" class="cr-button cr-button-quiet" id="crSettingsDrawerCancel">取消</button><button type="button" class="cr-button cr-button-primary" id="crSettingsDrawerSave">保存设置</button></div></footer></div></div>`;
}
function projectHub(taskName:string,taskId:string,projects:ProjectSnapshot[],expandedIds:Set<string>,previewExpandedIds:Set<string>,settingsExpandedIds:Set<string>,focusProjectId:string,companyName:string,auth:AuthSnapshot,workflowNav=''):string{
  const rows=projects.length?projects.map((snapshot,index)=>projectTableRows(taskId,snapshot,index,expandedIds.has(snapshot.project.id),previewExpandedIds.has(snapshot.project.id),settingsExpandedIds.has(snapshot.project.id),snapshot.project.id===focusProjectId,false,companyName,auth)).join(''):'';
  const tableBody=projects.length?`<tbody id="crProjectTableBody">${rows}</tbody>`:`<tbody id="crProjectTableBody"></tbody>`;
  const empty=projects.length?'':'<div class="cr-project-empty"><b>还没有合同审核项目</b><span>点击「新建项目」创建空项目；或将多份合同正文拖到「新建项目」上批量创建。请优先上传文字版，扫描件请控制体积和页数。项目会归档到当前任务「'+escapeHtml(taskName)+'」下的个人空间目录。</span></div>';
  return`<div class="cr-overview-full"><section class="cr-panel cr-project-workbench"><div class="cr-project-toolbar"><div class="cr-project-toolbar-actions"><div class="cr-project-toolbar-lead"><div class="cr-create-project-wrap"><button type="button" class="cr-button cr-button-primary" id="createLegalProject" title="点击创建空项目，或将多份合同正文拖到此按钮上">新建项目</button><span class="cr-create-project-hint">${LEGAL_REVIEW_CREATE_PROJECT_HINT}</span></div>${workflowNav}</div><button type="button" class="cr-toolbar-icon-btn" id="batchArchiveProjects" hidden aria-label="归档" title="归档"><img src="${CR_ICON_ARCHIVE_READY}" alt=""></button><button type="button" class="cr-toolbar-icon-btn" id="batchPreviewProjects" hidden aria-label="预览" title="预览"><img src="${CR_ICON_PREVIEW_CLOSED}" alt=""></button><button type="button" class="cr-toolbar-icon-btn" id="batchDeleteProjects" hidden aria-label="删除" title="删除"><img src="${CR_ICON_DELETE}" alt=""></button></div><button type="button" class="cr-button cr-button-primary cr-project-toolbar-review" id="batchStartReview" hidden disabled>审核</button></div><div class="cr-project-table-wrap${projects.length?'':' is-empty'}"><table class="cr-project-table" aria-label="合同审核项目列表"><thead><tr><th class="cr-col-check"><input type="checkbox" id="crProjectSelectAll" aria-label="全选"></th><th class="cr-col-index">序号</th><th class="cr-col-name">项目名称</th><th class="cr-col-body">正文</th><th class="cr-col-project-actions"><div class="cr-project-action-headings"><span>附件</span><span>预览</span><span>设置</span><span>审核</span></div></th><th class="cr-col-status">状态</th><th class="cr-col-result">审核结果</th><th class="cr-col-archive">归档</th></tr></thead>${tableBody}</table>${empty}</div></section></div>${settingsDrawerShell()}`;
}
function readComplianceFolderFromContainer(container:HTMLElement,scope:'personal'|'enterprise'):ComplianceLibraryFolderRef|null{
  const folderId=container.querySelector<HTMLInputElement>(`[data-compliance-folder-id="${scope}"]`)?.value.trim()??'';
  const folderPath=container.querySelector<HTMLInputElement>(`[data-compliance-folder-path="${scope}"]`)?.value.trim()??'';
  return normalizeComplianceLibraryFolderRef(folderId?{folderId,folderPath}:null);
}
function collectScopeAnnotationStyles(container:HTMLElement,fallback:ScopeAnnotationStyles):ScopeAnnotationStyles{
  const read=(scope:'legal'|'business'|'compliance')=>{
    const colorInput=container.querySelector<HTMLInputElement>(`[data-settings-scope-color="${scope}"]`);
    const fontSelect=container.querySelector<HTMLSelectElement>(`[data-settings-scope-font="${scope}"]`);
    return normalizeScopeAnnotationStyle({
      color:colorInput?.value??fallback[scope].color,
      font:fontSelect?.value??fallback[scope].font,
    },fallback[scope]);
  };
  return{legal:read('legal'),business:read('business'),compliance:read('compliance')};
}
function collectSettingsPreferences(container:HTMLElement,fallback:Preferences):Preferences{
  const scopes=[...container.querySelectorAll<HTMLInputElement>('input[data-settings-scope]')];
  const positionRadio=container.querySelector<HTMLInputElement>('input[data-settings-position-radio]:checked');
  const positionSelect=container.querySelector<HTMLSelectElement>('[data-settings-position-select]');
  const hiddenPosition=container.querySelector<HTMLInputElement>('[data-settings-position]');
  const draftPosition=container.querySelector<HTMLInputElement>('[data-settings-position-draft]');
  let reviewPositionCompany=hiddenPosition?.value.trim()||'';
  if(positionRadio?.value===TASK_CUSTOM_POSITION_VALUE){
    reviewPositionCompany=hiddenPosition?.value.trim()||draftPosition?.value.trim()||reviewPositionCompany;
  }else if(positionRadio?.value){
    reviewPositionCompany=positionRadio.value.trim();
  }else if(positionSelect?.value==='__custom__'||positionSelect?.value===TASK_CUSTOM_POSITION_VALUE){
    reviewPositionCompany=draftPosition?.value.trim()||reviewPositionCompany;
  }else if(positionSelect?.value){
    reviewPositionCompany=positionSelect.value.trim();
  }
  const reviewerName=container.querySelector<HTMLInputElement>('[data-settings-reviewer-name]')?.value.trim()||fallback.reviewerName;
  const complianceLibraryScope=container.querySelector<HTMLInputElement>('input[name="complianceLibraryScope"]:checked')?.value==='enterprise'?'enterprise':'personal';
  const complianceEnabled=scopes.find(item=>item.value==='compliance')?.checked??false;
  const annotateDescInput=container.querySelector<HTMLInputElement>('[data-settings-annotate-include-description]');
  const annotateSuggInput=container.querySelector<HTMLInputElement>('[data-settings-annotate-include-suggestion]');
  return normalizePreferences({reviewPositionCompany:reviewPositionCompany||fallback.reviewPositionCompany,reviewerName,contractType:container.querySelector<HTMLSelectElement>('[data-settings-contract-type]')?.value||fallback.contractType,reviewDepth:container.querySelector<HTMLInputElement>('input[data-settings-depth]:checked')?.value==='standard'?'standard':'deep',reviewFocus:fallback.reviewFocus,scopes:{legal:scopes.find(item=>item.value==='legal')?.checked??false,business:scopes.find(item=>item.value==='business')?.checked??false,compliance:complianceEnabled},scopeAnnotationStyles:collectScopeAnnotationStyles(container,fallback.scopeAnnotationStyles),complianceLibraryScope,complianceLibraryFolders:{personal:readComplianceFolderFromContainer(container,'personal'),enterprise:readComplianceFolderFromContainer(container,'enterprise')},selectedCompliancePolicyFileIds:complianceEnabled?collectSelectedCompliancePolicyIds(container):[],enhancedFeatures:{enterpriseOwnershipLookup:container.querySelector<HTMLInputElement>('input[data-settings-enterprise-ownership]')?.checked??fallback.enhancedFeatures.enterpriseOwnershipLookup},defaultAnnotateIncludeDescription:annotateDescInput?annotateDescInput.checked:fallback.defaultAnnotateIncludeDescription!==false,defaultAnnotateIncludeSuggestion:annotateSuggInput?annotateSuggInput.checked:fallback.defaultAnnotateIncludeSuggestion!==false});
}
function collectDrawerPreferences(root:HTMLElement,fallback:Preferences):Preferences{
  const body=root.querySelector<HTMLElement>('#crSettingsDrawerBody');
  return body?collectSettingsPreferences(body,fallback):fallback;
}
/** 同步审核立场自定义输入区与隐藏值。 */
function syncSettingsPositionUi(container:HTMLElement):void{
  const positionSelect=container.querySelector<HTMLSelectElement>('[data-settings-position-select]');
  const hiddenPosition=container.querySelector<HTMLInputElement>('[data-settings-position]');
  const customWrap=container.querySelector<HTMLElement>('[data-settings-custom]');
  const draftPosition=container.querySelector<HTMLInputElement>('[data-settings-position-draft]');
  const positionRadio=container.querySelector<HTMLInputElement>('input[data-settings-position-radio]:checked');
  if(positionRadio&&hiddenPosition&&customWrap){
    const custom=positionRadio.value===TASK_CUSTOM_POSITION_VALUE;
    customWrap.classList.toggle('is-open',custom);
    if(custom){
      customWrap.removeAttribute('hidden');
      draftPosition?.focus();
    }else{
      customWrap.setAttribute('hidden','');
      hiddenPosition.value=positionRadio.value;
    }
    return;
  }
  if(!positionSelect||!hiddenPosition)return;
  if(!customWrap){
    if(positionSelect.value)hiddenPosition.value=positionSelect.value;
    return;
  }
  if(positionSelect.value==='__custom__'){
    customWrap.classList.add('is-open');
    customWrap.removeAttribute('hidden');
    if(draftPosition&&!draftPosition.value)draftPosition.value=hiddenPosition.value;
    draftPosition?.focus();
    return;
  }
  customWrap.classList.remove('is-open');
  customWrap.setAttribute('hidden','');
  if(positionSelect.value)hiddenPosition.value=positionSelect.value;
}
/** 打开空间中的合规管理制度文件夹。 */
function navigateToComplianceLibrary(scope:'personal'|'enterprise',folderId:string):void{
  const query=new URLSearchParams({folder:folderId,scope});
  navigate(`/tax-assistant/space?${query.toString()}`);
}
/** 从容器读取当前选中的制度库文件夹 ID。 */
function resolveComplianceFolderId(container:HTMLElement,scope:'personal'|'enterprise',summary:ComplianceLibrarySummary|null):string{
  const selected=container.querySelector<HTMLInputElement>(`[data-compliance-folder-id="${scope}"]`)?.value.trim();
  if(selected)return selected;
  return summary?.folderId??'';
}
/**
 * 绑定审核范围的批注颜色选择器（支持行内直接输入色号 + 调色盘快速点选）。
 * @param container 设置面板外层容器
 */
function bindScopeColorPickers(container:HTMLElement):void{
  const widgets=container.querySelectorAll<HTMLElement>('.cr-color-picker-widget');
  widgets.forEach(widget=>{
    const trigger=widget.querySelector<HTMLButtonElement>('[data-color-trigger]');
    const inlineInput=widget.querySelector<HTMLInputElement>('.cr-color-inline-input');
    const menu=widget.querySelector<HTMLElement>('.cr-color-popover-menu');
    const hiddenInput=widget.querySelector<HTMLInputElement>('input[data-settings-scope-color]');
    if(!trigger||!menu||!hiddenInput)return;

    const applyColor=(rawHex:string,closeMenu=true)=>{
      const normalized=normalizeAnnotationColor(rawHex);
      hiddenInput.value=normalized;
      trigger.style.backgroundColor=`#${normalized}`;
      if(inlineInput&&inlineInput.value.toUpperCase()!==normalized){
        inlineInput.value=normalized;
      }

      const presetButtons=menu.querySelectorAll<HTMLButtonElement>('[data-color-select]');
      presetButtons.forEach(btn=>{
        const val=btn.dataset.colorSelect;
        const isSelected=val===normalized;
        btn.classList.toggle('is-selected',isSelected);
      });

      if(closeMenu){
        menu.hidden=true;
        trigger.setAttribute('aria-expanded','false');
      }
    };

    const initialHex=normalizeAnnotationColor(hiddenInput.value);
    applyColor(initialHex,false);

    trigger.addEventListener('click',event=>{
      event.stopPropagation();
      const willOpen=menu.hidden;
      document.querySelectorAll<HTMLElement>('.cr-color-popover-menu').forEach(m=>{
        if(m!==menu){
          m.hidden=true;
          m.closest('.cr-color-picker-widget')?.querySelector('[data-color-trigger]')?.setAttribute('aria-expanded','false');
        }
      });
      menu.hidden=!willOpen;
      trigger.setAttribute('aria-expanded',String(willOpen));
    });

    menu.addEventListener('click',event=>{
      event.stopPropagation();
    });

    menu.querySelectorAll<HTMLButtonElement>('[data-color-select]').forEach(btn=>{
      btn.addEventListener('click',()=>{
        const selectedHex=btn.dataset.colorSelect??'';
        applyColor(selectedHex,true);
      });
    });

    if(inlineInput){
      inlineInput.addEventListener('input',()=>{
        const raw=inlineInput.value.replace(/[^0-9a-fA-F]/g,'').slice(0,6).toUpperCase();
        inlineInput.value=raw;
        if(raw.length===6){
          hiddenInput.value=raw;
          trigger.style.backgroundColor=`#${raw}`;
          menu.querySelectorAll<HTMLButtonElement>('[data-color-select]').forEach(btn=>{
            btn.classList.toggle('is-selected',btn.dataset.colorSelect===raw);
          });
        }
      });

      inlineInput.addEventListener('keydown',event=>{
        if(event.key==='Enter'){
          event.preventDefault();
          applyColor(inlineInput.value,true);
          inlineInput.blur();
        }
      });

      inlineInput.addEventListener('blur',()=>{
        applyColor(inlineInput.value,false);
      });
    }
  });

  const onDocClick=(event:MouseEvent)=>{
    const target=event.target as HTMLElement|null;
    if(target?.closest('.cr-color-picker-widget'))return;
    container.querySelectorAll<HTMLElement>('.cr-color-popover-menu').forEach(menu=>{
      menu.hidden=true;
      menu.closest('.cr-color-picker-widget')?.querySelector('[data-color-trigger]')?.setAttribute('aria-expanded','false');
    });
  };
  document.addEventListener('click',onDocClick);
}

/**
 * 打开制度库文件夹路径选择器（全新 Apple / Google 顶级现代桌面设计）。
 * @param container 设置表单容器
 * @param scope 空间范围
 * @param enterpriseOrgId 企业组织 ID
 */
async function openComplianceFolderPicker(container:HTMLElement,scope:'personal'|'enterprise',enterpriseOrgId=''):Promise<void>{
  let resolvedEnterpriseOrgId=enterpriseOrgId;
  if(scope==='enterprise'&&!resolvedEnterpriseOrgId){
    const orgPayload=await api.get<{organizations:UserOrganization[]}>(apiUrl('/api/organizations')).catch(()=>({organizations:[] as UserOrganization[]}));
    resolvedEnterpriseOrgId=orgPayload.organizations?.find(isActiveEnterpriseOrganization)?.id??'';
    if(!resolvedEnterpriseOrgId){
      window.alert('当前账号暂无可用团队空间');
      return;
    }
  }
  let folders:WorkspaceFolder[]=[];
  try{
    folders=await loadWorkspaceFolders(scope,resolvedEnterpriseOrgId);
  }catch(error){
    window.alert(error instanceof Error?error.message:'加载文件夹失败');
    return;
  }
  if(!folders.length){
    window.alert('当前空间暂无可用文件夹');
    return;
  }

  // 初始选中：从当前输入框已保存的 folderId 或系统制度库默认目录中恢复
  const currentSavedFolderId=container.querySelector<HTMLInputElement>(`[data-compliance-folder-id="${scope}"]`)?.value.trim()||'';
  let selectedFolderId=currentSavedFolderId||folders.find(item=>item.name==='合规管理制度')?.id||folders[0]?.id||'';
  let viewParentId=folders.find(item=>item.id===selectedFolderId)?.parentId||'';

  const scopeBadgeText=scope==='enterprise'?'团队空间':'个人空间';

  const overlay=document.createElement('div');
  overlay.className='cr-folder-modal-overlay';
  overlay.innerHTML=`<div class="cr-folder-modal-card" role="dialog" aria-modal="true" aria-labelledby="crFolderModalTitle">
    <header class="cr-folder-modal-header">
      <div class="cr-folder-modal-header-text">
        <span class="cr-folder-modal-header-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
        </span>
        <h3 id="crFolderModalTitle" class="cr-folder-modal-title">选择制度文件夹</h3>
        <span class="cr-folder-scope-pill">${escapeHtml(scopeBadgeText)}</span>
      </div>
      <button type="button" class="cr-folder-modal-close" data-folder-modal-close aria-label="关闭" title="关闭窗口">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </header>
    <div class="cr-folder-modal-body">
      <div class="cr-folder-modal-list" data-folder-modal-list></div>
    </div>
    <footer class="cr-folder-modal-footer">
      <div class="cr-folder-modal-current">
        <span class="cr-folder-modal-current-lbl">当前已选：</span>
        <div class="cr-folder-modal-current-chip" title="">
          <span class="cr-folder-chip-icon">📂</span>
          <span class="cr-folder-chip-text" data-folder-modal-current-name>尚未选择</span>
        </div>
      </div>
      <div class="cr-folder-modal-footer-btns">
        <button type="button" class="cr-button cr-folder-btn-cancel" data-folder-modal-close>取消</button>
        <button type="button" class="cr-button cr-folder-btn-confirm" data-folder-modal-confirm>确认使用该路径</button>
      </div>
    </footer>
  </div>`;

  document.body.appendChild(overlay);

  const listEl=overlay.querySelector<HTMLElement>('[data-folder-modal-list]');
  const currentNameEl=overlay.querySelector<HTMLElement>('[data-folder-modal-current-name]');
  const currentChipEl=overlay.querySelector<HTMLElement>('.cr-folder-modal-current-chip');

  const close=()=>overlay.remove();

  const updateSelectedDisplay=()=>{
    if(!currentNameEl)return;
    if(!selectedFolderId){
      currentNameEl.textContent='根目录';
      if(currentChipEl)currentChipEl.title='根目录';
      return;
    }
    const pathText=buildFolderPathLabel(folders,selectedFolderId);
    currentNameEl.textContent=pathText;
    if(currentChipEl)currentChipEl.title=pathText;
  };

  const render=()=>{
    if(!listEl)return;
    updateSelectedDisplay();

    // 筛选当前 viewParentId 下的直属文件夹
    const children=folders.filter(item=>(item.parentId||'')===viewParentId).sort((a,b)=>a.name.localeCompare(b.name,'zh-CN'));
    const returnControl=viewParentId?'<button type="button" class="cr-folder-return" data-folder-return>返回上一级</button>':'';

    if(!children.length){
      listEl.innerHTML=`${returnControl}<div class="cr-folder-empty"><p>没有子文件夹</p><span>可直接确认使用当前路径</span></div>`;
      listEl.querySelector<HTMLButtonElement>('[data-folder-return]')?.addEventListener('click',()=>{viewParentId=folders.find(item=>item.id===viewParentId)?.parentId||'';render();});
      return;
    }

    listEl.innerHTML=`${returnControl}${children.map(folder=>{
      const isSelected=selectedFolderId===folder.id;
      const subCount=folders.filter(sub=>(sub.parentId||'')===folder.id).length;
      return`<div class="cr-folder-row${isSelected?' is-selected':''}" data-folder-card="${escapeHtml(folder.id)}" role="button" tabindex="0">
        <div class="cr-folder-row-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
        </div>
        <div class="cr-folder-row-info">
          <span class="cr-folder-row-name">${escapeHtml(folder.name)}</span>
          <span class="cr-folder-row-sub">${subCount>0?`包含 ${subCount} 个子文件夹`:'合规管理制度目录'}</span>
        </div>
        ${subCount>0?`<button type="button" class="cr-folder-row-enter" data-folder-enter="${escapeHtml(folder.id)}" aria-label="进入 ${escapeHtml(folder.name)}">
          <span>进入</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>`:''}
        <div class="cr-folder-row-radio${isSelected?' is-checked':''}" aria-hidden="true">
          <span class="cr-radio-inner"></span>
        </div>
      </div>`;
    }).join('')}`;

    listEl.querySelector<HTMLButtonElement>('[data-folder-return]')?.addEventListener('click',()=>{viewParentId=folders.find(item=>item.id===viewParentId)?.parentId||'';render();});

    // 点击行选中
    listEl.querySelectorAll<HTMLElement>('[data-folder-card]').forEach(card=>{
      card.addEventListener('click',event=>{
        const target=event.target as HTMLElement|null;
        if(target?.closest('[data-folder-enter]'))return;
        selectedFolderId=card.dataset.folderCard??'';
        render();
      });
      // 双击直接进入
      card.addEventListener('dblclick',()=>{
        const id=card.dataset.folderCard??'';
        const hasChild=folders.some(item=>(item.parentId||'')===id);
        if(hasChild){
          viewParentId=id;
          selectedFolderId=id;
          render();
        }
      });
    });

    // 点击进入按钮
    listEl.querySelectorAll<HTMLElement>('[data-folder-enter]').forEach(btn=>{
      btn.addEventListener('click',event=>{
        event.preventDefault();
        event.stopPropagation();
        viewParentId=btn.dataset.folderEnter??'';
        selectedFolderId=viewParentId;
        render();
      });
    });
  };

  overlay.querySelectorAll<HTMLButtonElement>('[data-folder-modal-close]').forEach(btn=>btn.addEventListener('click',close));

  overlay.querySelector<HTMLButtonElement>('[data-folder-modal-confirm]')?.addEventListener('click',()=>{
    const finalFolderId=selectedFolderId||viewParentId||folders[0]?.id||'';
    if(!finalFolderId){
      window.alert('请选择一个有效的制度文件夹');
      return;
    }
    const folderPath=buildFolderPathLabel(folders,finalFolderId);
    const idInput=container.querySelector<HTMLInputElement>(`[data-compliance-folder-id="${scope}"]`);
    const pathInput=container.querySelector<HTMLInputElement>(`[data-compliance-folder-path="${scope}"]`);
    const label=container.querySelector<HTMLElement>(`[data-compliance-folder-display="${scope}"] .cr-compliance-folder-path-label`);
    const countEl=container.querySelector<HTMLElement>(`[data-compliance-file-count="${scope}"]`);
    if(idInput)idInput.value=finalFolderId;
    if(pathInput)pathInput.value=folderPath;
    if(label){
      label.textContent=folderPath;
      label.title=folderPath;
    }
    close();
    void loadComplianceLibrarySummary(scope,finalFolderId).then(summary=>{
      if(!countEl)return;
      const count=summary?.fileCount??0;
      countEl.textContent=count>0?`已收录 ${count} 份制度规范`:'尚未收录制度规范';
    });
  });

  render();
}
function syncComplianceLibraryPanel(container:HTMLElement):void{
  const complianceEnabled=Boolean(container.querySelector<HTMLInputElement>('input[data-settings-scope][value="compliance"]')?.checked);
  const panel=container.querySelector<HTMLElement>('[data-compliance-library]');
  if(panel){
    panel.hidden=!complianceEnabled;
    panel.classList.toggle('is-open',complianceEnabled);
  }
  const policyPanel=container.querySelector<HTMLElement>('[data-compliance-policy-selection]');
  if(policyPanel){
    policyPanel.hidden=!complianceEnabled;
    policyPanel.classList.toggle('is-open',complianceEnabled);
  }
}
/** 绑定项目设置中的合规制度增删与采纳推荐交互。 */
function bindCompliancePolicySelectionEvents(container:HTMLElement,complianceContext?:ComplianceContext):void{
  const panel=container.querySelector<HTMLElement>('[data-compliance-policy-selection]');
  if(!panel)return;
  const list=panel.querySelector<HTMLElement>('[data-compliance-policy-list]');
  const readRecommendations=():CompliancePolicyRecommendation[]=>{
    try{return JSON.parse(decodeURIComponent(panel.getAttribute('data-recommended-policies')??'%5B%5D')) as CompliancePolicyRecommendation[]}catch{return[]}
  };
  const mergePolicyIds=(nextIds:string[]):void=>{
    const merged=normalizeCompliancePolicyFileIds([...collectSelectedCompliancePolicyIds(container),...nextIds]);
    if(!list)return;
    const recommendations=readRecommendations();
    const catalog=readCompliancePolicyCatalog(panel);
    const profileStub:QuickProfile|null=recommendations.length?{fileId:'',sourceFileName:'',status:'ready',contractTitle:'',contractType:'',typeConfidence:0,parties:[],suggestedReviewPosition:'',evidence:[],warnings:[],detectedAt:'',recommendedCompliancePolicies:recommendations}:null;
    const known=compliancePolicyMetaMap(profileStub,merged,catalog);
    list.innerHTML=buildCompliancePolicyRows(merged,known);
  };
  void hydrateCompliancePolicyCatalog(container,complianceContext);
  list?.addEventListener('click',event=>{
    const target=event.target as HTMLElement;
    if(!target.closest('[data-remove-compliance-policy]'))return;
    target.closest('[data-compliance-policy-row]')?.remove();
    refreshCompliancePolicyList(container);
  });
  panel.querySelector<HTMLButtonElement>('[data-apply-recommended-policies]')?.addEventListener('click',()=>{
    const recommendations=readRecommendations();
    if(!recommendations.length)return;
    writeCompliancePolicyCatalog(panel,mergePolicyCatalog(readCompliancePolicyCatalog(panel),recommendations));
    mergePolicyIds(recommendations.map(item=>item.fileId));
  });
  panel.querySelector<HTMLButtonElement>('[data-add-compliance-policy]')?.addEventListener('click',()=>{
    void openCompliancePolicyPickerModal(container,complianceContext,ids=>mergePolicyIds(ids));
  });
}
function bindSettingsFormEvents(container:HTMLElement,complianceContext?:ComplianceContext):void{
  const complianceSummaries=complianceContext?{personal:complianceContext.personal,enterprise:complianceContext.enterprise}:undefined;
  const enterpriseOrgId=complianceContext?.enterpriseOrgId??'';
  const positionSelect=container.querySelector<HTMLSelectElement>('[data-settings-position-select]');
  const hiddenPosition=container.querySelector<HTMLInputElement>('[data-settings-position]');
  const draftPosition=container.querySelector<HTMLInputElement>('[data-settings-position-draft]');
  const confirmBtn=container.querySelector<HTMLButtonElement>('[data-settings-position-confirm]');
  const mode=container.getAttribute('data-settings-mode')??'project';
  const contractTypeSelect=container.querySelector<HTMLSelectElement>('[data-settings-contract-type]');
  const constructionNote=container.querySelector<HTMLElement>('[data-construction-review-note]');
  const saleReviewNote=container.querySelector<HTMLElement>('[data-sale-review-note]');
  const governanceReviewNote=container.querySelector<HTMLElement>('[data-governance-review-note]');
  const leaseReviewNote=container.querySelector<HTMLElement>('[data-lease-review-note]');
  const serviceReviewNote=container.querySelector<HTMLElement>('[data-service-review-note]');
  const mandateReviewNote=container.querySelector<HTMLElement>('[data-mandate-review-note]');
  const financeReviewNote=container.querySelector<HTMLElement>('[data-finance-review-note]');
  const logisticsReviewNote=container.querySelector<HTMLElement>('[data-logistics-review-note]');
  const specialLawReviewNote=container.querySelector<HTMLElement>('[data-special-law-review-note]');
  const otherMixedReviewNote=container.querySelector<HTMLElement>('[data-other-mixed-review-note]');
  const nonContractReviewNote=container.querySelector<HTMLElement>('[data-non-contract-review-note]');
  positionSelect?.addEventListener('change',()=>{
    if(mode==='task'&&positionSelect.value&&hiddenPosition)hiddenPosition.value=positionSelect.value;
    syncSettingsPositionUi(container);
  });
  const syncSchemeSpecialtyNotes=()=>{
    if(!contractTypeSelect)return;
    const value=contractTypeSelect.value;
    if(constructionNote)constructionNote.hidden=!isConstructionContractTypeLabel(value);
    if(saleReviewNote)saleReviewNote.hidden=!isSaleContractSeriesLabel(value);
    if(governanceReviewNote)governanceReviewNote.hidden=!isEquityGovernanceSeriesLabel(value);
    if(leaseReviewNote)leaseReviewNote.hidden=!isLeaseContractSeriesLabel(value);
    if(serviceReviewNote)serviceReviewNote.hidden=!isServiceContractSeriesLabel(value);
    if(mandateReviewNote)mandateReviewNote.hidden=!isMandateContractSeriesLabel(value);
    if(financeReviewNote)financeReviewNote.hidden=!isFinanceContractSeriesLabel(value);
    if(logisticsReviewNote)logisticsReviewNote.hidden=!isLogisticsContractSeriesLabel(value);
    if(specialLawReviewNote)specialLawReviewNote.hidden=!isSpecialLawContractSeriesLabel(value);
    if(otherMixedReviewNote)otherMixedReviewNote.hidden=!isOtherMixedContractSeriesLabel(value);
    if(nonContractReviewNote)nonContractReviewNote.hidden=!isNonContractDocumentSeriesLabel(value);
  };
  contractTypeSelect?.addEventListener('change',syncSchemeSpecialtyNotes);
  syncSchemeSpecialtyNotes();
  confirmBtn?.addEventListener('click',()=>{
    const text=draftPosition?.value.trim()??'';
    if(!text||!hiddenPosition)return;
    hiddenPosition.value=text;
    if(draftPosition)draftPosition.value=text;
    const customSub=container.querySelector<HTMLElement>(`input[data-settings-position-radio][value="${TASK_CUSTOM_POSITION_VALUE}"]`)
      ?.closest('.cr-scheme-position-card,.cr-settings-position-option')
      ?.querySelector('.cr-scheme-entity-sub, small');
    if(customSub){
      customSub.textContent=text;
      customSub.setAttribute('title',text);
    }
    confirmBtn.textContent='已确认 ✓';
    confirmBtn.classList.add('is-confirmed');
    window.setTimeout(()=>{confirmBtn.textContent='确认';confirmBtn.classList.remove('is-confirmed')},1500);
    syncSettingsPositionUi(container);
  });
  draftPosition?.addEventListener('keydown',event=>{
    if(event.key==='Enter'){
      event.preventDefault();
      confirmBtn?.click();
    }
  });
  container.querySelectorAll<HTMLInputElement>('input[data-settings-depth]').forEach(input=>{
    input.addEventListener('change',()=>{
      container.querySelectorAll('.cr-settings-segment').forEach(item=>item.classList.toggle('is-active',item.querySelector('input')===input&&input.checked));
    });
  });
  container.querySelectorAll<HTMLInputElement>('input[data-settings-scope]').forEach(input=>{
    input.addEventListener('change',()=>{
      const val=input.value;
      const checked=input.checked;
      container.querySelectorAll<HTMLInputElement>(`input[data-settings-scope][value="${val}"]`).forEach(sibling=>{
        sibling.checked=checked;
      });
      syncComplianceLibraryPanel(container);
      container.querySelectorAll<HTMLElement>(`[data-scope-item="${val}"]`).forEach(item=>{
        item.classList.toggle('is-checked',checked);
      });
      input.closest('.cr-scheme-scope-item')?.classList.toggle('is-checked',checked);
    });
  });
  const ownershipToggle=container.querySelector<HTMLInputElement>('input[data-settings-enterprise-ownership]');
  ownershipToggle?.addEventListener('change',async()=>{
    const card=ownershipToggle.closest<HTMLElement>('.cr-scheme-enhanced-card');
    if(!ownershipToggle.checked){card?.classList.remove('is-checked');return}
    const ready=await isQichachaOwnershipReady();
    if(!ready){
      ownershipToggle.checked=false;
      card?.classList.remove('is-checked');
      window.alert('开启企业性质与关联关系识别前，请联系管理员在后台「AI 服务 · 工商信息」配置企查查 AppKey/SecretKey。');
      return;
    }
    card?.classList.add('is-checked');
  });
  container.querySelectorAll<HTMLInputElement>('input[data-settings-position-radio]').forEach(input=>input.addEventListener('change',()=>{
    if(!input.checked||!hiddenPosition)return;
    if(input.value===TASK_CUSTOM_POSITION_VALUE){
      if(!hiddenPosition.value||hiddenPosition.value===TASK_CUSTOM_POSITION_VALUE)hiddenPosition.value='';
    }else{
      hiddenPosition.value=input.value;
    }
    container.querySelectorAll('.cr-settings-position-option,.cr-scheme-position-card').forEach(option=>{
      option.classList.toggle('is-active',option.querySelector('input')===input);
    });
    syncSettingsPositionUi(container);
  }));
  container.querySelectorAll<HTMLInputElement>('input[name="complianceLibraryScope"]').forEach(input=>input.addEventListener('change',()=>{
    container.querySelectorAll('.cr-compliance-library-option').forEach(option=>{
      option.classList.toggle('is-active',option.querySelector('input')===input);
    });
  }));
  const openCompliance=()=>{
    const scope=container.querySelector<HTMLInputElement>('input[name="complianceLibraryScope"]:checked')?.value==='enterprise'?'enterprise':'personal';
    const summary=scope==='enterprise'?complianceSummaries?.enterprise:complianceSummaries?.personal;
    const folderId=resolveComplianceFolderId(container,scope,summary??null);
    if(folderId)navigateToComplianceLibrary(scope,folderId);
    else navigate('/tax-assistant/space');
  };
  container.querySelector<HTMLButtonElement>('[data-open-compliance-library]')?.addEventListener('click',openCompliance);
  container.querySelector<HTMLButtonElement>('[data-upload-compliance-library]')?.addEventListener('click',openCompliance);
  container.querySelectorAll<HTMLButtonElement>('[data-pick-compliance-folder]').forEach(button=>button.addEventListener('click',()=>{
    const scope=button.dataset.pickComplianceFolder==='enterprise'?'enterprise':'personal';
    void openComplianceFolderPicker(container,scope,enterpriseOrgId);
  }));
  syncComplianceLibraryPanel(container);
  bindCompliancePolicySelectionEvents(container,complianceContext);
  bindScopeColorPickers(container);
  if(hiddenPosition)syncSettingsPositionUi(container);
}

/**
 * 任务默认审核方案页面。
 * 遵循 Apple HIG / Google Material 3 现代标准与秋波蓝品牌视觉规范，
 * 采用单列居中卡片流，去除冗杂边栏与冗长口水话，提供清晰鲜明的层级对齐与高对比度文字。
 *
 * @param taskName - 任务名称
 * @param taskId - 任务 ID
 * @param value - 任务级审核偏好配置
 * @param stats - 任务方案关联的项目统计
 * @param companyName - 所属企业名称
 * @param userDisplayName - 当前用户名称
 * @param complianceContext - 空间制度库上下文信息
 * @returns 页面 HTML 结构
 */
function taskSchemeSettingsPage(taskName:string,taskId:string,value:Preferences,_stats:TaskSchemeStats,_companyName:string,userDisplayName:string,complianceContext:ComplianceContext):string{
  const typeOptions=buildContractTypeSelectOptions(value.contractType,'',true);
  const positionResult=buildTaskReviewPositionOptions(userDisplayName,value.reviewPositionCompany);
  const positionOptions=positionResult.options;
  const positionSelected=positionResult.selected;
  const customDraft=positionResult.customDraft;

  const personalCount=complianceContext.personal?.fileCount??0;
  const enterpriseCount=complianceContext.enterprise?.fileCount??0;
  const scope=value.complianceLibraryScope==='enterprise'&&complianceContext.hasEnterprise?'enterprise':'personal';

  const positionCards=positionOptions.map(item=>{
    const isCustom=item.value===TASK_CUSTOM_POSITION_VALUE;
    const active=positionSelected===item.value;
    const iconSvg=isCustom
      ?`<svg class="cr-scheme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`
      :`<svg class="cr-scheme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
    const title=isCustom?'自定义':'本人';
    const subtitle=isCustom?(customDraft||'填写并确认立场主体名称'):item.value;
    return`<label class="cr-scheme-position-card cr-settings-position-option${active?' is-active':''}">
      <input type="radio" name="taskReviewPosition" data-settings-position-radio value="${escapeHtml(item.value)}" ${active?'checked':''}>
      <div class="cr-scheme-card-icon-box">${iconSvg}</div>
      <div class="cr-scheme-card-info">
        <strong class="cr-scheme-entity-name">${escapeHtml(title)}</strong>
        <small class="cr-scheme-entity-sub" title="${escapeHtml(subtitle)}">${escapeHtml(subtitle)}</small>
      </div>
      <div class="cr-scheme-check-circle" aria-hidden="true"></div>
    </label>`;
  }).join('');
  const customOpen=positionSelected===TASK_CUSTOM_POSITION_VALUE;
  const hiddenPositionValue=customOpen?customDraft:positionSelected;

  const scopeRows=([
    ['legal','法律风险','<path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1ZM2 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1ZM7 21h10M12 3v18M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/>'],
    ['business','商业风险','<rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>'],
    ['compliance','合规管理','<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>'],
  ] as const).map(([scopeKey,label,icon])=>{
    const enabled=value.scopes[scopeKey];
    return`<div class="cr-scheme-scope-item${enabled?' is-checked':''}" data-scope-item="${scopeKey}">
      <span class="cr-scheme-scope-icon-wrap" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icon}</svg></span>
      <strong class="cr-scheme-scope-name">${label}</strong>
      ${scopeAnnotationStyleControls(scopeKey,value.scopeAnnotationStyles[scopeKey])}
      <label class="cr-scheme-checkbox-square-wrap" title="启用${label}">
        <input type="checkbox" class="cr-scheme-scope-checkbox" data-settings-scope value="${scopeKey}" ${enabled?'checked':''} aria-label="启用${label}">
        <span class="cr-scheme-checkbox-square" aria-hidden="true"></span>
      </label>
    </div>`;
  }).join('');

  const activePositionLabel=positionSelected===TASK_CUSTOM_POSITION_VALUE?'自定义':'本人';
  const activePositionDetail=positionSelected===TASK_CUSTOM_POSITION_VALUE?(customDraft||'待确认立场主体'):positionSelected;
  const reviewDepthLabel=value.reviewDepth==='deep'?'深度审查':'标准审查';
  const ownershipLabel=value.enhancedFeatures.enterpriseOwnershipLookup?'已启用':'未启用';

  return`<div class="cr-task-scheme-layout">
    <div class="cr-task-scheme-container">
      <div class="cr-scheme-header">
        <div>
          <h2 class="cr-scheme-title">默认任务方案</h2>
          <p class="cr-scheme-subtitle">配置合同审查的默认方案，提升审查效率与一致性</p>
        </div>
        <div class="cr-scheme-header-btns">
          <span class="cr-scheme-save-state" aria-live="polite"><span class="cr-scheme-save-dot" aria-hidden="true"></span><span id="reviewMessage" class="cr-scheme-message-label">更改将在保存后生效</span></span>
        </div>
      </div>

      <nav class="cr-scheme-workspace-tabs" aria-label="默认方案设置分类">
        <button type="button" class="cr-scheme-workspace-tab" data-scheme-tab="basic"><img src="/assets/icons/scheme/user.svg" alt="" aria-hidden="true"><span>基本信息</span></button>
        <button type="button" class="cr-scheme-workspace-tab is-active" data-scheme-tab="review"><img src="/assets/icons/scheme/scale.svg" alt="" aria-hidden="true"><span>审核规则</span></button>
        <button type="button" class="cr-scheme-workspace-tab" data-scheme-tab="library"><img src="/assets/icons/scheme/file-description.svg" alt="" aria-hidden="true"><span>制度库</span></button>
        <button type="button" class="cr-scheme-workspace-tab" data-scheme-tab="enhanced"><img src="/assets/icons/scheme/wand.svg" alt="" aria-hidden="true"><span>增强功能</span></button>
      </nav>

      <div class="cr-settings-panel-body cr-scheme-columns cr-scheme-workspace" data-settings-mode="task">
        <main class="cr-scheme-workspace-main">
        <section class="cr-panel cr-task-scheme-panel cr-scheme-basic-panel cr-scheme-workspace-panel" data-scheme-panel="basic" hidden>
          <div class="cr-scheme-section cr-scheme-section-hero">
            <h3 class="cr-scheme-section-title">基本信息</h3>
            <div class="cr-scheme-field-stack">
              <span class="cr-scheme-rule-label">默认审核立场</span>
              <div class="cr-scheme-positions" role="radiogroup" aria-label="默认审核立场">${positionCards}</div>
              <div class="cr-scheme-custom-position cr-settings-custom${customOpen?' is-open':''}" data-settings-custom ${customOpen?'':'hidden'}>
                <input class="cr-input cr-settings-custom-input" data-settings-position-draft maxlength="200" value="${escapeHtml(customDraft)}" placeholder="输入立场主体名称并点击确认">
                <button type="button" class="cr-button cr-button-secondary cr-settings-custom-btn" data-settings-position-confirm>确认</button>
              </div>
              <input type="hidden" data-settings-position value="${escapeHtml(hiddenPositionValue)}">
            </div>
            <div class="cr-scheme-reviewer-field">
              <label class="cr-scheme-rule-label" for="cr-settings-reviewer-name">审阅人员</label>
              <input class="cr-input cr-scheme-reviewer-input" id="cr-settings-reviewer-name" data-settings-reviewer-name maxlength="80" value="${escapeHtml(value.reviewerName||userDisplayName)}" placeholder="您的姓名">
            </div>
          </div>
          <div class="cr-scheme-section cr-scheme-contract-section">
            <h3 class="cr-scheme-section-title">合同设置</h3>
            <div class="cr-scheme-field-stack">
              <label class="cr-scheme-rule-label" for="cr-settings-contract-type">合同类型</label>
              <div class="cr-scheme-select-wrapper">
                <select class="cr-select cr-scheme-select-control" id="cr-settings-contract-type" data-settings-contract-type>
                  ${typeOptions.map(item=>`<option ${value.contractType===item?'selected':''}>${escapeHtml(item)}</option>`).join('')}
                </select>
              </div>
              <div class="cr-scheme-specialty-note" data-construction-review-note ${isConstructionContractTypeLabel(value.contractType)?'':'hidden'}><strong>建设工程专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-sale-review-note ${isSaleContractSeriesLabel(value.contractType)?'':'hidden'}><strong>买卖合同系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-governance-review-note ${isEquityGovernanceSeriesLabel(value.contractType)?'':'hidden'}><strong>股权交易与公司治理系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-lease-review-note ${isLeaseContractSeriesLabel(value.contractType)?'':'hidden'}><strong>租赁与融资租赁系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-service-review-note ${isServiceContractSeriesLabel(value.contractType)?'':'hidden'}><strong>承揽、技术与服务合同系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-mandate-review-note ${isMandateContractSeriesLabel(value.contractType)?'':'hidden'}><strong>委托及经营管理合同系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-finance-review-note ${isFinanceContractSeriesLabel(value.contractType)?'':'hidden'}><strong>融资与担保合同系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-logistics-review-note ${isLogisticsContractSeriesLabel(value.contractType)?'':'hidden'}><strong>运输、保管与仓储合同系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-special-law-review-note ${isSpecialLawContractSeriesLabel(value.contractType)?'':'hidden'}><strong>特别法合同系列专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-other-mixed-review-note ${isOtherMixedContractSeriesLabel(value.contractType)?'':'hidden'}><strong>其他或混合合同专项机制已启用</strong></div>
              <div class="cr-scheme-specialty-note" data-non-contract-review-note ${isNonContractDocumentSeriesLabel(value.contractType)?'':'hidden'}><strong>非合同文件：先识别大类和次级，按读者与目的给出体例意见；法律法规只守底线（不套用合同专项规则）</strong></div>
            </div>
          </div>
        </section>

        <section class="cr-panel cr-task-scheme-panel cr-scheme-review-panel cr-scheme-workspace-panel is-active" data-scheme-panel="review">
          <div class="cr-scheme-section">
            <h3 class="cr-scheme-section-title">审核维度与深度</h3>
            <div class="cr-scheme-rules-grid">
              <div class="cr-scheme-rule-block">
                <span class="cr-scheme-rule-label">排查范围</span>
                <div class="cr-scheme-scope-cards">${scopeRows}</div>
              </div>
              <div class="cr-scheme-rule-block cr-scheme-depth-block">
                <span class="cr-scheme-rule-label">审查深度</span>
                <div class="cr-scheme-depth-switch">
                  <div class="cr-settings-segmented cr-scheme-segmented-control" role="radiogroup" aria-label="审查深度">
                    <label class="cr-settings-segment${value.reviewDepth==='standard'?' is-active':''}"><input type="radio" name="settingsReviewDepth" data-settings-depth value="standard" ${value.reviewDepth==='standard'?'checked':''}><span>标准审查</span></label>
                    <label class="cr-settings-segment${value.reviewDepth==='deep'?' is-active':''}"><input type="radio" name="settingsReviewDepth" data-settings-depth value="deep" ${value.reviewDepth==='deep'?'checked':''}><span>深度审查</span></label>
                  </div>
                </div>
              </div>
              ${taskSchemeAnnotateDefaultsMarkup(value)}
            </div>
          </div>

        </section>

        <section class="cr-panel cr-task-scheme-panel cr-scheme-review-panel cr-scheme-workspace-panel" data-scheme-panel="library" hidden>
          <div class="cr-scheme-section cr-scheme-compliance-section">
            <div class="cr-compliance-library cr-scheme-compliance-box${value.scopes.compliance?' is-open':''}" data-compliance-library ${value.scopes.compliance?'':'hidden'}>
              <div class="cr-scheme-compliance-head">
                <h4 class="cr-scheme-compliance-title">合规管理制度库</h4>
                <div class="cr-compliance-library-actions">
                  <button type="button" class="cr-button cr-button-secondary cr-scheme-btn-sm" data-open-compliance-library>打开制度文件夹</button>
                  <button type="button" class="cr-button cr-button-quiet cr-scheme-btn-sm" data-upload-compliance-library>上传制度文件</button>
                </div>
              </div>
              <div class="cr-compliance-library-options cr-scheme-compliance-options">
                <label class="cr-compliance-library-option cr-scheme-compliance-opt${scope==='personal'?' is-active':''}">
                  <input type="radio" name="complianceLibraryScope" value="personal" ${scope==='personal'?'checked':''} data-compliance-scope>
                  <div class="cr-scheme-comp-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div class="cr-scheme-comp-details">
                    <b>个人空间制度库</b>
                    <small data-compliance-file-count="personal">${personalCount?`已收录 ${personalCount} 份制度规范`:'尚未设置路径'}</small>
                    ${complianceFolderPathHtml('personal',value.complianceLibraryFolders.personal,complianceContext.personal)}
                  </div>
                  <div class="cr-scheme-radio-ring" aria-hidden="true"></div>
                </label>
                ${complianceContext.hasEnterprise?`
                <label class="cr-compliance-library-option cr-scheme-compliance-opt${scope==='enterprise'?' is-active':''}">
                  <input type="radio" name="complianceLibraryScope" value="enterprise" ${scope==='enterprise'?'checked':''} data-compliance-scope>
                  <div class="cr-scheme-comp-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1"/></svg>
                  </div>
                  <div class="cr-scheme-comp-details">
                    <b>团队空间制度库</b>
                    <small data-compliance-file-count="enterprise">${enterpriseCount?`已收录 ${enterpriseCount} 份制度规范`:'尚未设置路径'}</small>
                    ${complianceFolderPathHtml('enterprise',value.complianceLibraryFolders.enterprise,complianceContext.enterprise)}
                  </div>
                  <div class="cr-scheme-radio-ring" aria-hidden="true"></div>
                </label>`:''}
              </div>
            </div>
            <div class="cr-scheme-library-disabled" data-compliance-library-disabled ${value.scopes.compliance?'hidden':''}>
              <strong>合规管理当前未启用</strong>
              <span>请先在“审核规则”中启用合规管理，再选择本次审查使用的制度库。</span>
              <button type="button" class="cr-button cr-button-secondary cr-scheme-btn-sm" data-scheme-open-tab="review">前往审核规则</button>
            </div>
          </div>
        </section>

        <section class="cr-panel cr-task-scheme-panel cr-scheme-workspace-panel" data-scheme-panel="enhanced" hidden>
          <div class="cr-scheme-section cr-scheme-enhanced-section">
            <h3 class="cr-scheme-section-title">增强功能</h3>
            <label class="cr-scheme-enhanced-card${value.enhancedFeatures.enterpriseOwnershipLookup?' is-checked':''}">
              <span class="cr-scheme-enhanced-icon" aria-hidden="true"><img src="/assets/icons/scheme/wand.svg" alt=""></span>
              <span class="cr-scheme-enhanced-copy"><strong>企业性质与关联关系识别</strong><small>预览时识别合同当事人的企业性质与关联关系</small></span>
              <input type="checkbox" data-settings-enterprise-ownership ${value.enhancedFeatures.enterpriseOwnershipLookup?'checked':''} aria-label="启用企业性质与关联关系识别">
              <span class="cr-scheme-switch" aria-hidden="true"></span>
            </label>
          </div>
        </section>
        </main>

        <aside class="cr-scheme-summary" aria-label="方案摘要">
          <div class="cr-scheme-summary-head"><h3>方案摘要</h3></div>
          <div class="cr-scheme-summary-list">
            <section class="cr-scheme-summary-item">
              <div class="cr-scheme-summary-label"><img src="/assets/icons/scheme/user.svg" alt=""><strong>默认审核立场</strong><button type="button" data-scheme-edit="basic"><img src="/assets/icons/scheme/edit.svg" alt="">编辑</button></div>
              <div class="cr-scheme-summary-value"><b data-summary-position-label>${escapeHtml(activePositionLabel)}</b><span data-summary-position-detail>${escapeHtml(activePositionDetail)}</span></div>
            </section>
            <section class="cr-scheme-summary-item">
              <div class="cr-scheme-summary-label"><img src="/assets/icons/scheme/user-search.svg" alt=""><strong>审阅人员</strong><button type="button" data-scheme-edit="basic"><img src="/assets/icons/scheme/edit.svg" alt="">编辑</button></div>
              <div class="cr-scheme-summary-value"><b data-summary-reviewer>${escapeHtml(value.reviewerName||userDisplayName)}</b></div>
            </section>
            <section class="cr-scheme-summary-item">
              <div class="cr-scheme-summary-label"><img src="/assets/icons/scheme/file-description.svg" alt=""><strong>合同类型</strong><button type="button" data-scheme-edit="basic"><img src="/assets/icons/scheme/edit.svg" alt="">编辑</button></div>
              <div class="cr-scheme-summary-value"><b data-summary-contract>${escapeHtml(value.contractType)}</b></div>
            </section>
            <section class="cr-scheme-summary-item">
              <div class="cr-scheme-summary-label"><img src="/assets/icons/scheme/shield-check.svg" alt=""><strong>审核深度</strong><button type="button" data-scheme-edit="review"><img src="/assets/icons/scheme/edit.svg" alt="">编辑</button></div>
              <div class="cr-scheme-summary-value"><b data-summary-depth>${reviewDepthLabel}</b></div>
            </section>
            <section class="cr-scheme-summary-item">
              <div class="cr-scheme-summary-label"><img src="/assets/icons/scheme/wand.svg" alt=""><strong>增强功能</strong><button type="button" data-scheme-edit="enhanced"><img src="/assets/icons/scheme/edit.svg" alt="">编辑</button></div>
              <div class="cr-scheme-summary-value cr-scheme-summary-enhanced"><b>企业性质与关联关系识别</b><span data-summary-ownership class="${value.enhancedFeatures.enterpriseOwnershipLookup?'is-enabled':''}">${ownershipLabel}</span></div>
            </section>
          </div>
          <button type="button" class="cr-button cr-button-primary cr-scheme-primary-btn" id="saveTaskSchemeSettings">保存默认方案</button>
        </aside>
      </div>
    </div>
  </div>`;
}
function bindTaskSchemeSettings(root:HTMLElement,taskId:string,companyName:string,userDisplayName:string,complianceContext:ComplianceContext):void{
  const container=root.querySelector<HTMLElement>('.cr-scheme-columns.cr-settings-panel-body');
  if(container)bindSettingsFormEvents(container,complianceContext);
  const activateSchemeTab=(tabName:string)=>{
    root.querySelectorAll<HTMLElement>('[data-scheme-tab]').forEach(tab=>{
      const active=tab.dataset.schemeTab===tabName;
      tab.classList.toggle('is-active',active);
      tab.setAttribute('aria-selected',String(active));
    });
    root.querySelectorAll<HTMLElement>('[data-scheme-panel]').forEach(panel=>{
      // 审核规则是默认方案的主工作区：在这里同时展示所选制度库，
      // 让用户无需在两个孤立页面之间来回跳转；“制度库”标签仍保留，
      // 便于专注处理制度文件。
      const active=panel.dataset.schemePanel===tabName
        ||(tabName==='review'&&panel.dataset.schemePanel==='library');
      panel.hidden=!active;
      panel.classList.toggle('is-active',active);
    });
  };
  const refreshSchemeSummary=()=>{
    if(!container)return;
    const radio=container.querySelector<HTMLInputElement>('input[data-settings-position-radio]:checked');
    const isCustom=radio?.value===TASK_CUSTOM_POSITION_VALUE;
    const positionDetail=isCustom
      ?(container.querySelector<HTMLInputElement>('[data-settings-position-draft]')?.value.trim()||'待确认立场主体')
      :(radio?.value||'未选择');
    const setText=(selector:string,text:string)=>{const node=root.querySelector<HTMLElement>(selector);if(node)node.textContent=text};
    setText('[data-summary-position-label]',isCustom?'自定义':'本人');
    setText('[data-summary-position-detail]',positionDetail);
    setText('[data-summary-reviewer]',container.querySelector<HTMLInputElement>('[data-settings-reviewer-name]')?.value.trim()||'未填写');
    setText('[data-summary-contract]',container.querySelector<HTMLSelectElement>('[data-settings-contract-type]')?.value||'未选择');
    setText('[data-summary-depth]',container.querySelector<HTMLInputElement>('input[data-settings-depth]:checked')?.value==='deep'?'深度审查':'标准审查');
    const ownership=container.querySelector<HTMLInputElement>('[data-settings-enterprise-ownership]')?.checked===true;
    const ownershipNode=root.querySelector<HTMLElement>('[data-summary-ownership]');
    if(ownershipNode){ownershipNode.textContent=ownership?'已启用':'未启用';ownershipNode.classList.toggle('is-enabled',ownership)}
    const complianceEnabled=container.querySelector<HTMLInputElement>('input[data-settings-scope][value="compliance"]')?.checked===true;
    const disabledNotice=root.querySelector<HTMLElement>('[data-compliance-library-disabled]');
    if(disabledNotice)disabledNotice.hidden=complianceEnabled;
  };
  root.querySelectorAll<HTMLButtonElement>('[data-scheme-tab]').forEach(tab=>tab.addEventListener('click',()=>activateSchemeTab(tab.dataset.schemeTab||'review')));
  root.querySelectorAll<HTMLButtonElement>('[data-scheme-edit]').forEach(button=>button.addEventListener('click',()=>activateSchemeTab(button.dataset.schemeEdit||'basic')));
  root.querySelectorAll<HTMLButtonElement>('[data-scheme-open-tab]').forEach(button=>button.addEventListener('click',()=>activateSchemeTab(button.dataset.schemeOpenTab||'review')));
  container?.addEventListener('input',refreshSchemeSummary);
  container?.addEventListener('change',()=>window.setTimeout(refreshSchemeSummary,0));
  refreshSchemeSummary();
  const save=async()=>{
    const message=root.querySelector<HTMLElement>('#reviewMessage');
    const fallback=defaultPreferences(companyName,userDisplayName,complianceContext.hasEnterprise);
    if(!container){if(message){message.hidden=false;message.style.color='#b91c1c';message.textContent='表单未就绪'}return}
    const radio=container.querySelector<HTMLInputElement>('input[data-settings-position-radio]:checked');
    if(!radio){if(message){message.hidden=false;message.style.color='#b91c1c';message.textContent='请选择默认审核立场'}return}
    if(radio.value===TASK_CUSTOM_POSITION_VALUE){
      const confirmed=container.querySelector<HTMLInputElement>('[data-settings-position]')?.value.trim();
      const draft=container.querySelector<HTMLInputElement>('[data-settings-position-draft]')?.value.trim();
      if(!confirmed&&!draft){if(message){message.hidden=false;message.style.color='#b91c1c';message.textContent='请填写并确认自定义立场名称'}return}
      if(draft&&confirmed!==draft){
        const hidden=container.querySelector<HTMLInputElement>('[data-settings-position]');
        if(hidden)hidden.value=draft;
      }
    }
    const next=collectSettingsPreferences(container,fallback);
    if(next.reviewPositionCompany===TASK_CUSTOM_POSITION_VALUE||!next.reviewPositionCompany.trim()){
      if(message){message.hidden=false;message.style.color='#b91c1c';message.textContent='请填写并确认自定义立场名称'}return
    }
    if(!Object.values(next.scopes).some(Boolean)){if(message){message.hidden=false;message.style.color='#b91c1c';message.textContent='请至少选择一个审核范围'}return}
    const syncInherited=Boolean(root.querySelector<HTMLInputElement>('#syncInheritedProjects')?.checked);
    if(message){message.hidden=false;message.style.color='#377180';message.textContent='正在保存…'}
    try{
      const payload=await saveTaskSchemeToCloud(taskId,next,syncInherited);
      if(message){
        message.hidden=false;
        message.style.color='#167245';
        message.textContent=payload.syncSummary.clearedProjectCount>0
          ?`已保存，并同步 ${payload.syncSummary.clearedProjectCount} 个继承项目`
          :'已保存';
      }
      refreshSchemeSummary();
    }catch(error){
      if(message){
        message.hidden=false;
        message.style.color='#b91c1c';
        message.textContent=error instanceof Error?error.message:'保存失败';
      }
    }
  };
  root.querySelector('#saveTaskSchemeSettings')?.addEventListener('click',()=>void save());
}
async function saveProjectSettings(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot,container:HTMLElement,messageEl:HTMLElement|null):Promise<boolean>{
  const fallback=loadPreferences(taskId,projectId,companyName);
  const positionSelect=container.querySelector<HTMLSelectElement>('[data-settings-position-select]');
  if(positionSelect?.value==='__custom__'){
    const confirmed=container.querySelector<HTMLInputElement>('[data-settings-position]')?.value.trim();
    const draft=container.querySelector<HTMLInputElement>('[data-settings-position-draft]')?.value.trim();
    if(!confirmed&&!draft){if(messageEl){messageEl.hidden=false;messageEl.textContent='请填写并确认审核立场'}return false}
  }else if(!positionSelect?.value){if(messageEl){messageEl.hidden=false;messageEl.textContent='请选择审核立场'}return false}
  const next=collectSettingsPreferences(container,fallback);
  if(!Object.values(next.scopes).some(Boolean)){if(messageEl){messageEl.hidden=false;messageEl.textContent='请至少选择一个审核范围'}return false}
  const compliancePolicyError=validateCompliancePolicySelection(next);
  if(compliancePolicyError){if(messageEl){messageEl.hidden=false;messageEl.textContent=compliancePolicyError}return false}
  if(messageEl){messageEl.hidden=false;messageEl.textContent='正在保存…'}
  try{
    await savePreferencesToCloud(taskId,projectId,next);
  }catch(error){
    if(messageEl){messageEl.hidden=false;messageEl.textContent=error instanceof Error?error.message:'设置保存失败'}
    return false;
  }
  if(messageEl){messageEl.hidden=false;messageEl.textContent='设置已保存'}
  const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
  settingsExpanded.delete(projectId);
  saveSettingsExpandedProjectIds(taskId,settingsExpanded);
  await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
  updateBatchToolbar(root,taskId);
  return true;
}
async function restoreProjectSettings(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot,_container:HTMLElement,messageEl:HTMLElement|null):Promise<boolean>{
  if(!window.confirm('确定恢复为任务默认方案吗？本项目已保存的专属设置将被清除。'))return false;
  if(messageEl){messageEl.hidden=false;messageEl.textContent='正在恢复任务默认方案…'}
  try{
    await restorePreferencesToCloud(taskId,projectId);
    await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
    const message=root.querySelector<HTMLElement>(`[data-settings-message="${projectId}"]`);
    if(message){message.hidden=false;message.textContent='已恢复为任务默认方案'}
    updateBatchToolbar(root,taskId);
    return true;
  }catch(error){
    if(messageEl){messageEl.hidden=false;messageEl.textContent=error instanceof Error?error.message:'恢复失败'}
    return false;
  }
}
function selectedProjectIds(root:HTMLElement):string[]{return[...root.querySelectorAll<HTMLInputElement>('.cr-project-select:checked')].map(input=>input.dataset.projectSelect??'').filter(Boolean)}
function selectedRowsHaveArchiveReady(root:HTMLElement,ids:string[]):boolean{
  return ids.some(projectId=>root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-row-status.is-done`));
}
function rowHasMainFile(root:HTMLElement,projectId:string):boolean{
  const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  return Boolean(row?.querySelector('.cr-table-filename'));
}
function rowIsReviewCompleted(root:HTMLElement,projectId:string):boolean{
  return Boolean(root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-row-status.is-done`));
}
function isRowBatchReviewReady(root:HTMLElement,taskId:string,projectId:string):boolean{
  if(rowIsReviewCompleted(root,projectId))return false;
  return rowHasMainFile(root,projectId)&&hasSavedPreferences(taskId,projectId);
}
/** 返回阻止批量审核的原因；可开始时返回 null。 */
function batchReviewBlockedReason(root:HTMLElement,taskId:string,ids:string[]):string|null{
  if(!ids.length)return'请先勾选要审核的项目。';
  const missingMain=ids.filter(projectId=>!rowHasMainFile(root,projectId));
  if(missingMain.length)return`有 ${missingMain.length} 个项目尚未上传合同正文，请先上传后再开始审核。`;
  const missingSettings=ids.filter(projectId=>!hasSavedPreferences(taskId,projectId));
  if(missingSettings.length)return`有 ${missingSettings.length} 个项目尚未保存审核设置，请点击行内「设置」，填写后点击「保存设置」。`;
  const completed=ids.filter(projectId=>rowIsReviewCompleted(root,projectId));
  if(completed.length)return`有 ${completed.length} 个项目已完成审核，请取消勾选后再开始批量审核。`;
  return null;
}
function updateBatchToolbar(root:HTMLElement,taskId:string):void{
  const ids=selectedProjectIds(root);
  const archiveBtn=root.querySelector<HTMLButtonElement>('#batchArchiveProjects');
  const previewBtn=root.querySelector<HTMLButtonElement>('#batchPreviewProjects');
  const deleteBtn=root.querySelector<HTMLButtonElement>('#batchDeleteProjects');
  const settingsBtn=root.querySelector<HTMLButtonElement>('#batchApplySettings');
  const reviewBtn=root.querySelector<HTMLButtonElement>('#batchStartReview');
  const selectAll=root.querySelector<HTMLInputElement>('#crProjectSelectAll');
  const rowCount=root.querySelectorAll('.cr-project-table-row').length;
  if(selectAll)selectAll.checked=rowCount>0&&ids.length===rowCount;
  if(archiveBtn){
    archiveBtn.hidden=!ids.length||!isLegalFeatureEnabled('legal-archive-v1');
    archiveBtn.disabled=!isLegalFeatureEnabled('legal-archive-v1')||!selectedRowsHaveArchiveReady(root,ids);
  }
  if(previewBtn)previewBtn.hidden=!ids.length||!isLegalFeatureEnabled('legal-quick-inspect-v1');
  if(deleteBtn)deleteBtn.hidden=!ids.length;
  if(settingsBtn)settingsBtn.disabled=!ids.length;
  if(reviewBtn){
    const blocked=isLegalFeatureEnabled('legal-ai-review-v1')?batchReviewBlockedReason(root,taskId,ids):'智能审核已关闭';
    reviewBtn.hidden=!ids.length||!isLegalFeatureEnabled('legal-ai-review-v1');
    reviewBtn.disabled=!ids.length||Boolean(blocked);
    reviewBtn.title=blocked??'审核';
    reviewBtn.classList.toggle('is-blocked',Boolean(ids.length&&blocked));
    if(!reviewBtn.textContent?.includes('正在审核'))reviewBtn.textContent='审核';
  }
}
/** 进入所选项目的成果归档页。 */
async function openProjectArchive(taskId:string,projectId:string):Promise<void>{
  if(!isLegalFeatureEnabled('legal-archive-v1')){window.alert('成果归档已关闭');return;}
  const project=await loadProjectById(taskId,projectId);
  if(!project)return;
  const snapshot=await loadProjectSnapshot(taskId,project);
  if(!isProjectArchiveReady(snapshot)){
    window.alert('该项目尚未完成智能审核，暂不可归档。');
    return;
  }
  if(!window.confirm('归档后，完整项目会从工作台移入个人空间的“归档”文件夹，仍可随时恢复。确认归档吗？'))return;
  await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/archive`),{});
  localStorage.removeItem(activeProjectKey(taskId));
  rememberActiveProject(taskId,projectId);
  rememberOverviewFocus(taskId,projectId);
  navigate(pathFor('deliverables'));
}
async function refreshProjectRowDom(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot,statusOverride?:ReviewStatus):Promise<ProjectSnapshot|null>{
  const project=projectStubFromRow(root,projectId)??await loadProjectById(taskId,projectId);
  if(!project){
    root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`)?.remove();
    root.querySelector(`.cr-project-expand-row[data-project-id="${projectId}"]`)?.remove();
    updateBatchToolbar(root,taskId);
    return null;
  }
  const snapshot=statusOverride
    ?(()=>{
      const files=statusOverride.files??{mainFiles:[],attachmentFiles:[],annotatedFiles:[]};
      const main=selectedMain(taskId,project.id,files.mainFiles);
      return{
        project,
        files,
        review:statusOverride.review,
        risks:statusOverride.risks??[],
        main,
        quickProfile:statusOverride.quickProfiles?.find(item=>item.fileId===main?.id)??null,
        protectedIds:new Set(statusOverride.protectedFileIds??[]),
        preferences:statusOverride.preferences??null,
        preferencesConfigured:Boolean(statusOverride.preferencesConfigured||statusOverride.preferences?.savedAt),
        taskScheme:statusOverride.taskScheme??taskSchemeCache.get(taskId)??null,
        projectPreferences:statusOverride.projectPreferences??null,
      } satisfies ProjectSnapshot;
    })()
    :await loadProjectSnapshot(taskId,project,companyName);
  if(statusOverride?.preferences)rememberProjectPreferences(taskId,project.id,statusOverride.preferences);
  if(statusOverride?.taskScheme)rememberTaskScheme(taskId,statusOverride.taskScheme);
  syncProjectNameFromMainFile(taskId,snapshot);
  const tbody=root.querySelector('#crProjectTableBody');
  const rows=[...tbody?.querySelectorAll('.cr-project-table-row')??[]];
  const index=rows.findIndex(row=>row.getAttribute('data-project-id')===projectId);
  const attachmentExpanded=loadExpandedProjectIds(taskId).has(projectId);
  const previewExpanded=loadPreviewExpandedProjectIds(taskId).has(projectId);
  const settingsExpanded=loadSettingsExpandedProjectIds(taskId).has(projectId);
  const focusProjectId=overviewFocusProjectId(taskId);
  const html=projectTableRows(taskId,snapshot,index>=0?index:rows.length,attachmentExpanded,previewExpanded,settingsExpanded,projectId===focusProjectId,selectedProjectIds(root).includes(projectId),companyName,auth);
  const holder=document.createElement('tbody');
  holder.innerHTML=html;
  const mainRow=holder.querySelector('.cr-project-table-row');
  const expandRow=holder.querySelector('.cr-project-expand-row');
  const existingMain=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  const existingExpand=root.querySelector(`.cr-project-expand-row[data-project-id="${projectId}"]`);
  if(existingMain&&mainRow)existingMain.replaceWith(mainRow);
  if(existingExpand&&expandRow)existingExpand.replaceWith(expandRow);
  bindOverviewRow(root,auth,taskId,companyName,projectId);
  if(previewExpanded)void loadProjectPreviewContent(root,taskId,projectId,companyName,auth);
  updateBatchToolbar(root,taskId);
  if(isWorkbenchProjectReviewing(taskId,projectId)&&snapshot.review?.status!=='in_progress'){
    const shown=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-row-status`)?.textContent??'';
    const shownPercent=Number(/^审核中\s+(\d+)/u.exec(shown)?.[1]??'');
    if(!Number.isFinite(shownPercent)||shownPercent<=5)setProjectRowReviewingUi(root,projectId,5,'正在启动审核');
  }
  return snapshot;
}
function startWorkbenchReviewPolling(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot,previousReviewId=''):void{
  const key=workbenchReviewPollerKey(taskId,projectId);
  stopWorkbenchReviewPoller(taskId,projectId);
  let seenInProgress=false;
  let lastPercent=5;
  let lastPhase='正在启动审核';
  const tick=async()=>{
    if(workbenchReviewTickLocks.has(key))return;
    workbenchReviewTickLocks.add(key);
    try{
      const status=await api.get<ReviewStatus>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/status?poll=${Date.now()}`),{timeoutMs:30000});
      const review=status.review;
      const isPreviousRun=Boolean(previousReviewId)&&review?.id===previousReviewId;
      if(review?.status==='in_progress'&&!isPreviousRun){
        seenInProgress=true;
        lastPercent=Math.max(lastPercent,Math.round(review.reviewProgress??lastPercent));
        lastPhase=review.reviewPhase||lastPhase;
        setProjectRowReviewingUi(root,projectId,lastPercent,lastPhase);
        return;
      }
      if(isWorkbenchProjectReviewing(taskId,projectId)&&(isPreviousRun||!seenInProgress||!review)){
        setProjectRowReviewingUi(root,projectId,seenInProgress?lastPercent:5,seenInProgress?lastPhase:'正在启动审核');
        return;
      }
      clearWorkbenchProjectReviewing(taskId,projectId);
      if(!review)return;
      applyFinishedReviewToRow(root,projectId,review,status.risks??[]);
      await refreshProjectRowDom(root,taskId,projectId,companyName,auth,status);
      stopWorkbenchReviewPoller(taskId,projectId);
    }catch{/** 页面暂时离线或状态写入中时保留轮询 */}
    finally{workbenchReviewTickLocks.delete(key)}
  };
  workbenchReviewPollers.set(key,window.setInterval(()=>{void tick()},2000));
  window.setTimeout(()=>{void tick()},400);
}
function syncProjectExpandDom(root:HTMLElement,taskId:string,projectId:string,snapshot:ProjectSnapshot|null,companyName:string,auth:AuthSnapshot):void{
  const mode=projectExpandMode(taskId,projectId,false,false,false);
  const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  const expandRow=root.querySelector<HTMLElement>(`.cr-project-expand-row[data-project-id="${projectId}"]`);
  const inner=expandRow?.querySelector<HTMLElement>('.cr-project-expand-inner');
  if(expandRow)expandRow.hidden=!mode;
  if(expandRow&&mode)expandRow.dataset.expandMode=mode;
  row?.classList.toggle('is-expanded',mode==='attachment');
  row?.classList.toggle('is-preview-expanded',mode==='preview');
  row?.classList.toggle('is-settings-expanded',mode==='settings');
  const attachBtn=row?.querySelector<HTMLButtonElement>('[data-project-expand]');
  const previewBtn=row?.querySelector<HTMLButtonElement>(`[data-project-preview="${projectId}"]`);
  const settingsBtn=row?.querySelector<HTMLButtonElement>(`[data-project-settings="${projectId}"]`);
  if(attachBtn){
    attachBtn.setAttribute('aria-expanded',mode==='attachment'?'true':'false');
    attachBtn.classList.toggle('is-active',mode==='attachment');
  }
  if(previewBtn){
    previewBtn.setAttribute('aria-expanded',mode==='preview'?'true':'false');
    previewBtn.classList.toggle('is-active',mode==='preview');
    const previewImg=previewBtn.querySelector('img');
    if(previewImg)previewImg.src=previewIcon(mode==='preview');
  }
  if(settingsBtn){
    settingsBtn.setAttribute('aria-expanded',mode==='settings'?'true':'false');
    settingsBtn.classList.toggle('is-active',mode==='settings');
    const settingsImg=settingsBtn.querySelector('img');
    if(settingsImg)settingsImg.src=settingsIcon(mode==='settings');
  }
  if(mode&&snapshot&&inner)inner.innerHTML=projectExpandInnerHtml(snapshot,mode,taskId,companyName,auth);
}
async function closeProjectPreviewDom(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot):Promise<void>{
  const previewExpanded=loadPreviewExpandedProjectIds(taskId);
  previewExpanded.delete(projectId);
  savePreviewExpandedProjectIds(taskId,previewExpanded);
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
  bindExpandRowEvents(root,auth,taskId,companyName,projectId);
}
async function toggleExpandDom(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot):Promise<void>{
  const expanded=loadExpandedProjectIds(taskId);
  const next=!expanded.has(projectId);
  if(next){
    const previewExpanded=loadPreviewExpandedProjectIds(taskId);
    previewExpanded.delete(projectId);
    savePreviewExpandedProjectIds(taskId,previewExpanded);
    const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
    settingsExpanded.delete(projectId);
    saveSettingsExpandedProjectIds(taskId,settingsExpanded);
    expanded.add(projectId);
  }else expanded.delete(projectId);
  saveExpandedProjectIds(taskId,expanded);
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
  bindExpandRowEvents(root,auth,taskId,companyName,projectId);
  rememberOverviewFocus(taskId,projectId);
}
async function closeProjectSettingsDom(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot):Promise<void>{
  const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
  settingsExpanded.delete(projectId);
  saveSettingsExpandedProjectIds(taskId,settingsExpanded);
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
  bindExpandRowEvents(root,auth,taskId,companyName,projectId);
}
async function toggleProjectSettings(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot):Promise<void>{
  const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
  const next=!settingsExpanded.has(projectId);
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  if(next){
    const attachmentExpanded=loadExpandedProjectIds(taskId);
    attachmentExpanded.delete(projectId);
    saveExpandedProjectIds(taskId,attachmentExpanded);
    const previewExpanded=loadPreviewExpandedProjectIds(taskId);
    previewExpanded.delete(projectId);
    savePreviewExpandedProjectIds(taskId,previewExpanded);
    settingsExpanded.add(projectId);
    saveSettingsExpandedProjectIds(taskId,settingsExpanded);
    syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
    bindExpandRowEvents(root,auth,taskId,companyName,projectId);
  }else{
    settingsExpanded.delete(projectId);
    saveSettingsExpandedProjectIds(taskId,settingsExpanded);
    syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
    bindExpandRowEvents(root,auth,taskId,companyName,projectId);
  }
  rememberOverviewFocus(taskId,projectId);
}
function closeSettingsDrawer(root:HTMLElement):void{
  const drawer=root.querySelector<HTMLElement>('#crSettingsDrawer');
  if(drawer)drawer.hidden=true;
}
type PreviewParty={name:string;role?:string;ownershipKind?:string;ownershipLabel?:string;ownershipTag?:string;ownershipBasis?:string;econKind?:string;actualController?:string;creditCode?:string;shareholders?:Array<{name:string;ratio?:number}>;relatedNames?:string[]};
type PreviewPayload={classification?:ContractClassification;schemaVersion?:number;contractTypeRoutingVersion?:number;contractTypeAnalysis?:string;contractType?:string;contractTypeSeries?:string;contractTypeSpecific?:string;contractTypeDisplay?:string;enhancedFeatures?:{enterpriseOwnershipLookup?:boolean};parties?:PreviewParty[];partyRelation?:string;mainContent?:string;contractAmount?:string;recommendedCompliancePolicies?:CompliancePolicyRecommendation[];warnings?:string[];fillReminders?:string[];text?:string;contractTitle?:string};

/** 预览当事方名称是否像企业主体（需走企查查工商 enrichment）。 */
function previewPartyLooksLikeEnterprise(name:string):boolean{
  return /公司|集团|院|所|中心|银行|合伙/u.test(String(name??'').trim());
}

/** 是否尚未写入可用的企业性质简述。 */
function previewPartyOwnershipPending(party:PreviewParty):boolean{
  if(!previewPartyLooksLikeEnterprise(party.name??''))return false;
  if(formatPartyOwnershipBrief(party))return false;
  if(party.econKind||party.actualController)return false;
  if(/企业类型：/.test(String(party.ownershipBasis??'')))return false;
  const kind=String(party.ownershipKind??'').trim();
  if(!kind||kind==='unknown')return true;
  return !party.ownershipBasis;
}

/** 拆分旧预览 JSON 里合并在一行的当事方，并尽量保留已匹配主体的工商字段。 */
function flattenPreviewParties(parties:PreviewParty[]):PreviewParty[]{
  const out:PreviewParty[]=[];
  for(const party of parties){
    const role=String(party.role??'').trim();
    const name=String(party.name??'').trim();
    const combinedLabel=role&&!/^(?:甲|乙|丙|丁)\s*方|发包|承包/u.test(name)?`${role}：${name}`:name;
    const entries=splitCombinedPartyEntries(combinedLabel);
    if(entries.length<=1){
      out.push(party);
      continue;
    }
    for(const entry of entries){
      const duplicate=out.find(item=>legalPartyNamesLikelyMatch(String(item.name??''),entry.name));
      if(duplicate){
        if(!duplicate.role&&entry.role)duplicate.role=entry.role;
        continue;
      }
      const keepFields=legalPartyNamesLikelyMatch(entry.name,name)?party:null;
      out.push(keepFields
        ?{...keepFields,name:entry.name,role:entry.role||keepFields.role}
        :{name:entry.name,role:entry.role||role});
    }
  }
  return out.length?out:parties;
}

/** 预览列表中单条当事方的展示文案。 */
function previewPartyListLabel(party:PreviewParty):string{
  const role=String(party.role??'').trim();
  const name=String(party.name??'').trim();
  if(/^(?:甲|乙|丙|丁)\s*方\s*[：:]|^发包|^承包/u.test(name))return name;
  if(role&&!name.startsWith(role))return`${role}：${name}`;
  return name;
}

function formatPreviewParties(parties:PreviewParty[],partyRelation?:string,showRelation=true):string{
  const list=flattenPreviewParties(parties);
  if(!list.length)return'未能识别';
  const items=`<ol class="cr-preview-party-list">${list.map(party=>{
    const label=previewPartyListLabel(party);
    const brief=formatPartyOwnershipBrief(party);
    // 未查询时由统一提示说明原因，避免在每个主体后重复“未查询”造成误导。
    const suffix=brief?`（${brief}）`:'';
    return `<li>${escapeHtml(label + suffix)}</li>`;
  }).join('')}</ol>`;
  const relation=String(partyRelation??'').trim()||(showRelation?summarizePartyRelations(list):'');
  const relationHtml=relation?`<p class="cr-preview-party-relation">${escapeHtml(relation)}</p>`:'';
  return `${items}${relationHtml}`;
}
function formatPreviewAmount(value?:string):string{
  return sanitizePreviewContractAmount(value);
}
function normalizePreviewPayload(payload:PreviewPayload):PreviewPayload{
  const record=payload.classification;
  const c=record&&isCurrentContractClassification(record)?record:pendingContractClassification();
  const authoritative=contractClassificationPreview(c);
  return {...payload,parties:flattenPreviewParties(payload.parties??[]),contractTypeSeries:authoritative.series,contractTypeSpecific:authoritative.specific,contractTypeDisplay:authoritative.label,contractTypeAnalysis:authoritative.analysis};
}
/** 预览区「合同类型」：系列 + 具体识别名，附类型判断说明。 */
function renderPreviewContractTypeBlock(payload:PreviewPayload):string{
  const recognized=String(payload.contractType??'').trim();
  const fallbackParts=recognized?buildContractTypePreviewParts(recognized):{series:'',specific:'',label:''};
  const series=String(payload.contractTypeSeries??fallbackParts.series).trim();
  const specific=String(payload.contractTypeSpecific??fallbackParts.specific).trim();
  const display=String(payload.contractTypeDisplay??fallbackParts.label).trim();
  const analysis=String(payload.contractTypeAnalysis??'').trim();
  let headline='—';
  if(series&&specific){
    headline=`<span class="cr-preview-type-series">${escapeHtml(series)}</span><span class="cr-preview-type-sep"> · </span><span class="cr-preview-type-specific">${escapeHtml(specific)}</span>`;
  }else if(display){
    headline=escapeHtml(display);
  }else if(series){
    headline=escapeHtml(series);
  }else if(specific){
    headline=escapeHtml(specific);
  }else if(analysis){
    headline=escapeHtml(analysis.split(/[。；]/u)[0]??analysis);
  }
  // 说明即使以合同类型开头也不能隐藏；模型通常会先复述类型，再给出判断依据。
  const showAnalysis=Boolean(analysis&&analysis!==display&&analysis!==specific);
  const analysisHtml=showAnalysis?`<p class="cr-preview-type-analysis">${escapeHtml(analysis)}</p>`:'';
  return`<div class="cr-preview-type-block">${headline}${analysisHtml}</div>`;
}
/**
 * 快速预览是否已识别为非合同文件。
 * @param payload 预览结果
 */
function previewIsNonContractDocument(payload:PreviewPayload):boolean{
  const fields=[payload.contractTypeSeries,payload.contractTypeDisplay,payload.contractType,payload.contractTypeSpecific];
  return fields.some(item=>{
    const value=String(item??'').trim();
    if(!value)return false;
    if(value==='非合同文件'||value.startsWith('非合同文件'))return true;
    return isNonContractDocumentSeriesLabel(value);
  });
}
function renderCompliancePolicyRecommendations(recommendations:CompliancePolicyRecommendation[],nonContract=false):string{
  if(!recommendations.length)return'';
  const items=recommendations.map(item=>{
    const path=item.folderPath?`<span class="cr-preview-policy-path">${escapeHtml(item.folderPath)}</span>`:'';
    const reason=item.reason?`<span class="cr-preview-policy-reason">${escapeHtml(item.reason)}</span>`:'';
    return`<li><span class="cr-preview-policy-name">${escapeHtml(item.fileName)}</span>${path}${reason}</li>`;
  }).join('');
  const sourceLabel=nonContract?'文件内容':'合同内容';
  const hint=recommendations.some(item=>item.source==='ai')
    ?`由大模型结合${sourceLabel}与制度目录推荐；可在项目「设置」中确认或调整。`
    :'可在项目「设置」中确认或调整参照的制度列表。';
  return`<section class="cr-preview-policies"><h4>推荐合规管理制度</h4><ul class="cr-preview-policy-list">${items}</ul><p class="cr-preview-policy-hint">${hint}</p></section>`;
}
function renderPreviewContent(payload:PreviewPayload,qichachaReady=false):string{
  const normalized=normalizePreviewPayload(payload);
  const nonContract=previewIsNonContractDocument(normalized);
  const typeLabel=nonContract?'文件类型':'合同类型';
  const partyLabel=nonContract?'文件发布主体':'合同当事方';
  const contentLabel=nonContract?'文件主要内容':'合同主要内容';
  const amountLabel=nonContract?'涉及金额':'合同金额';
  const typeBlock=renderPreviewContractTypeBlock(normalized);
  const partiesHtml=formatPreviewParties(normalized.parties??[],normalized.partyRelation,normalized.enhancedFeatures?.enterpriseOwnershipLookup===true);
  const ownershipHint=renderPreviewOwnershipHint(normalized,qichachaReady);
  const rawMain=String(normalized.mainContent??'').trim();
  const mainContent=rawMain&&!isPoorPreviewMainContent(rawMain)?rawMain:PREVIEW_MAIN_CONTENT_UNAVAILABLE;
  const amount=formatPreviewAmount(normalized.contractAmount);
  const amountRow=amount?`<div><dt>${amountLabel}</dt><dd>${escapeHtml(amount)}</dd></div>`:'';
  const policySection=renderCompliancePolicyRecommendations(normalized.recommendedCompliancePolicies??[],nonContract);
  return`<dl class="cr-preview-summary"><div><dt>${typeLabel}</dt><dd>${typeBlock}</dd></div><div><dt>${partyLabel}</dt><dd>${partiesHtml}${ownershipHint}</dd></div><div><dt>${contentLabel}</dt><dd>${escapeHtml(mainContent)}</dd></div>${amountRow}</dl>${policySection}`;
}
/** 当前正文是否已完成快速预览（仅认与正文 fileId 绑定的预审结果）。 */
function previewReadyForMain(snapshot:ProjectSnapshot):boolean{
  const main=snapshot.main;
  const profile=snapshot.quickProfile;
  if(!main||!profile||profile.fileId!==main.id)return false;
  return Boolean(profile.previewFileId)||profile.status==='ready'||profile.status==='needs-manual-review';
}
/** 解析当前正文对应的预览文件 ID。 */
function resolvePreviewFileIdFromSnapshot(snapshot:ProjectSnapshot):string{
  const main=snapshot.main;
  const profile=snapshot.quickProfile;
  if(!main||!profile||profile.fileId!==main.id)return'';
  return profile.previewFileId??'';
}
async function resolvePreviewFileId(taskId:string,projectId:string,fileId=''):Promise<string>{
  const status=await api.get<ReviewStatus>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/status`));
  const mainId=fileId||status.files?.mainFiles?.[0]?.id||'';
  const profile=status.quickProfiles?.find(item=>item.fileId===mainId);
  return profile?.previewFileId??'';
}
/** 等待上传后后台生成的预览文件就绪。 */
async function waitForPreviewFileId(taskId:string,projectId:string,fileId:string,timeoutMs=180000):Promise<string>{
  const started=Date.now();
  while(Date.now()-started<timeoutMs){
    const status=await api.get<ReviewStatus>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/status`));
    const profile=status.quickProfiles?.find(item=>item.fileId===fileId);
    if(profile?.previewFileId)return profile.previewFileId;
    if(profile?.status==='failed')return'';
    await new Promise(resolve=>window.setTimeout(resolve,2000));
  }
  return'';
}
async function fetchPreviewPayload(previewId:string):Promise<PreviewPayload|null>{
  const response=await fetch(apiUrl(`/api/files/${encodeURIComponent(previewId)}/download`),{credentials:'include'});
  if(!response.ok)return null;
  return await response.json() as PreviewPayload;
}
/** 正文上传后后台触发预览生成；正文更换后若无当前正文绑定的预审结果则重新发起。 */
function scheduleBackgroundPreview(taskId:string,projectId:string,snapshot:ProjectSnapshot):void{
  if(!isLegalFeatureEnabled('legal-quick-inspect-v1'))return;
  if(!snapshot.main||previewReadyForMain(snapshot))return;
  void ensurePreviewInspect(taskId,projectId,snapshot.main.id).catch(()=>{/** 后台预览失败时由用户稍后重试查看 */});
}
const QICHACHA_OWNERSHIP_SKIP_MARKER='__qichacha_lookup_skipped__';

/** 上次预审已明确跳过企查查（未配置、平台关闭或查询不可用）。 */
function previewOwnershipLookupSettled(payload:PreviewPayload):boolean{
  return (payload.warnings??[]).some(item=>item===QICHACHA_OWNERSHIP_SKIP_MARKER||/企查查|未配置企查查|未能核验|查询暂不可用|跳过国资|查询未完成|国资与关联识别功能未启用/.test(String(item??'')));
}

/** 预览 JSON 是否仍缺企业性质或关联关系字段。 */
function previewNeedsOwnershipRefresh(payload:PreviewPayload):boolean{
  const parties=Array.isArray(payload.parties)?payload.parties:[];
  if(parties.length>=2&&!String(payload.partyRelation??'').trim())return true;
  return parties.some(party=>{
    const name=String(party.name??'').trim();
    if(!name||!/公司|集团|院|所|中心|银行|合伙/.test(name))return false;
    if(!party.ownershipKind)return true;
    if(party.ownershipKind==='non-state'&&/法人独资/.test(`${party.econKind??''}${party.ownershipBasis??''}`))return true;
    if((party.ownershipKind==='unknown'||party.ownershipKind==='non-state')&&/法人独资/.test(`${party.econKind??''}${party.ownershipBasis??''}`)&&!/国资委|财政部|国务院|事业单位|属国企|国资归属/.test(`${party.ownershipBasis??''}${party.actualController??''}${party.ownershipTag??''}`))return true;
    return party.ownershipKind==='unknown'&&!party.econKind&&!party.actualController&&!party.ownershipBasis;
  });
}

/**
 * 上次预览是否因缺少企查查开放平台凭证而跳过查询。
 * @param payload 当前预览 JSON
 */
function previewOwnershipSkippedForMissingKeys(payload:PreviewPayload):boolean{
  return (payload.warnings??[]).some(item=>/后台尚未配置企查查|尚未配置企查查开放平台|企查查查询暂不可用/.test(String(item??'')));
}

/**
 * 是否应在打开预览时重新发起预审以补全国资与关联关系。
 * @param payload 当前预览 JSON
 * @param qichachaReady 后台是否已配置企查查客户身份识别
 */
function shouldRefreshPreviewOwnership(payload:PreviewPayload,qichachaReady:boolean):boolean{
  if(payload.enhancedFeatures?.enterpriseOwnershipLookup!==true)return false;
  if(!isLegalFeatureEnabled('legal-party-ownership-v1')&&!qichachaReady)return false;
  if((payload.warnings??[]).some(item=>/国资与关联识别功能未启用/.test(String(item??''))))return false;
  if(qichachaReady&&previewOwnershipSkippedForMissingKeys(payload))return true;
  if(previewOwnershipLookupSettled(payload))return false;
  return previewNeedsOwnershipRefresh(payload);
}

/** 读取后台是否已配置企查查客户身份识别（开放平台 AppKey/SecretKey）。 */
async function isQichachaOwnershipReady():Promise<boolean>{
  try{
    const payload=await api.get<{snapshot:{
      platform?:{qichachaKycConfigured?:boolean};
      qichachaMcp?:{status:string;platformAvailable:boolean;configured?:boolean};
    }}>(apiUrl('/api/user/integrations'));
    const card=payload.snapshot?.qichachaMcp;
    return Boolean(
      payload.snapshot?.platform?.qichachaKycConfigured
      || (card?.platformAvailable && card.status==='ready')
      || (card?.platformAvailable && card.configured),
    );
  }catch{
    return false;
  }
}

/** 未查到企业性质时在预览区给出可操作说明。 */
function renderPreviewOwnershipHint(payload:PreviewPayload,qichachaReady:boolean):string{
  if(payload.enhancedFeatures?.enterpriseOwnershipLookup!==true)return'';
  if(!isLegalFeatureEnabled('legal-party-ownership-v1')&&!qichachaReady)return'';
  const parties=Array.isArray(payload.parties)?payload.parties:[];
  const hasEnterprise=parties.some(party=>previewPartyLooksLikeEnterprise(String(party.name??'')));
  if(!hasEnterprise)return'';
  const hasBrief=parties.some(party=>Boolean(formatPartyOwnershipBrief(party)));
  const relation=String(payload.partyRelation??'').trim()||summarizePartyRelations(parties);
  const ownershipPending=parties.some(party=>previewPartyOwnershipPending(party));
  if(!ownershipPending&&(hasBrief||relation))return'';
  const warningText=(payload.warnings??[]).find(item=>{
    const text=String(item??'');
    return text!==QICHACHA_OWNERSHIP_SKIP_MARKER&&/企查查|MCP|Bearer Token|身份凭证|invalid_token|外部接入|保存|国资与关联识别/.test(text);
  });
  if(warningText)return`<p class="cr-preview-ownership-hint">${escapeHtml(warningText)}</p>`;
  if(qichachaReady){
    return'<p class="cr-preview-ownership-hint">已配置企查查客户身份识别，但本次预览未写入工商结果。请关闭预览后重新打开；若仍无结果，请核对 AppKey 余额。</p>';
  }
  if(previewOwnershipLookupSettled(payload)||previewNeedsOwnershipRefresh(payload)){
    return'<p class="cr-preview-ownership-hint">企业性质与关联关系使用企查查客户身份识别接口。请联系管理员在后台「AI 服务 · 工商信息」配置 AppKey/SecretKey，然后重新打开预览。</p>';
  }
  return'';
}
async function loadProjectPreviewContent(root:HTMLElement,taskId:string,projectId:string,_companyName:string,_auth:AuthSnapshot):Promise<void>{
  const body=root.querySelector<HTMLElement>(`[data-project-preview-body="${projectId}"]`);
  if(!body)return;
  const project=await loadProjectById(taskId,projectId);
  if(!project)return;
  const snapshot=await loadProjectSnapshot(taskId,project);
  if(!snapshot.main){body.innerHTML='<div class="cr-preview-loading">请先上传合同正文</div>';return}
  body.innerHTML='<div class="cr-preview-loading">正在加载预览…</div>';
  let previewId=resolvePreviewFileIdFromSnapshot(snapshot);
  if(!previewId){
    body.innerHTML='<div class="cr-preview-loading">预览正在后台生成，请稍候…</div>';
    try{
      const profile=await ensurePreviewInspect(taskId,projectId,snapshot.main.id);
      previewId=profile.previewFileId??'';
    }catch{/** 请求超时后继续轮询服务端已写入的预览 */}
    if(!previewId)previewId=await waitForPreviewFileId(taskId,projectId,snapshot.main.id);
    if(!previewId){body.innerHTML='<div class="cr-preview-loading">预览尚未就绪，请稍后再次打开查看。</div>';return}
  }
  try{
    let payload=await fetchPreviewPayload(previewId);
    if(!payload)throw new Error('预览文件读取失败');
    if(snapshot.quickProfile?.classification&&isCurrentContractClassification(snapshot.quickProfile.classification)){
      payload=normalizePreviewPayload({...payload,classification:snapshot.quickProfile.classification});
    }
    const qichachaReady=await isQichachaOwnershipReady();
    const needOwnership=shouldRefreshPreviewOwnership(payload,qichachaReady);
    const needRouting=(payload.contractTypeRoutingVersion??0)<CONTRACT_CLASSIFICATION_VERSION
      ||(payload.classification?.warnings??[]).some(item=>/具体合同类型与主类别冲突/.test(String(item)));
    const needSummary=shouldRefreshPreviewMainContent(String(payload.mainContent??''),String(payload.contractType??''))
      ||needRouting
      ||shouldRefreshPreviewTypeAnalysis(String(payload.contractTypeAnalysis??''),String(payload.contractType??''))
      ||shouldRefreshPreviewContractAmount(payload.contractAmount);
    if(needOwnership||needSummary){
      const refreshingNonContract=previewIsNonContractDocument(payload);
      body.innerHTML=needSummary
        ?(refreshingNonContract?'<div class="cr-preview-loading">正在重新生成文件概述…</div>':'<div class="cr-preview-loading">正在重新生成合同概述…</div>')
        :(refreshingNonContract?'<div class="cr-preview-loading">正在识别发布主体企业性质…</div>':'<div class="cr-preview-loading">正在识别当事方企业性质…</div>');
      const profile=await ensurePreviewInspect(taskId,projectId,snapshot.main.id,{force:true});
      previewId=profile.previewFileId??previewId;
      payload=await fetchPreviewPayload(previewId)??payload;
      if(profile.classification&&isCurrentContractClassification(profile.classification))payload=normalizePreviewPayload({...payload,classification:profile.classification});
    }
    body.innerHTML=renderPreviewContent(payload,qichachaReady);
  }catch{
    body.innerHTML='<div class="cr-preview-loading">预览加载失败，请稍后重试。</div>';
  }
}
async function toggleProjectPreview(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot):Promise<void>{
  if(!isLegalFeatureEnabled('legal-quick-inspect-v1'))return;
  const previewExpanded=loadPreviewExpandedProjectIds(taskId);
  const next=!previewExpanded.has(projectId);
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  if(next){
    const attachmentExpanded=loadExpandedProjectIds(taskId);
    attachmentExpanded.delete(projectId);
    saveExpandedProjectIds(taskId,attachmentExpanded);
    const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
    settingsExpanded.delete(projectId);
    saveSettingsExpandedProjectIds(taskId,settingsExpanded);
    previewExpanded.add(projectId);
    savePreviewExpandedProjectIds(taskId,previewExpanded);
    syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
    bindExpandRowEvents(root,auth,taskId,companyName,projectId);
    await loadProjectPreviewContent(root,taskId,projectId,companyName,auth);
  }else{
    previewExpanded.delete(projectId);
    savePreviewExpandedProjectIds(taskId,previewExpanded);
    syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
    bindExpandRowEvents(root,auth,taskId,companyName,projectId);
  }
  rememberOverviewFocus(taskId,projectId);
}
async function openProjectPreview(root:HTMLElement,taskId:string,projectId:string,companyName:string,auth:AuthSnapshot):Promise<void>{
  const previewExpanded=loadPreviewExpandedProjectIds(taskId);
  const attachmentExpanded=loadExpandedProjectIds(taskId);
  attachmentExpanded.delete(projectId);
  saveExpandedProjectIds(taskId,attachmentExpanded);
  const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
  settingsExpanded.delete(projectId);
  saveSettingsExpandedProjectIds(taskId,settingsExpanded);
  if(!previewExpanded.has(projectId))previewExpanded.add(projectId);
  savePreviewExpandedProjectIds(taskId,previewExpanded);
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  syncProjectExpandDom(root,taskId,projectId,snapshot,companyName,auth);
  bindExpandRowEvents(root,auth,taskId,companyName,projectId);
  await loadProjectPreviewContent(root,taskId,projectId,companyName,auth);
  rememberOverviewFocus(taskId,projectId);
}
async function loadComplianceContext(organizations:UserOrganization[],folders?:ComplianceLibraryFolders):Promise<ComplianceContext>{
  const enterpriseOrg=organizations.find(isActiveEnterpriseOrganization);
  const hasEnterprise=Boolean(enterpriseOrg);
  const personalFolderId=folders?.personal?.folderId??'';
  const enterpriseFolderId=folders?.enterprise?.folderId??'';
  const [personal,enterprise]=await Promise.all([
    loadComplianceLibrarySummary('personal',personalFolderId),
    hasEnterprise?loadComplianceLibrarySummary('enterprise',enterpriseFolderId):Promise.resolve(null),
  ]);
  return{hasEnterprise,enterpriseOrgId:enterpriseOrg?.id??'',personal,enterprise};
}
/** 返回总览并展开指定项目的行内设置。 */
function navigateToProjectSettingsOnOverview(taskId:string,projectId:string):void{
  rememberOverviewFocus(taskId,projectId);
  rememberActiveProject(taskId,projectId);
  const expanded=loadSettingsExpandedProjectIds(taskId);
  expanded.add(projectId);
  saveSettingsExpandedProjectIds(taskId,expanded);
  navigate(pathFor('overview'));
}
async function openSettingsDrawer(root:HTMLElement,taskId:string,projectIds:string[],companyName:string,auth:AuthSnapshot):Promise<void>{
  const drawer=root.querySelector<HTMLElement>('#crSettingsDrawer');
  const body=root.querySelector<HTMLElement>('#crSettingsDrawerBody');
  const title=root.querySelector<HTMLElement>('#crSettingsDrawerTitle');
  const message=root.querySelector<HTMLElement>('#crSettingsDrawerMessage');
  if(!drawer||!body||!title)return;
  const value=await loadTaskSchemeFromCloud(taskId,companyName);
  const userDisplayName=auth.user.displayName||auth.user.email||'';
  const orgPayload=await api.get<{organizations:UserOrganization[]}>(apiUrl('/api/organizations')).catch(()=>({organizations:[] as UserOrganization[]}));
  const complianceContext=await loadComplianceContext(orgPayload.organizations??[],value.complianceLibraryFolders);
  body.innerHTML=settingsFieldsHtml(taskId,value,null,companyName,userDisplayName,'task',complianceContext);
  bindSettingsFormEvents(body,complianceContext);
  title.textContent=`任务默认审核方案（${projectIds.length} 项将继承，已自定义项保留）`;
  if(message)message.textContent='';
  drawer.hidden=false;
  drawer.dataset.projectIds=projectIds.join(',');
}
async function executeProjectReview(taskId:string,projectId:string,auth:AuthSnapshot,companyName:string):Promise<void>{
  const project=await loadProjectById(taskId,projectId);
  if(!project)throw new Error('项目不存在');
  const snapshot=await loadProjectSnapshot(taskId,project,companyName);
  if(!snapshot.main)throw new Error('请先上传合同正文');
  const preferences=resolveProjectSettingsPreferences(taskId,projectId,companyName,snapshot);
  await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/review`),{projectId,fileId:snapshot.main.id,attachmentFileIds:snapshot.files.attachmentFiles.map(file=>file.id),reviewPositionCompany:preferences.reviewPositionCompany,reviewerName:preferences.reviewerName||auth.user.displayName||auth.user.email||'法务审核',contractType:preferences.contractType,reviewDepth:preferences.reviewDepth,reviewFocus:preferences.reviewFocus,scopes:preferences.scopes,complianceLibraryScope:preferences.complianceLibraryScope},{timeoutMs:600000});
}
async function runSingleProjectReview(root:HTMLElement,taskId:string,projectId:string,auth:AuthSnapshot,companyName:string):Promise<void>{
  const project=await loadProjectById(taskId,projectId);
  const snapshot=project?await loadProjectSnapshot(taskId,project,companyName):null;
  const meta=snapshot?projectReviewActionMeta(taskId,snapshot):{enabled:false,running:false,title:'无法开始审核',emphasize:false};
  if(!meta.enabled){
    window.alert(meta.title);
    return;
  }
  const rowReviewBtn=root.querySelector<HTMLButtonElement>(`[data-project-review="${projectId}"]`);
  if(rowReviewBtn){rowReviewBtn.disabled=true;rowReviewBtn.classList.add('is-running');const img=rowReviewBtn.querySelector('img');if(img)img.src=CR_ICON_REVIEW_START_DISABLED}
  markWorkbenchProjectReviewing(taskId,projectId);
  setProjectRowReviewingUi(root,projectId,5,'正在启动审核');
  try{
    await executeProjectReviewWithWorkbenchProgress(root,taskId,projectId,auth,companyName,snapshot?.review?.id??'');
  }catch(error){
    clearWorkbenchProjectReviewing(taskId,projectId);
    stopWorkbenchReviewPoller(taskId,projectId);
    window.alert(error instanceof Error?error.message:'审核失败');
    await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
  }
}
async function runBatchReview(root:HTMLElement,taskId:string,projectIds:string[],auth:AuthSnapshot,companyName:string):Promise<void>{
  const eligible=projectIds.filter(projectId=>isRowBatchReviewReady(root,taskId,projectId));
  if(!eligible.length){window.alert('没有可开始审核的项目，请检查正文与审核设置是否已保存。');return}
  const reviewBtn=root.querySelector<HTMLButtonElement>('#batchStartReview');
  if(reviewBtn){reviewBtn.disabled=true;reviewBtn.textContent=`正在审核 1/${eligible.length}…`}
  let done=0;
  for(const projectId of eligible){
    if(reviewBtn)reviewBtn.textContent=`正在审核 ${done+1}/${eligible.length}…`;
    const rowReviewBtn=root.querySelector<HTMLButtonElement>(`[data-project-review="${projectId}"]`);
    setProjectRowReviewingUi(root,projectId,5,'正在启动审核');
    if(rowReviewBtn){rowReviewBtn.disabled=true;rowReviewBtn.classList.add('is-running');const img=rowReviewBtn.querySelector('img');if(img)img.src=CR_ICON_REVIEW_START_DISABLED}
    markWorkbenchProjectReviewing(taskId,projectId);
    try{
      await executeProjectReviewWithWorkbenchProgress(root,taskId,projectId,auth,companyName);
    }catch(error){
      clearWorkbenchProjectReviewing(taskId,projectId);
      stopWorkbenchReviewPoller(taskId,projectId);
      window.alert(error instanceof Error?error.message:`项目审核失败`);
      await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
      break;
    }
    done++;
  }
  if(reviewBtn){reviewBtn.textContent='审核';reviewBtn.disabled=false}
  updateBatchToolbar(root,taskId);
}
function bindExpandRowEvents(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string,projectId:string):void{
  const expandRow=root.querySelector(`.cr-project-expand-row[data-project-id="${projectId}"]`);
  if(!expandRow)return;
  const attachBtn=expandRow.querySelector<HTMLButtonElement>(`[data-project-attachment="${projectId}"]`);
  const attachInput=expandRow.querySelector<HTMLInputElement>(`[data-project-attachment-input="${projectId}"]`);
  const previewClose=expandRow.querySelector<HTMLButtonElement>(`[data-project-preview-close="${projectId}"]`);
  const dropzone=expandRow.querySelector<HTMLElement>(`[data-attachment-dropzone="${projectId}"]`);
  const startAttachmentUpload=async(selected:File[])=>{
    if(!selected.length)return;
    const progressEl=expandRow.querySelector<HTMLElement>(`[data-attachment-upload-progress="${projectId}"]`);
    const addBtn=expandRow.querySelector<HTMLButtonElement>(`[data-project-attachment="${projectId}"]`);
    try{
      if(progressEl){progressEl.hidden=false;progressEl.textContent='上传中 0%'}
      if(addBtn)addBtn.disabled=true;
      await uploadAttachmentFiles(taskId,projectId,selected,percent=>{if(progressEl)progressEl.textContent=`上传中 ${percent}%`});
      await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
    }catch(error){window.alert(error instanceof Error?error.message:'上传附件失败')}
    finally{if(attachInput)attachInput.value='';if(progressEl)progressEl.hidden=true;if(addBtn)addBtn.disabled=false}
  };
  if(attachBtn)attachBtn.onclick=()=>{attachInput?.click()};
  expandRow.querySelectorAll<HTMLButtonElement>('[data-attachment-drop-open]').forEach(button=>{button.onclick=()=>{attachInput?.click()}});
  if(attachInput)attachInput.onchange=async event=>{
    const input=event.currentTarget as HTMLInputElement;
    await startAttachmentUpload(Array.from(input.files??[]));
  };
  if(dropzone)bindFileDropZone(dropzone,files=>{void startAttachmentUpload(files)});
  if(previewClose)previewClose.onclick=()=>void closeProjectPreviewDom(root,taskId,projectId,companyName,auth);
  const settingsSave=expandRow.querySelector<HTMLButtonElement>(`[data-project-settings-save="${projectId}"]`);
  const settingsRestore=expandRow.querySelector<HTMLButtonElement>(`[data-project-settings-restore="${projectId}"]`);
  const settingsBody=expandRow.querySelector<HTMLElement>('.cr-settings-panel');
  const settingsMessage=expandRow.querySelector<HTMLElement>(`[data-settings-message="${projectId}"]`);
  if(settingsSave&&settingsBody)settingsSave.onclick=()=>void saveProjectSettings(root,taskId,projectId,companyName,auth,settingsBody,settingsMessage);
  if(settingsRestore&&settingsBody)settingsRestore.onclick=()=>void restoreProjectSettings(root,taskId,projectId,companyName,auth,settingsBody,settingsMessage);
  if(settingsBody)bindSettingsFormEvents(settingsBody,overviewComplianceContext??undefined);
  expandRow.querySelectorAll<HTMLButtonElement>('.legal-review-file-delete').forEach(button=>{
    button.onclick=async()=>{
      const fileId=button.dataset.fileId;if(!fileId||!window.confirm('确定从个人空间中删除该待审核文件吗？'))return;
      try{button.disabled=true;await api.delete(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/files/${encodeURIComponent(fileId)}?projectId=${encodeURIComponent(projectId)}`));if(localStorage.getItem(mainKey(taskId,projectId))===fileId)localStorage.removeItem(mainKey(taskId,projectId));await refreshProjectRowDom(root,taskId,projectId,companyName,auth)}catch(error){window.alert(error instanceof Error?error.message:'删除失败');button.disabled=false}
    };
  });
}
function bindOverviewRow(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string,projectId:string):void{
  const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
  if(!row)return;
  row.querySelector<HTMLInputElement>('.cr-project-select')?.addEventListener('change',()=>updateBatchToolbar(root,taskId));
  row.querySelector<HTMLButtonElement>('[data-project-expand]')?.addEventListener('click',()=>void toggleExpandDom(root,taskId,projectId,companyName,auth));
  row.querySelector<HTMLButtonElement>('[data-project-preview]:not([disabled])')?.addEventListener('click',()=>void toggleProjectPreview(root,taskId,projectId,companyName,auth));
  row.querySelector<HTMLButtonElement>('[data-project-settings]')?.addEventListener('click',()=>void toggleProjectSettings(root,taskId,projectId,companyName,auth));
  row.querySelector<HTMLButtonElement>(`[data-project-review="${projectId}"]:not([disabled])`)?.addEventListener('click',()=>void runSingleProjectReview(root,taskId,projectId,auth,companyName));
  row.querySelector<HTMLButtonElement>('[data-project-archive]:not([disabled])')?.addEventListener('click',()=>void openProjectArchive(taskId,projectId));
  row.querySelector<HTMLButtonElement>('[data-project-result]:not([disabled])')?.addEventListener('click',()=>{
    rememberActiveProject(taskId,projectId);
    rememberOverviewFocus(taskId,projectId);
    navigate(pathFor('risks'));
  });
  row.querySelectorAll<HTMLButtonElement>('[data-project-main]').forEach(button=>button.addEventListener('click',()=>{const input=root.querySelector<HTMLInputElement>(`[data-project-main-input="${projectId}"]`);input?.click()}));
  row.querySelector<HTMLInputElement>(`[data-project-main-input="${projectId}"]`)?.addEventListener('change',async event=>{
    const input=event.currentTarget as HTMLInputElement;const file=input.files?.[0];if(!file)return;
    row.querySelectorAll<HTMLButtonElement>('[data-project-main]').forEach(button=>{button.disabled=true});
    setProjectMainBodyUploading(root,projectId,'上传中');
    try{
      const uploaded=await uploadMainFile(taskId,projectId,file,percent=>{
        setProjectMainBodyUploading(root,projectId,percent>0&&percent<100?`上传中 ${percent}%`:'上传中');
      });
      localStorage.setItem(mainKey(taskId,projectId),uploaded.id);
      const bodyCell=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-col-body`);
      if(bodyCell)bodyCell.innerHTML=renderProjectMainBodyCell(projectId,uploaded);
      const statusBadge=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"] .cr-row-status`);
      if(statusBadge){statusBadge.textContent='等待预览';statusBadge.className='cr-row-status is-preview-pending';}
      row.querySelectorAll<HTMLButtonElement>('[data-project-main]').forEach(button=>button.addEventListener('click',()=>{const mainInput=root.querySelector<HTMLInputElement>(`[data-project-main-input="${projectId}"]`);mainInput?.click()}));
      try{await ensurePreviewInspect(taskId,projectId,uploaded.id)}catch{/** 预审接口不可用时继续 */}
      await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
    }catch(error){
      window.alert(error instanceof Error?error.message:'上传合同正文失败');
      await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
    }finally{input.value=''}
  });
  row.querySelector<HTMLInputElement>(`[data-project-name="${projectId}"]`)?.addEventListener('change',async event=>{
    const input=event.currentTarget as HTMLInputElement;const name=input.value.trim();if(!name)return;
    try{await api.patch(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}`),{name})}catch(error){window.alert(error instanceof Error?error.message:'项目名称保存失败')}
  });
  bindExpandRowEvents(root,auth,taskId,companyName,projectId);
  row.addEventListener('click',event=>{
    if((event.target as HTMLElement).closest('button,input,label,a'))return;
    rememberOverviewFocus(taskId,projectId);
    root.querySelectorAll('.cr-project-table-row').forEach(item=>item.classList.toggle('is-active',item.getAttribute('data-project-id')===projectId));
  });
}
function overviewTaskRequired(taskId:string):string{
  return`<div class="cr-page"><section class="cr-inline-alert"><div><b>请先选择合同文件审核任务</b><p>在任务管理中进入「审合同」等合同文件审核任务后，再在此工作台新建项目。新建项目不会出现在任务管理列表中。</p></div><button class="cr-button cr-button-primary" id="chooseLegalTask">前往任务管理</button></section></div>`;
}

function fileCard(file:CloudFile,role:'main'|'attachment',isSelected:boolean):string{return`<article class="cr-file-card${isSelected?' is-selected':''}"><img src="${iconFor(file.originalName)}" alt=""><div><b title="${escapeHtml(file.originalName)}">${escapeHtml(file.originalName)}</b><span>${formatSize(file.sizeBytes)} · ${formatDate(file.createdAt)}</span></div>${role==='main'?`<button class="cr-file-select" data-select-main="${escapeHtml(file.id)}">${isSelected?'当前正文':'设为正文'}</button>`:'<span class="cr-file-role">附件</span>'}<button class="cr-icon-button legal-review-file-delete" data-file-id="${escapeHtml(file.id)}">删除</button></article>`}
function materials(files:ReviewFiles,main:CloudFile|null):string{return`<div class="cr-stage-layout"><main class="cr-stage-main">${pageHeading('步骤 01','准备审核材料','先确定一份合同正文，再补充与履约、价格或技术要求相关的附件。文件上传后立即归档至个人空间。')}
  <section class="cr-panel cr-upload-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">必需材料</span><h3>合同正文</h3><p>支持 DOC、DOCX、PDF、TXT、PNG/JPG。请优先上传可复制文字的 Word 或 PDF。${LEGAL_REVIEW_TEXT_UPLOAD_HINT}${LEGAL_REVIEW_SCAN_UPLOAD_HINT}${LEGAL_REVIEW_STORAGE_UPLOAD_HINT}建议使用 DOCX 生成原位批注。</p></div><button class="cr-button cr-button-primary" id="pickMainFile">上传合同正文</button></div><input id="contractMainFile" type="file" accept=".doc,.docx,.pdf,.txt,.png,.jpg,.jpeg" hidden><div class="cr-file-list">${files.mainFiles.length?files.mainFiles.map(file=>fileCard(file,'main',file.id===main?.id)).join(''):'<button class="cr-dropzone" id="emptyMainUpload"><b>选择合同正文文件</b><span>${LEGAL_REVIEW_UPLOAD_SIZE_HINT}</span></button>'}</div></section>
  <section class="cr-panel cr-upload-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">补充材料</span><h3>附件与背景文件</h3><p>支持 DOC、DOCX、PDF、TXT、PNG/JPG。附件用于核对正文，不单独出批注。文字版同样建议不超过 30 MB、200 页；${LEGAL_REVIEW_SCAN_UPLOAD_HINT}也可不传附件，直接审核正文。</p></div><button class="cr-button cr-button-secondary" id="pickAttachmentFiles">添加附件</button></div><input id="contractAttachmentFiles" type="file" multiple accept=".doc,.docx,.pdf,.txt,.png,.jpg,.jpeg" hidden><div class="cr-file-list" id="attachmentDropzone" data-attachment-dropzone="materials">${files.attachmentFiles.length?files.attachmentFiles.map(file=>fileCard(file,'attachment',false)).join(''):'<button class="cr-dropzone" id="emptyAttachmentUpload" type="button"><b>点击或拖入多份附件</b><span>可拖入多份 · 扫描件请控制在 8 MB、12 页以内</span></button>'}</div></section>${files.previewFiles?.length?`<section class="cr-panel cr-preview-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">自动生成</span><h3>预览成果</h3><p>快速预审生成的可阅读文本，归档在当前项目的“预览成果”文件夹。</p></div></div><div class="cr-file-list">${files.previewFiles.map(file=>`<article class="cr-file-card"><img src="${iconFor(file.originalName)}" alt=""><div><b>${escapeHtml(file.originalName)}</b><span>${formatSize(file.sizeBytes)} · ${formatDate(file.createdAt)}</span></div><button class="cr-button cr-button-secondary" data-download-preview="${escapeHtml(file.id)}">打开预览</button></article>`).join('')}</div></section>`:''}</main>
  <aside class="cr-stage-aside"><section class="cr-panel cr-sticky-card"><span class="cr-section-kicker">个人空间</span><h3>工作文件存储规则</h3><div class="cr-storage-path"><span>个人空间</span><span>任务</span><span>审合同</span><span>项目</span><span>合同文件原件</span></div><p>正文与附件归档在当前任务文件夹内的项目目录，成果进入同一项目的“成果 / 阅读版”。</p></section><section class="cr-panel cr-check-card"><h3>材料检查</h3><div class="cr-check-row ${main?'is-ready':''}"><i></i><p><b>合同正文</b><small>${main?'已选择，可继续':'尚未选择'}</small></p></div><div class="cr-check-row is-ready"><i></i><p><b>文件可读取</b><small>审核前将再次校验</small></p></div></section></aside></div><div class="cr-stage-footer"><span id="reviewMessage">${main?`当前正文：${escapeHtml(main.originalName)}`:'请先上传并选择合同正文'}</span><button class="cr-button cr-button-primary" id="gotoProjectSettings" ${main?'':'disabled'}>下一步：项目设置</button></div>`}

function settings(value:Preferences,profile:QuickProfile|null):string{const parties=profile?.parties??[];const recognizedType=profile?.contractType||'';const recommendedSeries=recommendedContractTypeSeriesFromProfile(profile);const displayValue={...value,contractType:normalizeContractTypeForSelect(value.contractType||recommendedSeries||'')};const typeOptions=buildContractTypeSelectOptions(displayValue.contractType,recognizedType,false);const partyOptions=[...new Map([...parties.map(item=>[item.name,`${item.role} · ${item.name}`] as const),...(value.reviewPositionCompany&&!parties.some(item=>item.name===value.reviewPositionCompany)?[[value.reviewPositionCompany,`当前主体 · ${value.reviewPositionCompany}`] as const]:[])])];const fillReminders=profile?mergeContractFillReminders(profile.fillReminders,profile.warnings.filter(isContractFillReminderLine)):[];const fillSection=fillReminders.length?renderContractFillRemindersPanel(fillReminders):'';const suggestion=profile?`<section class="cr-panel cr-insight-panel ${profileClass(profile)}"><div class="cr-insight-panel-head"><div><span class="cr-section-kicker">快速预审 · 仅作建议</span><h3>${escapeHtml(profile.contractTitle)}</h3><p>模型已综合正文、标题和交易目的完成初步判断；以下内容均需由你确认。</p></div><span class="cr-confidence">${profile.typeConfidence}% 置信度</span></div><div class="cr-insight-summary"><div><small>模型识别合同类型</small><b>${escapeHtml(profile.contractType)}</b></div><div><small>模型识别当事方</small><b>${escapeHtml(profile.parties.map(item=>`${item.role} ${item.name}`).join(' · ')||'未完整识别')}</b></div></div><div class="cr-insight-actions"><button type="button" class="cr-button cr-button-secondary" data-apply-contract-type="${escapeHtml(normalizeContractTypeForSelect(recommendedSeries||profile.contractType))}">采用模型类型</button>${profile.suggestedReviewPosition?`<button type="button" class="cr-button cr-button-secondary" data-apply-review-position="${escapeHtml(profile.suggestedReviewPosition)}">采用审核主体</button>`:''}<span>${escapeHtml(profile.recognitionMethod==='ai'?'DeepSeek V4.1 Flash 已完成识别':profile.status==='ready'?'已完成本地兜底识别':'建议补充确认')}</span></div></section>`:`<section class="cr-panel cr-insight-panel is-pending"><div class="cr-insight-panel-head"><div><span class="cr-section-kicker">快速预审</span><h3>暂未获得识别结果</h3><p>返回工作台重新上传或更换合同正文后，系统会自动提取类型和当事方建议。</p></div></div></section>`;return`<div class="cr-stage-layout"><main class="cr-stage-main">${pageHeading('步骤 02','定义审核策略','保留专业审核控制，同时让合同原件的快速预审结果成为可确认的建议。')}${fillSection}${suggestion}
  <section class="cr-panel cr-form-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">基础信息</span><h3>合同与审核立场</h3></div></div><div class="cr-form-grid"><label><span>审核立场主体</span><select class="cr-select" id="reviewPositionSelect"><option value="">请选择识别主体</option>${partyOptions.map(([name,label])=>`<option value="${escapeHtml(name)}" ${value.reviewPositionCompany===name?'selected':''}>${escapeHtml(label)}</option>`).join('')}<option value="__custom__" ${value.reviewPositionCompany&&!partyOptions.some(([name])=>name===value.reviewPositionCompany)?'selected':''}>自定义填写</option></select><input class="cr-input cr-input-subtle" id="reviewPosition" maxlength="200" value="${escapeHtml(value.reviewPositionCompany)}" placeholder="也可以直接填写主体名称"><small>可反复切换快速预审识别的主体；也可以自定义填写。</small></label><label><span>合同类型</span><select class="cr-select" id="contractType">${typeOptions.map(item=>`<option ${displayValue.contractType===item?'selected':''}>${escapeHtml(item)}</option>`).join('')}</select><small>可采纳模型类型，也可改选民法典有名合同类型或自定义类型。</small></label></div></section>
  <section class="cr-panel cr-form-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">审核范围</span><h3>选择分析维度</h3><p>至少保留一个维度。</p></div></div><div class="cr-choice-grid cr-scope-grid">${[['legal','法律风险','效力、责任边界、解除和争议解决'],['business','商业风险','价格、付款、验收和履约条件'],['compliance','合规风险','授权、数据、监管和内部制度']].map(item=>`<label class="cr-choice-card"><input type="checkbox" name="reviewScope" value="${item[0]}" ${value.scopes[item[0] as keyof ReviewScopes]?'checked':''}><span><b>${item[1]}</b><small>${item[2]}</small></span></label>`).join('')}</div></section>
  <section class="cr-panel cr-form-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">审查深度</span><h3>选择输出强度</h3></div></div><div class="cr-choice-grid cr-depth-grid"><label class="cr-choice-card"><input type="radio" name="reviewDepth" value="standard" ${value.reviewDepth==='standard'?'checked':''}><span><b>标准审查</b><small>聚焦核心问题，适合常规合同快速把关。</small></span></label><label class="cr-choice-card"><input type="radio" name="reviewDepth" value="deep" ${value.reviewDepth==='deep'?'checked':''}><span><b>深度审查</b><small>加强条款联动、履约场景和潜在争议分析。</small></span><em>推荐</em></label></div></section>
  <section class="cr-panel cr-form-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">重点关注</span><h3>本次审核优先事项</h3></div></div><div class="cr-chip-grid">${FOCUS_OPTIONS.map(item=>`<label><input type="checkbox" name="reviewFocus" value="${item}" ${value.reviewFocus.includes(item)?'checked':''}><span>${item}</span></label>`).join('')}</div></section></main>
  <aside class="cr-stage-aside"><section class="cr-panel cr-sticky-card"><span class="cr-section-kicker">策略说明</span><h3>结果会如何变化</h3><p>系统先判断审核主体在合同中的角色，再结合合同类型、分析维度和重点事项输出风险。</p><div class="cr-note-list"><span>立场决定风险判断方向</span><span>范围决定意见分类</span><span>重点事项影响优先级</span></div></section></aside></div><div class="cr-stage-footer"><span id="reviewMessage">设置将用于本次智能审查</span><div><button class="cr-button cr-button-quiet" data-review-route="materials">返回材料</button><button class="cr-button cr-button-primary" id="saveReviewSettings">保存并继续</button></div></div>`}

function analysis(main:CloudFile|null,files:ReviewFiles,value:Preferences,review:LegalReviewRun|null):string{return`<div class="cr-stage-layout"><main class="cr-stage-main">${pageHeading('步骤 03','启动智能审查','系统将通读合同正文和可读取附件，识别风险并生成可下载的 Word 审阅批注版。')}
  <section class="cr-panel cr-analysis-card"><div class="cr-analysis-top"><div><span class="cr-section-kicker">审查任务</span><h3>${escapeHtml(main?.originalName??'尚未选择合同正文')}</h3><p>${files.attachmentFiles.length} 份附件 · ${escapeHtml(value.contractType)} · ${value.reviewDepth==='deep'?'深度审查':'标准审查'}</p></div><span class="cr-readiness ${main?'is-ready':''}">${main?'准备就绪':'缺少正文'}</span></div><div class="cr-analysis-pipeline" id="analysisPipeline">${[['文件解析','读取正文结构、标题和条款内容'],['上下文理解','识别合同主体、交易关系与附件关联'],['风险分析','执行法律、商业与合规多维审查'],['意见定位','将修改建议定位至 Word 原文'],['成果生成','生成风险清单和 Word 批注版']].map((item,index)=>`<div data-analysis-phase="${index}"><span>${String(index+1).padStart(2,'0')}</span><p><b>${item[0]}</b><small>${item[1]}</small></p><em>等待</em></div>`).join('')}</div><div class="cr-analysis-action"><div><b>预计 1–5 分钟</b><span>审查期间请保持页面打开</span></div><button class="cr-button cr-button-primary cr-button-large" id="startReview" ${main?'':'disabled'}>开始智能审查</button></div></section>${review?.status==='failed'?`<section class="cr-inline-alert is-error"><div><b>上次审核未完成</b><p>${escapeHtml(review.error||'请检查文件和 AI 服务配置后重试。')}</p></div></section>`:''}</main>
  <aside class="cr-stage-aside"><section class="cr-panel cr-sticky-card"><span class="cr-section-kicker">本次设置</span><h3>审查范围</h3><dl class="cr-summary-list"><div><dt>审核立场</dt><dd>${escapeHtml(value.reviewPositionCompany)}</dd></div><div><dt>合同类型</dt><dd>${escapeHtml(value.contractType)}</dd></div><div><dt>分析维度</dt><dd>${Object.values(value.scopes).filter(Boolean).length} 项</dd></div><div><dt>重点事项</dt><dd>${value.reviewFocus.length} 项</dd></div></dl><button class="cr-button cr-button-quiet cr-button-block" data-review-route="settings">任务默认方案</button></section></aside></div><div class="cr-stage-footer"><span id="reviewMessage">${main?'材料和设置已就绪':'请返回材料准备并选择合同正文'}</span><button class="cr-button cr-button-quiet" data-review-route="materials">上一步</button></div>`}

function analysisWithProgress(main:CloudFile|null,files:ReviewFiles,value:Preferences,review:LegalReviewRun|null):string{
  const page=analysis(main,files,value,review);
  if(review?.status!=='in_progress')return page;
  const progress=Math.max(5,Math.min(95,Math.round(review.reviewProgress??50)));
  const phase=visibleWorkbenchReviewPhase(review.reviewPhase);
  const runningHint=phase?`审核已在后台运行 · ${escapeHtml(phase)} · 可离开页面后返回工作台查看进度`:'审核已在后台运行 · 可离开页面后返回工作台查看进度';
  const footerHint=phase?`审核中 ${progress}% · ${escapeHtml(phase)}`:`审核中 ${progress}%`;
  return page
    .replace('审查期间请保持页面打开',runningHint)
    .replace('id="startReview" >开始智能审查</button>',`id="startReview" disabled>审核中 ${progress}%</button>`)
    .replace('材料和设置已就绪',footerHint);
}

function normalizeResultText(value:string):string{return value.replace(/\s+/gu,'').replace(/[“”‘’]/gu,'').trim()}

/**
 * 为每条风险在正文中选取唯一、匹配度最高的行索引（用于锚点与滚动定位）。
 */
function resolveRiskAnchorLineIndexes(lines:string[],risks:LegalRisk[]):Map<string,number>{
  const lineByRisk=new Map<string,number>();
  risks.forEach(risk=>{
    const original=normalizeResultText(risk.originalText||'');
    if(!original)return;
    let bestIndex=-1;
    let bestScore=0;
    lines.forEach((line,index)=>{
      const value=line.trim();
      if(!value)return;
      const normalized=normalizeResultText(value);
      if(!normalized.includes(original)&&!original.includes(normalized))return;
      const score=Math.min(normalized.length,original.length);
      if(score>bestScore){bestScore=score;bestIndex=index}
    });
    if(bestIndex>=0)lineByRisk.set(risk.id,bestIndex);
  });
  return lineByRisk;
}

/**
 * 将正文锚点元素的顶边对齐到滚动容器可见区域顶部。
 */
function scrollClauseToScrollerTop(target:HTMLElement,scroller:HTMLElement):void{
  const targetTop=target.getBoundingClientRect().top;
  const scrollerTop=scroller.getBoundingClientRect().top;
  const nextScrollTop=scroller.scrollTop+(targetTop-scrollerTop);
  scroller.scrollTo({top:Math.max(0,nextScrollTop),behavior:'smooth'});
}

/**
 * 列出风险清单分页中当前可见的风险行（受筛选与搜索影响）。
 */
function visibleRiskRows(root:HTMLElement):HTMLElement[]{
  return [...root.querySelectorAll<HTMLElement>('[data-risk-select]')].filter(item=>!item.hidden);
}

/**
 * 采纳/不采纳后自动选中下一条风险，并将左侧清单滚动到该行顶部，便于连续复核。
 */
function focusNextRiskAfterDecision(
  root:HTMLElement,
  currentRiskId:string,
  filter:string,
  previousVisibleIndex:number,
  selectRisk:(riskId:string)=>void,
):void{
  const list=root.querySelector<HTMLElement>('#riskList');
  const visibleAfter=visibleRiskRows(root);
  let nextId='';
  if(filter==='pending'){
    nextId=visibleAfter[previousVisibleIndex]?.dataset.riskSelect
      ??visibleAfter[Math.max(0,previousVisibleIndex-1)]?.dataset.riskSelect
      ??'';
  }else{
    const pos=visibleAfter.findIndex(item=>item.dataset.riskSelect===currentRiskId);
    if(pos>=0)nextId=visibleAfter[pos+1]?.dataset.riskSelect??visibleAfter[pos]?.dataset.riskSelect??'';
    else nextId=visibleAfter[0]?.dataset.riskSelect??'';
  }
  if(!nextId)return;
  selectRisk(nextId);
  const row=root.querySelector<HTMLElement>(`[data-risk-select="${CSS.escape(nextId)}"]`);
  if(list&&row)scrollClauseToScrollerTop(row,list);
}

type ReviewBasisPayload={
  statutes:Array<{title:string;clause:string}>;
  compliancePolicies:Array<{fileName:string;clause:string}>;
  reviewReason?:string;
};

function closeReviewBasisModal(root:HTMLElement):void{
  root.querySelector('#legalReviewBasisModal')?.remove();
}

/**
 * 展示当前风险意见所依据的法律法规与合规制度。
 */
function openReviewBasisModal(root:HTMLElement,risk:LegalRisk,basis:ReviewBasisPayload):void{
  closeReviewBasisModal(root);
  const statuteItems=basis.statutes.length
    ?basis.statutes.map(item=>`<li><b>${escapeHtml(item.title)}</b><p class="cr-basis-clause">${escapeHtml(item.clause)}</p></li>`).join('')
    :'<li><b>暂未匹配到条文库中的具体条款</b><p>请查看本条「风险说明」或下方「审查理由摘要」；重新智能审查后，系统会尽量写入对应法条原文。</p></li>';
  const policySection=basis.compliancePolicies.length
    ?`<section class="cr-basis-section"><h4>合规管理制度</h4><ul class="cr-basis-list">${basis.compliancePolicies.map(item=>`<li><b>${escapeHtml(item.fileName.replace(/\.(docx?|pdf|txt)$/i,''))}</b><p class="cr-basis-clause">${escapeHtml(item.clause)}</p></li>`).join('')}</ul></section>`
    :'';
  const reasonSection=basis.reviewReason
    ?`<section class="cr-basis-section"><h4>审查理由摘要</h4><ul class="cr-basis-list"><li><p class="cr-basis-clause">${escapeHtml(basis.reviewReason)}</p></li></ul></section>`
    :'';
  root.insertAdjacentHTML('beforeend',`<div class="modal-overlay show" id="legalReviewBasisModal" role="dialog" aria-modal="true" aria-labelledby="legalReviewBasisTitle"><div class="modal cr-basis-modal"><div class="modal-header"><div><h3 id="legalReviewBasisTitle">审核依据</h3><p class="modal-subtitle">${escapeHtml(risk.category)} · ${escapeHtml(typeLabel(risk.reviewType??'legal'))}</p></div><button type="button" class="modal-close" data-close-basis aria-label="关闭">×</button></div><div class="modal-body cr-basis-modal-body"><p class="cr-basis-lead">以下由法务大模型根据本条意见生成：法律、行政法规、地方性法规、规章、司法解释及合规制度对应条文要点（非全文）。</p><section class="cr-basis-section"><h4>法律、行政法规、地方性法规、规章及司法解释</h4><ul class="cr-basis-list">${statuteItems}</ul></section>${policySection}${reasonSection}</div><div class="modal-footer"><button type="button" class="primary-button" data-close-basis>关闭</button></div></div></div>`);
  const overlay=root.querySelector('#legalReviewBasisModal');
  overlay?.querySelectorAll('[data-close-basis]').forEach(button=>button.addEventListener('click',()=>closeReviewBasisModal(root)));
  overlay?.addEventListener('click',event=>{if(event.target===overlay)closeReviewBasisModal(root)});
}

function renderResultDocumentHtml(text:string,risks:LegalRisk[]):string{
  const source=text.trim();
  if(!source)return risks.map((risk,index)=>`<section class="cr-result-document-clause${index===0?' is-highlighted':''}" data-risk-clause="${escapeHtml(risk.id)}"><b>${escapeHtml(risk.category)}</b><p>${escapeHtml(risk.originalText||risk.description)}</p></section>`).join('');
  const lines=source.split(/\r?\n/);
  const anchorLineByRisk=resolveRiskAnchorLineIndexes(lines,risks);
  const matched=new Set<string>();
  const sections=lines.map((line,index)=>{
    const value=line.trim();
    if(!value)return '<div class="cr-result-document-spacer" aria-hidden="true"></div>';
    const risk=risks.find(item=>anchorLineByRisk.get(item.id)===index);
    if(risk)matched.add(risk.id);
    const heading=/^(第[一二三四五六七八九十百]+条|[一二三四五六七八九十百]+[、.]|[一二三四五六七八九十百]+\s)/u.test(value);
    return `<p class="cr-result-document-line${risk?' is-risk-line':''}${heading?' is-heading':''}"${risk?` data-risk-clause="${escapeHtml(risk.id)}"`:''}>${escapeHtml(value)}</p>`;
  }).join('');
  const unmatched=risks.filter(risk=>!matched.has(risk.id)).map(risk=>`<section class="cr-result-document-clause cr-result-document-unmatched" data-risk-clause="${escapeHtml(risk.id)}"><b>${escapeHtml(risk.category)}</b><p>${escapeHtml(risk.originalText||risk.description)}</p></section>`).join('');
  return sections+unmatched;
}
function riskReviewStateMarkup(risk:LegalRisk):string{
  if(risk.userDecision==='accepted')return'<span class="cr-result-risk-state is-done is-accepted">已采纳</span>';
  if(risk.userDecision==='dismissed')return'<span class="cr-result-risk-state is-done is-dismissed">不采纳</span>';
  return'<span class="cr-result-risk-state">待确认</span>';
}

/**
 * 更新风险清单行上的复核状态展示。
 */
function applyRiskRowDecisionUi(row:HTMLElement,decision:'accepted'|'dismissed'):void{
  const state=row.querySelector<HTMLElement>('.cr-result-risk-state');
  if(!state)return;
  state.textContent=decision==='accepted'?'已采纳':'不采纳';
  state.className=`cr-result-risk-state is-done ${decision==='accepted'?'is-accepted':'is-dismissed'}`;
}

/**
 * 渲染审核结果页左侧单条风险清单行（含多选勾选框）。
 */
function renderRiskListRow(risk:LegalRisk,index:number,selected:boolean):string{
  return`<div class="cr-result-risk-row${selected?' is-selected':''}" role="button" tabindex="0" data-risk-select="${escapeHtml(risk.id)}" data-risk-text="${escapeHtml(`${risk.category} ${risk.description} ${risk.suggestedRevision}`.toLowerCase())}"><input type="checkbox" class="cr-result-risk-check" data-risk-check="${escapeHtml(risk.id)}" aria-label="选择：${escapeHtml(risk.category)}"><span class="cr-result-risk-index">${String(index+1).padStart(2,'0')}</span><span class="cr-result-risk-title"><b>${escapeHtml(risk.category)}</b><small>${escapeHtml(risk.description)}</small></span><span class="cr-result-risk-clause">${escapeHtml(risk.reviewType?typeLabel(risk.reviewType):'综合审查')}</span>${riskReviewStateMarkup(risk)}</div>`;
}
/** 下载批注版：未完成复核时提示，并按当前结论重新生成（排除不采纳项）。 */
async function downloadLegalReviewAnnotatedExport(taskId:string,projectId:string,userReviewCompleted:boolean):Promise<void>{
  if(!userReviewCompleted){
    const proceed=window.confirm('您尚未点击「完成复核」。建议先在左侧逐条确认各风险意见后，再下载批注版。\n\n是否仍直接下载批注版？');
    if(!proceed)return;
  }
  const payload=await api.post<{annotatedFileId:string}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/annotated-export`),{});
  if(payload.annotatedFileId)await downloadCloudFile(payload.annotatedFileId);
}
/** 下载修订模式修改版：仅完成复核后可生成。 */
async function downloadLegalReviewRevisedExport(taskId:string,projectId:string):Promise<void>{
  const payload=await api.post<{revisedFileId:string}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/revised-export`),{},{timeoutMs:600000});
  if(payload.revisedFileId)await downloadCloudFile(payload.revisedFileId);
}
const riskResultAnnotatePartsMarkup='<div class="cr-result-annotate-parts"><span class="cr-result-annotate-parts-label">批注内容构成</span><label class="cr-result-annotate-part"><input type="checkbox" data-annotate-include-description checked>风险说明</label><label class="cr-result-annotate-part"><input type="checkbox" data-annotate-include-suggestion checked>修改意见</label></div>';
function readRiskAnnotateParts(root:HTMLElement):{annotateIncludeDescription:boolean;annotateIncludeSuggestion:boolean}{
  return{
    annotateIncludeDescription:root.querySelector<HTMLInputElement>('[data-annotate-include-description]')?.checked??true,
    annotateIncludeSuggestion:root.querySelector<HTMLInputElement>('[data-annotate-include-suggestion]')?.checked??true,
  };
}
/** 写作指导中间栏滚动区：用视口高度锁定列表，避免父级 overflow:hidden 裁切后无法滚动。 */
function syncWritingGuidanceScrollLayout(root:HTMLElement):void{
  const page=root.querySelector<HTMLElement>('.cr-result-page.is-writing-view');
  if(!page)return;
  const workspace=page.querySelector<HTMLElement>('.cr-writing-workspace');
  const list=page.querySelector<HTMLElement>('.cr-writing-guidance-list');
  const outline=page.querySelector<HTMLElement>('.cr-writing-outline');
  if(!workspace||!list)return;
  const top=workspace.getBoundingClientRect().top;
  const bottomGap=8;
  const height=Math.max(240,Math.floor(window.innerHeight-top-bottomGap));
  workspace.style.height=`${height}px`;
  workspace.style.maxHeight=`${height}px`;
  list.style.height=`${height}px`;
  list.style.maxHeight=`${height}px`;
  list.style.overflowY='auto';
  if(outline){
    outline.style.maxHeight=`${height}px`;
    outline.style.overflowY='auto';
  }
}

/**
 * 绑定写作指导视图的高度同步（切换 Tab、窗口缩放时重算）。
 */
function bindWritingGuidanceScrollLayout(root:HTMLElement):void{
  const run=()=>syncWritingGuidanceScrollLayout(root);
  run();
  window.requestAnimationFrame(run);
  window.setTimeout(run,0);
  if(typeof ResizeObserver!=='undefined'){
    const page=root.querySelector<HTMLElement>('.cr-result-page');
    if(page){
      const observer=new ResizeObserver(()=>run());
      observer.observe(page);
    }
  }
  window.addEventListener('resize',run,{passive:true});
}

/**
 * 议案整篇评估与局部写作卡片用不同版式。
 */
function isOverallWritingItem(item:WritingGuidanceItem):boolean{
  return /议题整体评估|整体评估/u.test(item.category||'');
}

/**
 * 把成文评估按空行拆成段落。
 */
function writingEssayHtml(text:string):string{
  const parts=text.split(/\n{2,}/u).map(part=>part.trim()).filter(Boolean);
  const chunks=parts.length?parts:[text.trim()].filter(Boolean);
  return `<div class="cr-writing-essay">${chunks.map(part=>`<p>${escapeHtml(part)}</p>`).join('')}</div>`;
}

/**
 * 建议按条展示。模型用「一、」「二、」或换行分条时，每条单独成段。
 */
function writingSuggestionHtml(text:string):string{
  const normalized=text.replace(/\s*(?=[一二三四五六七八九十]+、)/gu,'\n').replace(/\s*(?=\d+[.、])/gu,'\n');
  const items=normalized.split(/\n+/u).map(part=>part.trim()).filter(Boolean);
  if(items.length<=1)return writingEssayHtml(text);
  return `<ol class="cr-writing-suggest-list">${items.map(item=>`<li>${escapeHtml(item.replace(/^[一二三四五六七八九十]+、\s*/u,'').replace(/^\d+[.、]\s*/u,''))}</li>`).join('')}</ol>`;
}

/**
 * 渲染非合同「写作与体例指导」专区（不混入风险清单）。
 */
function renderWritingGuidancePanel(
  items:WritingGuidanceItem[],
  _familyLabel:string,
):string{
  if(!items.length)return'';
  const localTotal=items.filter(item=>!isOverallWritingItem(item)).length;
  let localIndex=0;
  const outline=items.map((item,index)=>{
    const overall=isOverallWritingItem(item);
    const label=overall?'整体评估':item.category||item.aspect;
    const mark=overall?'评':String(++localIndex).padStart(2,'0');
    return `<button type="button" class="cr-writing-outline-item${index===0?' is-current':''}" data-writing-select="${escapeHtml(item.id)}"><span>${mark}</span><b>${escapeHtml(label)}</b></button>`;
  }).join('');
  localIndex=0;
  const rows=items.map((item,index)=>{
    const overall=isOverallWritingItem(item);
    const title=overall?'议题整体评估':(item.category||item.aspect);
    if(overall){
      const gaps=(item.shortcomings||'').trim();
      const copyText=`${item.description}\n\n${gaps}\n\n${item.suggestedRevision}`.trim();
      const gapsHtml=gaps?`<section class="cr-writing-gaps"><h4><span class="is-amber">提示</span>仍需注意的不足</h4>${writingEssayHtml(gaps)}</section>`:'';
      return `<article class="cr-writing-card cr-writing-overall${index===0?' is-current':''}" data-writing-id="${escapeHtml(item.id)}"><header class="cr-writing-card-head"><div><span class="cr-writing-step">整体评估</span><h3>${escapeHtml(title)}</h3></div><span class="cr-writing-aspect">${escapeHtml(item.aspect)}</span></header>${writingEssayHtml(item.description)}${gapsHtml}<section class="cr-writing-section"><h4><span class="is-teal">建议</span>给起草和上会</h4><div class="cr-writing-suggest">${writingSuggestionHtml(item.suggestedRevision)}</div><pre class="cr-writing-copy-source" hidden aria-hidden="true">${escapeHtml(copyText)}</pre><div class="cr-writing-copy-row"><button type="button" class="cr-button cr-button-text" data-copy-writing="">复制评估与建议</button></div></section></article>`;
    }
    localIndex+=1;
    const copyText=item.suggestedRevision||item.description;
    return `<article class="cr-writing-card${index===0?' is-current':''}" data-writing-id="${escapeHtml(item.id)}"><header class="cr-writing-card-head"><div><span class="cr-writing-step">问题 ${String(localIndex).padStart(2,'0')} / ${String(localTotal).padStart(2,'0')}</span><h3>${escapeHtml(title)}</h3></div><span class="cr-writing-aspect">${escapeHtml(item.aspect)}</span></header>${item.originalText?`<section class="cr-writing-section"><h4><span>原文</span>现有表述</h4><p class="cr-writing-original">${escapeHtml(item.originalText)}</p></section>`:''}<section class="cr-writing-section"><h4><span class="is-amber">说明</span>为什么需要调整</h4><p class="cr-writing-desc">${escapeHtml(item.description)}</p></section><section class="cr-writing-section"><h4><span class="is-teal">建议修改</span>可直接采用</h4><div class="cr-writing-suggest">${escapeHtml(item.suggestedRevision)}</div><div class="cr-writing-copy-row"><button type="button" class="cr-button cr-button-text" data-copy-writing="${escapeHtml(copyText)}">复制建议</button></div></section></article>`;
  }).join('');
  const overallCount=items.length-localTotal;
  const overallOnly=overallCount>0&&localTotal===0;
  const summary=overallCount?`${overallCount} 篇评估${localTotal?` · ${localTotal} 条`:''}`:`${items.length} 条`;
  const head=overallOnly?'':`<header class="cr-writing-guidance-head"><div><h2>写作指导</h2></div><p><b>${escapeHtml(summary)}</b></p></header>`;
  const nav=overallOnly?'':`<nav class="cr-writing-outline" aria-label="本页问题"><p>本页问题</p>${outline}</nav>`;
  return `<section class="cr-writing-guidance-panel${overallOnly?' is-overall-only':''}" aria-label="写作指导" hidden>${head}<div class="cr-writing-workspace">${nav}<div class="cr-writing-guidance-list">${rows}</div></div></section>`;
}

function risksPage(risks:LegalRisk[],review:LegalReviewRun|null,writingGuidance:WritingGuidanceItem[]=[]):string{
  const pending=risks.filter(risk=>!risk.userDecision).length;
  const done=risks.length-pending;
  const userReviewCompleted=Boolean(review?.userReviewCompletedAt);
  const first=risks[0];
  const showWriting=isNonContractDocumentSeriesLabel(review?.contractType)
    ||Boolean(review?.nonContractDocumentFamily)
    ||writingGuidance.length>0;
  const guidance=showWriting
    ?renderWritingGuidancePanel(
      writingGuidance.length?writingGuidance:(review?.writingGuidance??[]),
      String(review?.nonContractDocumentFamilyLabel??''),
    )
    :'';
  const riskRows=risks.length?risks.map((risk,index)=>renderRiskListRow(risk,index,index===0)).join(''):'<div class="cr-empty-large"><b>暂无风险意见</b><span>请先完成智能审查，或调整材料与审核设置后重试。</span><button class="cr-button cr-button-primary" data-review-route="analysis">前往智能审查</button></div>';
  const documentBody=risks.map((risk,index)=>`<section class="cr-result-document-clause${index===0?' is-highlighted':''}" data-risk-clause="${escapeHtml(risk.id)}"><b>${escapeHtml(risk.category)}</b><p>${escapeHtml(risk.originalText||risk.description)}</p></section>`).join('');
  const reviewReady=reviewFinishedSuccessfully(review);
  const failureBanner=review?.status==='failed'&&review.error&&!isOpinionCountQuotaError(review)?`<section class="cr-inline-alert is-error cr-result-failure-banner"><div><b>本次审核未完成</b><p>${escapeHtml(review.error)}</p></div></section>`:'';
  const detail=first?`<div class="cr-result-detail-head"><div class="cr-result-detail-head-left"><span class="cr-result-detail-index">01</span><h2 class="cr-result-detail-title"><span data-risk-detail-title>${escapeHtml(first.category)}</span><span class="cr-result-detail-type" data-risk-detail-meta>${escapeHtml(first.reviewType?typeLabel(first.reviewType):'综合审查')}</span></h2></div></div><div class="cr-result-detail-scroll"><section class="cr-result-detail-section"><h3>风险说明</h3><p data-risk-detail-description>${escapeHtml(first.description)}</p></section><section class="cr-result-detail-section"><h3>修改意见</h3><textarea data-risk-detail-suggestion placeholder="请写明操作，如：应增加… / 应修改为… / 应删除…">${escapeHtml(first.suggestedRevision||'请结合本项目交易安排补充具体修改表述。')}</textarea>${riskResultAnnotatePartsMarkup}</section><section class="cr-result-detail-section"><h3>参考依据</h3><div class="cr-result-basis"><div class="cr-result-basis-info"><span class="cr-result-basis-tag">法律依据</span><span class="cr-result-basis-desc">法律、行政法规、地方性法规、规章、司法解释及合规制度</span></div><button type="button" class="cr-button cr-button-text" data-risk-view-basis>查看依据</button></div></section><div class="cr-result-detail-actions"><button type="button" class="cr-button cr-button-primary" data-risk-action="accept">采纳建议</button><button type="button" class="cr-button cr-button-secondary" data-risk-action="edit">编辑</button><button type="button" class="cr-button cr-button-secondary" data-risk-action="dismiss">不采纳</button></div></div>`:'<div class="cr-empty-large"><b>请选择风险意见</b></div>';
  const viewSwitch=guidance?`<div class="cr-result-view-switch" role="tablist" aria-label="结果视图"><button type="button" class="is-active" data-result-view="opinions">审核意见</button><button type="button" data-result-view="writing">写作指导</button></div>`:'';
  return`<div class="cr-result-page is-list-layout">${failureBanner}<header class="cr-result-header"><div class="cr-result-header-left"><h1 class="cr-result-header-title">${escapeHtml(review?.sourceFileName||'合同审核复核')}</h1>${viewSwitch}</div><div class="cr-result-header-actions"><button class="cr-button cr-button-primary" type="button" data-complete-user-review ${reviewReady&&!userReviewCompleted?'':'disabled'}>${userReviewCompleted?'已完成复核':'完成复核'}</button><button class="cr-button cr-button-secondary" type="button" data-legal-review-annotated-export ${review?.annotatedFileId?'':''} ${userReviewCompleted?'data-user-review-completed="true"':''}>下载批注版</button><button class="cr-button cr-button-secondary" type="button" data-legal-review-revised-export ${userReviewCompleted?'':'disabled'} ${userReviewCompleted?'data-user-review-completed="true"':''}>下载修改版</button></div></header><div class="cr-result-layout"><section class="cr-result-list-pane"><div class="cr-result-list-toolbar"><div class="cr-result-filters"><button class="is-active" data-risk-filter="all">全部 ${risks.length}</button><button data-risk-filter="pending">待确认 ${pending}</button><button data-risk-filter="done">已处理 ${done}</button></div><input class="cr-result-search" id="riskSearch" placeholder="搜索条款或风险"></div><div class="cr-result-batch" id="riskBatchBar"><label class="cr-result-batch-all"><input type="checkbox" id="riskSelectAllVisible" aria-label="全选当前列表">全选当前</label><span class="cr-result-batch-summary" id="riskBatchSummary">已选 0 条</span><button type="button" class="cr-button cr-button-secondary cr-result-batch-btn" data-risk-batch-accept disabled>批量采纳</button><button type="button" class="cr-button cr-button-secondary cr-result-batch-btn" data-risk-batch-dismiss disabled>批量不采纳</button></div><div class="cr-result-risk-list" id="riskList">${riskRows}</div></section><section class="cr-result-document-pane"><div class="cr-result-document-top"><span>合同正文</span><b>${escapeHtml(review?.sourceFileName||'合同原文')}</b></div><div class="cr-result-document-scroll"><div class="cr-result-document-title">${escapeHtml((review?.sourceFileName||'合同审核结果').replace(/\.[^.]+$/,''))}</div><div class="cr-result-document-body cr-result-document-list" data-result-document>${documentBody||'<div class="cr-empty-large"><b>正在加载合同正文…</b></div>'}</div></div></section><aside class="cr-result-detail-pane">${detail}</aside></div>${guidance}</div>`;
}

function deliverables(files:ReviewFiles,review:LegalReviewRun|null,risks:LegalRisk[]):string{
  const revisedFiles=files.revisedFiles??[];
  return`<div class="cr-stage-layout"><main class="cr-stage-main">${pageHeading('步骤 02','交付并归档审核成果','下载 Word 批注版用于协商，下载修订模式修改版用于改稿；所有成果同时保留在个人空间任务目录中。')}${review?.status==='completed'?`<section class="cr-completion-banner"><div><span>审核流程已完成</span><h3>${escapeHtml(review.sourceFileName)}</h3><p>${review.riskCount} 条审核意见 · ${review.annotatedCommentCount} 处 Word 原文批注 · ${formatDate(review.completedAt)}</p></div><strong>${review.riskCount}</strong></section>`:'<section class="cr-inline-alert"><div><b>尚未生成审核成果</b><p>请在工作台完成合同审查后，再回到本页下载和归档。</p></div><button class="cr-button cr-button-primary" data-review-route="overview">返回工作台</button></section>'}<section class="cr-panel cr-deliverable-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">可交付文件</span><h3>Word 审阅批注版</h3><p>保留合同原文，并在对应条款位置加入法务审核意见；标记为「不采纳」的意见不会写入批注版。</p></div></div><div class="cr-output-list">${files.annotatedFiles.length?files.annotatedFiles.map(file=>`<article><img src="${iconFor(file.originalName)}" alt=""><div><b>${escapeHtml(file.originalName)}</b><span>${formatSize(file.sizeBytes)} · ${formatDate(file.createdAt)}</span></div><button class="cr-button cr-button-secondary" type="button" data-legal-review-annotated-export>下载文件</button></article>`).join(''):'<div class="cr-empty-compact"><b>暂无 Word 批注版</b><span>PDF 和 TXT 正文只生成网页风险清单。</span></div>'}</div></section><section class="cr-panel cr-deliverable-panel"><div class="cr-panel-heading"><div><span class="cr-section-kicker">可交付文件</span><h3>Word 修订模式修改版</h3><p>除明确标记「不采纳」外，其余审阅意见会落实为正文修改，并在<strong>原 Word 原稿</strong>上以修订模式留下增删痕迹；未完成复核前不可下载。</p></div></div><div class="cr-output-list">${revisedFiles.length?revisedFiles.map(file=>`<article><img src="${iconFor(file.originalName)}" alt=""><div><b>${escapeHtml(file.originalName)}</b><span>${formatSize(file.sizeBytes)} · ${formatDate(file.createdAt)}</span></div><button class="cr-button cr-button-secondary" type="button" data-legal-review-revised-export>下载文件</button></article>`).join(''):`<div class="cr-empty-compact"><b>${review?.userReviewCompletedAt?'暂无 Word 修改版':'完成复核后生成'}</b><span>修改版在原 Word 原稿上写入修订痕迹，保留原件版式；PDF/图片/TXT 暂不支持。</span></div>`}</div></section></main><aside class="cr-stage-aside"><section class="cr-panel cr-sticky-card"><span class="cr-section-kicker">归档位置</span><h3>个人空间 · 仅本人可见</h3><div class="cr-storage-path"><span>个人空间</span><span>任务</span><span>合同文件审核</span><span>成果</span><span>阅读版 / 修改版</span></div><button class="cr-button cr-button-primary cr-button-block" data-open-personal-space>打开个人空间</button></section><section class="cr-panel cr-check-card"><h3>成果清单</h3><div class="cr-check-row is-ready"><i></i><p><b>风险清单</b><small>${risks.length} 条结构化意见</small></p></div><div class="cr-check-row ${files.annotatedFiles.length?'is-ready':''}"><i></i><p><b>Word 批注版</b><small>${files.annotatedFiles.length?`${files.annotatedFiles.length} 份可下载`:'当前没有可用文件'}</small></p></div><div class="cr-check-row ${revisedFiles.length?'is-ready':''}"><i></i><p><b>Word 修改版</b><small>${revisedFiles.length?`${revisedFiles.length} 份可下载`:'完成复核后生成'}</small></p></div></section></aside></div>`;
}

function archiveRiskLabel(risks:LegalRisk[]):string{if(risks.some(item=>item.severity==='high'))return'高';if(risks.some(item=>item.severity==='medium'))return'中';return risks.length?'低':'—'}
function archiveResultLabel(review:LegalReviewRun|null):string{return review?.status==='completed'?'审核完成':review?.status==='failed'?'审核失败':'未完成'}
function archiveAllFiles(snapshot:ProjectSnapshot):CloudFile[]{return[snapshot.main,...snapshot.files.attachmentFiles,...snapshot.files.annotatedFiles,...(snapshot.files.revisedFiles??[]),...(snapshot.files.previewFiles??[])].filter((item):item is CloudFile=>Boolean(item))}
function archiveFileLabel(file:CloudFile,snapshot:ProjectSnapshot):string{if(file.id===snapshot.main?.id)return'原始合同';if(snapshot.files.attachmentFiles.some(item=>item.id===file.id))return'附件';if(snapshot.files.annotatedFiles.some(item=>item.id===file.id))return'Word批注版';if((snapshot.files.revisedFiles??[]).some(item=>item.id===file.id))return'Word修改版';return'预览成果'}
function archiveProjectPath(taskName:string,project:LegalReviewProject):string{return`个人空间 / ${taskName} / 归档 / ${project.name}`}
function archiveLedgerPage(taskName:string,snapshots:ProjectSnapshot[],selectedId:string):string{
  const selected=snapshots.find(item=>item.project.id===selectedId)??snapshots[0]??null;
  const rows=snapshots.map((snapshot,index)=>{const project=snapshot.project;const archivedAt=project.archivedAt??project.updatedAt;return`<tr class="cr-archive-row${snapshot.project.id===selected?.project.id?' is-selected':''}" data-archive-project="${escapeHtml(project.id)}" data-archive-search="${escapeHtml(`${project.name} ${snapshot.main?.originalName??''} ${project.archivedByName??''}`.toLowerCase())}" data-archive-date="${escapeHtml(archivedAt)}"><td><input type="checkbox" aria-label="选择${escapeHtml(project.name)}"></td><td><b>${escapeHtml(project.name||`项目${index+1}`)}</b></td><td>${escapeHtml(snapshot.main?.originalName??'—')}</td><td><span class="cr-archive-status is-done">${archiveResultLabel(snapshot.review)}</span></td><td><span class="cr-archive-risk is-${archiveRiskLabel(snapshot.risks)==='高'?'high':archiveRiskLabel(snapshot.risks)==='中'?'medium':'low'}">${archiveRiskLabel(snapshot.risks)}</span></td><td>${escapeHtml(formatDate(archivedAt))}</td><td>${escapeHtml(project.archivedByName||'本人')}</td><td>${archiveAllFiles(snapshot).length} 个文件</td><td><button class="cr-archive-link" data-archive-open="${escapeHtml(project.id)}">查看成果</button><button class="cr-archive-icon-button" title="下载全部" data-archive-download="${escapeHtml(project.id)}"><span class="cr-ms-icon">&#xE896;</span></button><button class="cr-archive-icon-button" title="更多" data-archive-menu="${escapeHtml(project.id)}"><span class="cr-ms-icon">&#xE712;</span></button></td></tr>`}).join('');
  return`<section class="cr-archive-page"><div class="cr-archive-toolbar"><div><span class="cr-section-kicker">个人空间 · 归档台账</span><h1>成果归档</h1></div><div class="cr-archive-filters"><label class="cr-archive-search"><span class="cr-ms-icon">&#xE721;</span><input id="crArchiveSearch" placeholder="搜索合同或项目"></label><select id="crArchiveDateFilter"><option value="all">归档时间</option><option value="30">近30天</option><option value="365">近一年</option></select><select id="crArchiveResultFilter"><option value="all">审核结果</option><option value="completed">审核完成</option><option value="failed">审核失败</option></select></div></div><div class="cr-archive-layout"><div class="cr-archive-table-card"><div class="cr-archive-table-scroll"><table class="cr-archive-table"><thead><tr><th><input type="checkbox" aria-label="全选"></th><th>项目名称</th><th>合同正文</th><th>审核结果</th><th>风险</th><th>归档时间</th><th>归档人</th><th>成果</th><th>操作</th></tr></thead><tbody id="crArchiveTableBody">${rows||'<tr><td colspan="9"><div class="cr-archive-empty"><span class="cr-ms-icon">&#xE8B7;</span><b>还没有归档合同</b><p>在工作台完成复核后点击归档，项目会移动到个人空间的归档文件夹。</p></div></td></tr>'}</tbody></table></div><div class="cr-archive-table-footer">共 ${snapshots.length} 个归档项目</div></div>${selected?archiveDetailPanel(taskName,selected):'<aside class="cr-archive-detail"><div class="cr-archive-empty"><b>选择一个项目</b></div></aside>'}</div></section>`
}
function archiveDetailPanel(taskName:string,snapshot:ProjectSnapshot):string{const project=snapshot.project;const files=archiveAllFiles(snapshot);return`<aside class="cr-archive-detail" data-archive-detail="${escapeHtml(project.id)}"><div class="cr-archive-detail-heading"><div><span class="cr-section-kicker">成果详情</span><h2>${escapeHtml(project.name)}</h2></div><button class="cr-archive-close" title="关闭" data-archive-close><span class="cr-ms-icon">&#xE711;</span></button></div><div class="cr-archive-meta"><div><span>归档时间</span><b>${escapeHtml(formatDate(project.archivedAt??project.updatedAt))}</b></div><div><span>归档人</span><b>${escapeHtml(project.archivedByName||'本人')}</b></div><div class="cr-archive-meta-wide"><span>路径</span><b>${escapeHtml(archiveProjectPath(taskName,project))}</b></div></div><div class="cr-archive-files"><h3>成果文件（${files.length}）</h3>${files.map(file=>`<div class="cr-archive-file"><img src="${iconFor(file.originalName)}" alt=""><div><b>${archiveFileLabel(file,snapshot)}</b><span title="${escapeHtml(file.originalName)}">${escapeHtml(file.originalName)}</span><small>${formatSize(file.sizeBytes)}</small></div><button title="下载" data-archive-file-download="${escapeHtml(file.id)}"><span class="cr-ms-icon">&#xE896;</span></button></div>`).join('')}</div><button class="cr-archive-primary" data-archive-download-all="${escapeHtml(project.id)}"><span class="cr-ms-icon">&#xE896;</span>下载全部</button><div class="cr-archive-more"><span>更多操作</span><button data-archive-restore="${escapeHtml(project.id)}"><span class="cr-ms-icon">&#xE777;</span>恢复到工作台</button><button data-archive-share="${escapeHtml(project.id)}"><span class="cr-ms-icon">&#xE72D;</span>共享到团队空间</button></div></aside>`}
function bindArchiveLedger(root:HTMLElement,taskId:string,auth:AuthSnapshot,taskName:string,snapshots:ProjectSnapshot[],organizations:UserOrganization[]):void{
  let currentId=snapshots[0]?.project.id??'';
  const rerender=async()=>{await renderLegalContractReviewPage(root,auth)};
  const downloadFiles=async(files:CloudFile[])=>{for(const file of files)await downloadCloudFile(file.id)};
  const applyFilters=()=>{const query=root.querySelector<HTMLInputElement>('#crArchiveSearch')?.value.trim().toLowerCase()??'';const date=root.querySelector<HTMLSelectElement>('#crArchiveDateFilter')?.value??'all';const result=root.querySelector<HTMLSelectElement>('#crArchiveResultFilter')?.value??'all';const cutoff=date==='all'?0:Date.now()-Number(date)*86400000;root.querySelectorAll<HTMLElement>('.cr-archive-row').forEach(row=>{const snapshot=snapshots.find(item=>item.project.id===row.dataset.archiveProject);const visible=Boolean(snapshot)&&(!query||String(row.dataset.archiveSearch).includes(query))&&(!cutoff||new Date(String(row.dataset.archiveDate)).getTime()>=cutoff)&& (result==='all'||(result==='completed'&&snapshot?.review?.status==='completed')||(result==='failed'&&snapshot?.review?.status==='failed'));row.hidden=!visible})};
  root.querySelectorAll<HTMLInputElement>('#crArchiveSearch').forEach(input=>input.addEventListener('input',applyFilters));root.querySelectorAll<HTMLSelectElement>('#crArchiveDateFilter,#crArchiveResultFilter').forEach(input=>input.addEventListener('change',applyFilters));
  root.querySelectorAll<HTMLElement>('[data-archive-open]').forEach(button=>button.addEventListener('click',()=>{const id=button.dataset.archiveOpen||'';const target=snapshots.find(item=>item.project.id===id);if(!target)return;currentId=id;root.querySelectorAll<HTMLElement>('.cr-archive-row').forEach(row=>row.classList.toggle('is-selected',row.dataset.archiveProject===id));const old=root.querySelector('.cr-archive-detail');old?.replaceWith(document.createRange().createContextualFragment(archiveDetailPanel(taskName,target)));bindArchiveDetail()}));
  const bindArchiveDetail=()=>{root.querySelector('[data-archive-close]')?.addEventListener('click',()=>root.querySelector('.cr-archive-detail')?.classList.add('is-hidden'));root.querySelectorAll<HTMLButtonElement>('[data-archive-file-download]').forEach(button=>button.addEventListener('click',()=>{const id=button.dataset.archiveFileDownload;if(id)void downloadCloudFile(id)}));root.querySelector<HTMLButtonElement>('[data-archive-download-all]')?.addEventListener('click',()=>{const target=snapshots.find(item=>item.project.id===currentId);if(target)void downloadFiles(archiveAllFiles(target))});root.querySelector<HTMLButtonElement>('[data-archive-restore]')?.addEventListener('click',async()=>{if(!window.confirm('恢复后项目会回到工作台，归档台账中将不再显示。确认恢复吗？'))return;await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(currentId)}/restore`),{});await rerender()});root.querySelector<HTMLButtonElement>('[data-archive-share]')?.addEventListener('click',async()=>{const target=snapshots.find(item=>item.project.id===currentId);const active=organizations.filter(isActiveEnterpriseOrganization);if(!target||!active.length){window.alert('当前没有可共享的团队空间');return}let choice=active[0];if(active.length>1){const selected=window.prompt(`输入要共享的企业序号：\n${active.map((item,index)=>`${index+1}. ${item.name}`).join('\n')}`,'1');const index=Math.max(0,Number(selected||1)-1);choice=active[index]??active[0]}if(!window.confirm(`将“${target.project.name}”的成果共享到团队空间“${choice.name}”？个人空间原件仍会保留。`))return;await api.post(apiUrl('/api/files/share-to-enterprise'),{fileIds:archiveAllFiles(target).map(file=>file.id),enterpriseOrganizationId:choice.id});window.alert('已共享到团队空间')})};
  bindArchiveDetail();root.querySelectorAll<HTMLButtonElement>('[data-archive-download]').forEach(button=>button.addEventListener('click',()=>{const target=snapshots.find(item=>item.project.id===button.dataset.archiveDownload);if(target)void downloadFiles(archiveAllFiles(target))}));root.querySelectorAll<HTMLButtonElement>('[data-archive-menu]').forEach(button=>button.addEventListener('click',()=>{const target=snapshots.find(item=>item.project.id===button.dataset.archiveMenu);if(target)void downloadFiles(archiveAllFiles(target))}));
}

function collectPreferences(root:HTMLElement,fallback:Preferences):Preferences{const scopes=[...root.querySelectorAll<HTMLInputElement>('input[name="reviewScope"]')],focus=[...root.querySelectorAll<HTMLInputElement>('input[name="reviewFocus"]:checked')].map(input=>input.value);return normalizePreferences({reviewPositionCompany:root.querySelector<HTMLInputElement>('#reviewPosition')?.value.trim()||fallback.reviewPositionCompany,reviewerName:fallback.reviewerName,contractType:root.querySelector<HTMLSelectElement>('#contractType')?.value||fallback.contractType,reviewDepth:root.querySelector<HTMLInputElement>('input[name="reviewDepth"]:checked')?.value==='standard'?'standard':'deep',reviewFocus:focus,scopes:{legal:scopes.find(item=>item.value==='legal')?.checked??false,business:scopes.find(item=>item.value==='business')?.checked??false,compliance:scopes.find(item=>item.value==='compliance')?.checked??false},scopeAnnotationStyles:fallback.scopeAnnotationStyles,complianceLibraryScope:fallback.complianceLibraryScope||'personal',complianceLibraryFolders:fallback.complianceLibraryFolders,selectedCompliancePolicyFileIds:fallback.selectedCompliancePolicyFileIds,enhancedFeatures:fallback.enhancedFeatures})}
function bindShared(root:HTMLElement):void{root.querySelectorAll<HTMLElement>('[data-review-route]').forEach(element=>element.addEventListener('click',event=>{event.preventDefault();const stage=element.dataset.reviewRoute as Stage|undefined;if(stage&&STAGES.some(item=>item.id===stage))navigate(pathFor(stage))}));root.querySelectorAll<HTMLElement>('[data-open-personal-space]').forEach(element=>element.addEventListener('click',()=>navigate('/tax-assistant/space')));root.querySelectorAll<HTMLButtonElement>('[data-download-annotated],[data-download-preview]').forEach(button=>{if(button.hasAttribute('data-legal-review-annotated-export')||button.hasAttribute('data-legal-review-revised-export'))return;button.addEventListener('click',()=>{const fileId=button.dataset.downloadAnnotated||button.dataset.downloadPreview;if(fileId)downloadCloudFile(fileId)})})}
function bindLegalReviewAnnotatedExport(root:HTMLElement,taskId:string,projectId:string,userReviewCompleted:boolean):void{
  root.querySelectorAll<HTMLButtonElement>('[data-legal-review-annotated-export]').forEach(button=>button.addEventListener('click',()=>{
    const completed=button.dataset.userReviewCompleted==='true'||userReviewCompleted;
    void downloadLegalReviewAnnotatedExport(taskId,projectId,completed).catch(error=>window.alert(error instanceof Error?error.message:'批注版下载失败'));
  }));
}
function bindLegalReviewRevisedExport(root:HTMLElement,taskId:string,projectId:string):void{
  root.querySelectorAll<HTMLButtonElement>('[data-legal-review-revised-export]').forEach(button=>button.addEventListener('click',()=>{
    void downloadLegalReviewRevisedExport(taskId,projectId).catch(error=>window.alert(error instanceof Error?error.message:'修改版下载失败'));
  }));
}
function setPhase(root:HTMLElement,active:number):void{root.querySelectorAll<HTMLElement>('[data-analysis-phase]').forEach((item,index)=>{item.classList.toggle('is-complete',index<active);item.classList.toggle('is-active',index===active);const state=item.querySelector('em');if(state)state.textContent=index<active?'完成':index===active?'处理中':'等待'})}

function activateLegalTask(taskId:string,organizationId:string):void{
  rememberLegalReviewTaskSelection(taskId,'legal-review');
  localStorage.setItem(`tax-assistant-active-task:${organizationId}`,taskId);
  localStorage.setItem(`tax-assistant-active-task-type:${organizationId}`,'legal-review');
}
async function loadProjectById(taskId:string,projectId:string):Promise<LegalReviewProject|null>{
  const projects=await loadLegalReviewProjects(taskId);
  return projects.find(item=>item.id===projectId)??null;
}
/** 工作台列表从空态切换为可展示项目行。 */
function prepareProjectTableForRows(root:HTMLElement):HTMLElement|null{
  const wrap=root.querySelector('.cr-project-table-wrap');
  wrap?.querySelector('.cr-project-empty')?.remove();
  wrap?.classList.remove('is-empty');
  return root.querySelector('#crProjectTableBody');
}
/** 向工作台表格追加一个项目行并绑定行内事件。 */
async function appendProjectTableRow(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string,project:LegalReviewProject,options?:{focus?:boolean;selected?:boolean}):Promise<ProjectSnapshot>{
  if(options?.focus){
    rememberOverviewFocus(taskId,project.id);
    rememberActiveProject(taskId,project.id);
  }
  const snapshot=await loadProjectSnapshot(taskId,project,companyName);
  const tbody=prepareProjectTableForRows(root);
  if(tbody){
    const index=tbody.querySelectorAll('.cr-project-table-row').length;
    const holder=document.createElement('tbody');
    holder.innerHTML=projectTableRows(taskId,snapshot,index,false,false,false,Boolean(options?.focus)||project.id===overviewFocusProjectId(taskId),Boolean(options?.selected),companyName,auth);
    holder.querySelectorAll('tr').forEach(tr=>tbody.appendChild(tr));
    bindOverviewRow(root,auth,taskId,companyName,project.id);
  }
  return snapshot;
}
/** 创建项目、上传正文并刷新列表行（拖放批量导入与单文件导入共用）。 */
async function createProjectWithMainFile(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string,file:File,focus:boolean):Promise<void>{
  const created=await api.post<{project:LegalReviewProject}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects`),{});
  await appendProjectTableRow(root,auth,taskId,companyName,created.project,{focus});
  const projectId=created.project.id;
  setProjectMainBodyUploading(root,projectId,`导入中 ${file.name}`);
  try{
    const uploaded=await uploadMainFile(taskId,projectId,file,percent=>{
      setProjectMainBodyUploading(root,projectId,percent>0&&percent<100?`上传中 ${percent}%`:'上传中');
    });
    localStorage.setItem(mainKey(taskId,projectId),uploaded.id);
    void ensurePreviewInspect(taskId,projectId,uploaded.id).catch(()=>{/** 预审在后台继续 */});
    await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
  }catch(error){
    window.alert(error instanceof Error?error.message:'上传合同正文失败');
    await refreshProjectRowDom(root,taskId,projectId,companyName,auth);
  }
}
/** 拖入多份正文时按文件数批量创建项目。 */
async function importMainFilesFromDrop(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string,dataTransfer:DataTransfer):Promise<void>{
  const files=collectMainFilesFromDataTransfer(dataTransfer);
  if(!files.length){
    window.alert('请拖入 DOC、DOCX、PDF 或 TXT 格式的合同正文。');
    return;
  }
  if(dataTransfer.files.length>LEGAL_MAIN_DROP_MAX){
    window.alert(`单次最多导入 ${LEGAL_MAIN_DROP_MAX} 份正文，已按前 ${LEGAL_MAIN_DROP_MAX} 份处理。`);
  }
  const createBtn=root.querySelector<HTMLButtonElement>('#createLegalProject');
  if(createBtn){
    createBtn.disabled=true;
    createBtn.classList.add('is-importing');
  }
  try{
    for(let index=0;index<files.length;index++){
      await createProjectWithMainFile(root,auth,taskId,companyName,files[index],index===files.length-1);
    }
    updateBatchToolbar(root,taskId);
  }finally{
    if(createBtn){
      createBtn.disabled=false;
      createBtn.classList.remove('is-importing');
    }
  }
}
/** 仅在「新建项目」按钮上接收拖入的正文，批量建项。 */
function bindCreateProjectDropTarget(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string):void{
  const button=root.querySelector<HTMLButtonElement>('#createLegalProject');
  if(!button)return;
  let dragDepth=0;
  const setDragHighlight=(active:boolean)=>{button.classList.toggle('is-drop-target',active)};
  const resetDrag=()=>{dragDepth=0;setDragHighlight(false)};
  button.addEventListener('dragenter',event=>{
    event.preventDefault();
    event.stopPropagation();
    dragDepth+=1;
    setDragHighlight(true);
  });
  button.addEventListener('dragover',event=>{
    event.preventDefault();
    event.stopPropagation();
    if(event.dataTransfer)event.dataTransfer.dropEffect='copy';
  });
  button.addEventListener('dragleave',event=>{
    event.stopPropagation();
    const related=event.relatedTarget as Node|null;
    if(related&&button.contains(related))return;
    dragDepth=Math.max(0,dragDepth-1);
    if(dragDepth===0)setDragHighlight(false);
  });
  button.addEventListener('drop',event=>{
    event.preventDefault();
    event.stopPropagation();
    resetDrag();
    legalCreateProjectClickSuppressUntil=Date.now()+LEGAL_CREATE_PROJECT_CLICK_SUPPRESS_MS;
    const dataTransfer=event.dataTransfer;
    if(!dataTransfer)return;
    const files=collectMainFilesFromDataTransfer(dataTransfer);
    if(!files.length){
      window.alert('请拖入 DOC、DOCX、PDF 或 TXT 格式的合同正文。');
      return;
    }
    void importMainFilesFromDrop(root,auth,taskId,companyName,dataTransfer);
  });
  window.addEventListener('dragend',resetDrag);
}
/** 合同审查工作台展示中（用于全局拖放拦截）。 */
function isLegalReviewWorkbenchVisible():boolean{
  return Boolean(document.querySelector('.cr-overview-full'));
}
/**
 * 工作台禁止浏览器直接打开拖入文件；仅「新建项目」与附件区可接收文件（全局只绑定一次）。
 */
let legalWorkbenchDragFileGuardBound=false;
function ensureLegalWorkbenchDragFileGuard():void{
  if(legalWorkbenchDragFileGuardBound)return;
  legalWorkbenchDragFileGuardBound=true;
  document.addEventListener('dragover',event=>{
    if(!isLegalReviewWorkbenchVisible()||!isFileDragEvent(event))return;
    event.preventDefault();
    if(event.dataTransfer)event.dataTransfer.dropEffect=isLegalReviewFileDropTarget(event.target)?'copy':'none';
  });
  document.addEventListener('drop',event=>{
    if(!isLegalReviewWorkbenchVisible()||!isFileDragEvent(event))return;
    if(isLegalReviewFileDropTarget(event.target))return;
    event.preventDefault();
    event.stopPropagation();
  });
}
function bindOverview(root:HTMLElement,auth:AuthSnapshot,taskId:string,companyName:string):void{
  ensureLegalWorkbenchDragFileGuard();
  bindCreateProjectDropTarget(root,auth,taskId,companyName);
  root.querySelectorAll('.cr-project-table-row').forEach(row=>{
    const projectId=row.getAttribute('data-project-id');
    if(projectId)bindOverviewRow(root,auth,taskId,companyName,projectId);
  });
  updateBatchToolbar(root,taskId);
  root.querySelector('th.cr-col-result')?.addEventListener('click',()=>{
    const activeId=overviewFocusProjectId(taskId)||activeLegalReviewProjectId(taskId);
    if(activeId){
      rememberActiveProject(taskId,activeId);
      navigate(pathFor('risks'));
    }
  });
  root.querySelector('#openTaskSchemeSettings')?.addEventListener('click',()=>navigate(pathFor('settings')));
  root.querySelector('#createLegalProject')?.addEventListener('click',async()=>{
    if(Date.now()<legalCreateProjectClickSuppressUntil)return;
    try{
      const created=await api.post<{project:LegalReviewProject}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects`),{});
      await appendProjectTableRow(root,auth,taskId,companyName,created.project,{focus:true});
      updateBatchToolbar(root,taskId);
    }catch(error){window.alert(error instanceof Error?error.message:'新建项目失败')}
  });
  root.querySelector('#crProjectSelectAll')?.addEventListener('change',event=>{
    const checked=(event.currentTarget as HTMLInputElement).checked;
    root.querySelectorAll<HTMLInputElement>('.cr-project-select').forEach(input=>{input.checked=checked});
    updateBatchToolbar(root,taskId);
  });
  root.querySelector('#batchArchiveProjects')?.addEventListener('click',async()=>{
    const ids=selectedProjectIds(root);
    for(const projectId of ids){
      const row=root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`);
      if(!row?.querySelector('.cr-row-status.is-done'))continue;
      await openProjectArchive(taskId,projectId);
      return;
    }
    window.alert('所选项目均未完成审核，暂不可归档。');
  });
  root.querySelector('#batchPreviewProjects')?.addEventListener('click',()=>{
    const ids=selectedProjectIds(root);
    const projectId=ids[0];
    if(!projectId)return;
    void openProjectPreview(root,taskId,projectId,companyName,auth);
  });
  root.querySelector('#batchDeleteProjects')?.addEventListener('click',async()=>{
    const ids=selectedProjectIds(root);
    if(!ids.length||!window.confirm(`确定删除选中的 ${ids.length} 个合同审核项目吗？项目文件夹内的文件将一并删除。`))return;
    try{
      for(const projectId of ids)await api.delete(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}`));
      ids.forEach(projectId=>{
        root.querySelector(`.cr-project-table-row[data-project-id="${projectId}"]`)?.remove();
        root.querySelector(`.cr-project-expand-row[data-project-id="${projectId}"]`)?.remove();
      });
      if(!root.querySelector('.cr-project-table-row')){
        const wrap=root.querySelector('.cr-project-table-wrap');
        wrap?.classList.add('is-empty');
        if(wrap&&!wrap.querySelector('.cr-project-empty')){
          wrap.insertAdjacentHTML('beforeend','<div class="cr-project-empty"><b>还没有合同审核项目</b><span>点击「新建项目」，或将多份正文拖到「新建项目」按钮上批量创建。</span></div>');
        }
      }
      updateBatchToolbar(root,taskId);
    }catch(error){window.alert(error instanceof Error?error.message:'删除失败')}
  });
  root.querySelector('#batchApplySettings')?.addEventListener('click',()=>{
    const ids=selectedProjectIds(root);
    if(!ids.length)return;
    void openSettingsDrawer(root,taskId,ids,companyName,auth);
  });
  root.querySelector('#batchStartReview')?.addEventListener('click',()=>{
    if(!isLegalFeatureEnabled('legal-ai-review-v1')){window.alert('智能审核已关闭');return}
    const ids=selectedProjectIds(root);
    const blocked=batchReviewBlockedReason(root,taskId,ids);
    if(blocked){window.alert(blocked);return}
    void runBatchReview(root,taskId,ids,auth,companyName);
  });
  root.querySelector('#crSettingsDrawerBackdrop')?.addEventListener('click',()=>closeSettingsDrawer(root));
  root.querySelector('#crSettingsDrawerClose')?.addEventListener('click',()=>closeSettingsDrawer(root));
  root.querySelector('#crSettingsDrawerCancel')?.addEventListener('click',()=>closeSettingsDrawer(root));
  root.querySelector('#crSettingsDrawerSave')?.addEventListener('click',()=>{
    void (async()=>{
      const drawer=root.querySelector<HTMLElement>('#crSettingsDrawer');
      const body=root.querySelector<HTMLElement>('#crSettingsDrawerBody');
      const message=root.querySelector<HTMLElement>('#crSettingsDrawerMessage');
      const projectIds=(drawer?.dataset.projectIds??'').split(',').filter(Boolean);
      if(!body)return;
      const positionRadio=body.querySelector<HTMLInputElement>('input[data-settings-position-radio]:checked');
      const positionSelect=body.querySelector<HTMLSelectElement>('[data-settings-position-select]');
      if(positionRadio?.value){
        const hidden=body.querySelector<HTMLInputElement>('[data-settings-position]');
        if(hidden)hidden.value=positionRadio.value;
      }else if(positionSelect?.value==='__custom__'){
        const confirmed=body.querySelector<HTMLInputElement>('[data-settings-position]')?.value.trim();
        const draft=body.querySelector<HTMLInputElement>('[data-settings-position-draft]')?.value.trim();
        if(!confirmed&&!draft){if(message)message.textContent='请填写并确认审核立场';return}
      }else if(!positionSelect?.value){if(message)message.textContent='请选择审核立场';return}
      const fallback=await loadTaskSchemeFromCloud(taskId,companyName);
      const next=collectSettingsPreferences(body,fallback);
      if(!Object.values(next.scopes).some(Boolean)){if(message)message.textContent='请至少选择一个审核范围';return}
      if(message)message.textContent='正在保存…';
      try{
        await saveTaskSchemeToCloud(taskId,next);
      }catch(error){
        if(message)message.textContent=error instanceof Error?error.message:'默认方案保存失败';
        return;
      }
      if(message)message.textContent='任务默认方案已保存';
      await Promise.all(projectIds.map(projectId=>refreshProjectRowDom(root,taskId,projectId,companyName,auth)));
      closeSettingsDrawer(root);
      updateBatchToolbar(root,taskId);
    })();
  });
}

export async function renderLegalContractReviewPage(root:HTMLElement,auth:AuthSnapshot):Promise<void>{
  const stage=currentStage();
  stopAllWorkbenchReviewPollers();
  const taskId=activeLegalReviewTaskId()||activeTaskId(auth);
  const taskType=activeLegalReviewTaskType()||localStorage.getItem(`tax-assistant-active-task-type:${auth.organization.id}`)||'';
  if(!taskId||taskType!=='legal-review'){
    renderLayout(root,auth,overviewTaskRequired(taskId));
    root.querySelector('#chooseLegalTask')?.addEventListener('click',()=>navigate('/tax-assistant/tasks'));
    return;
  }
  activateLegalTask(taskId,auth.organization.id);
  renderLayout(root,auth,'<div class="cr-page"><div class="cr-page-loading"><span></span><b>正在准备合同审核工作台</b><small>读取个人空间中的任务文件与审核记录</small></div></div>');
  try{
    const [taskPayload,orgPayload]=await Promise.all([
      api.get<{task:{name:string;companyName:string;organizationCode?:string}}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}`)),
      api.get<{organizations:UserOrganization[]}>(apiUrl('/api/organizations')).catch(()=>({organizations:[] as UserOrganization[]})),
    ]);
    const taskName=taskPayload.task?.name||'未命名合同审核任务';
    const enterpriseNames=resolveLegalReviewEnterpriseNames(taskPayload.task??{},auth,orgPayload.organizations??[]);
    rememberLegalReviewEnterpriseNames(taskId,enterpriseNames);
    rememberLegalReviewHasEnterprise(taskId,(orgPayload.organizations??[]).some(isActiveEnterpriseOrganization));
    const companyName=enterpriseNames[0]||'';
    if(stage==='overview'){
      const projects=await loadLegalReviewProjects(taskId);
      const taskSchemeValue=await loadTaskSchemeFromCloud(taskId,companyName);
      overviewComplianceContext=await loadComplianceContext(orgPayload.organizations??[],taskSchemeValue.complianceLibraryFolders);
      const expanded=loadExpandedProjectIds(taskId);
      const previewExpanded=loadPreviewExpandedProjectIds(taskId);
      const settingsExpanded=loadSettingsExpandedProjectIds(taskId);
      const snapshots=await Promise.all(projects.map(project=>loadProjectSnapshot(taskId,project,companyName)));
      snapshots.forEach(snapshot=>scheduleBackgroundPreview(taskId,snapshot.project.id,snapshot));
      const focusProjectId=overviewFocusProjectId(taskId)||activeLegalReviewProjectId(taskId)||snapshots[0]?.project.id||'';
      if(focusProjectId)rememberOverviewFocus(taskId,focusProjectId);
      const focusSnapshot=snapshots.find(item=>item.project.id===focusProjectId)??snapshots[0]??null;
      const taskSchemeConfigured=Boolean(taskSchemeCache.get(taskId)?.savedAt);
      const workflowNav=nav(Boolean(focusSnapshot?.main),taskSchemeConfigured,focusSnapshot?.review??null,true);
      const body=projectHub(taskName,taskId,snapshots,expanded,previewExpanded,settingsExpanded,focusProjectId,companyName,auth,workflowNav);
      renderLayout(root,auth,`<div class="cr-page">${header(LEGAL_REVIEW_WORKBENCH_TITLE,companyName,focusSnapshot?.review??null)}<div class="cr-stage-content">${body}</div></div>`);
      bindShared(root);bindOverview(root,auth,taskId,companyName);
      snapshots.filter(snapshot=>snapshot.review?.status==='in_progress').forEach(snapshot=>startWorkbenchReviewPolling(root,taskId,snapshot.project.id,companyName,auth));
      return;
    }
    if(stage==='settings'){
      const userDisplayName=auth.user.displayName||auth.user.email||'';
      const [value,projects]=await Promise.all([loadTaskSchemeFromCloud(taskId,companyName),loadLegalReviewProjects(taskId)]);
      const snapshots=await Promise.all(projects.map(project=>loadProjectSnapshot(taskId,project,companyName)));
      const stats=computeTaskSchemeStats(taskId,snapshots);
      const complianceContext=await loadComplianceContext(orgPayload.organizations??[],value.complianceLibraryFolders);
      const body=taskSchemeSettingsPage(taskName,taskId,value,stats,companyName,userDisplayName,complianceContext);
      renderLayout(root,auth,`<div class="cr-page cr-settings-page"><div class="cr-stage-content">${body}</div></div>`);
      bindShared(root);
      bindTaskSchemeSettings(root,taskId,companyName,userDisplayName,complianceContext);
      return;
    }
    if(stage==='deliverables'){
      renderLayout(root,auth,`<div class="cr-page cr-archive-host">${archivePageShell()}</div>`);
      bindShared(root);
      await mountArchivePage(root,archiveActions(taskId),orgPayload.organizations??[]);
      return;
    }
    const projectId=activeLegalReviewProjectId(taskId)||overviewFocusProjectId(taskId);
    if(!projectId){
      renderLayout(root,auth,`<div class="cr-page">${header(LEGAL_REVIEW_WORKBENCH_TITLE,companyName,null)}${nav(false,false,null)}<div class="cr-stage-content"><section class="cr-inline-alert"><div><b>请先选择合同审核项目</b><p>返回工作台总览，选择或新建一个项目后再继续审核流程。</p></div><button class="cr-button cr-button-primary" data-review-route="overview">返回工作台</button></section></div></div>`);
      bindShared(root);return;
    }
    rememberActiveProject(taskId,projectId);
    const status=await api.get<ReviewStatus>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/status`));
    rememberTaskScheme(taskId,status.taskScheme??null);
    rememberProjectPreferences(taskId,projectId,status.preferences??null);
    const migrated=await migrateLocalPreferencesIfNeeded(taskId,projectId,companyName,status.preferences??null,status.preferencesConfigured);
    const savedPreferences=migrated??cloudPreferencesCache.get(preferencesCacheKey(taskId,projectId))??status.preferences??null;
    if(savedPreferences)rememberProjectPreferences(taskId,projectId,savedPreferences);
    const writingGuidance=status.writingGuidance??status.review?.writingGuidance??[];
    const stageSnapshot:ProjectSnapshot={project:{id:projectId,name:'',createdAt:'',updatedAt:''},files:status.files??{mainFiles:[],attachmentFiles:[],annotatedFiles:[]},review:status.review,risks:status.risks??[],writingGuidance,main:selectedMain(taskId,projectId,(status.files??{mainFiles:[]}).mainFiles),quickProfile:status.quickProfiles?.find(item=>item.fileId===selectedMain(taskId,projectId,(status.files??{mainFiles:[]}).mainFiles)?.id)??null,protectedIds:new Set(status.protectedFileIds??[]),preferences:savedPreferences,preferencesConfigured:Boolean(status.preferencesConfigured||savedPreferences?.savedAt),taskScheme:status.taskScheme??null,projectPreferences:status.projectPreferences??null};
    const files=stageSnapshot.files,review=stageSnapshot.review,risks=stageSnapshot.risks,main=stageSnapshot.main,quickProfile=stageSnapshot.quickProfile,preferences=resolveProjectSettingsPreferences(taskId,projectId,companyName,stageSnapshot);
    const projectName=(await loadLegalReviewProjects(taskId)).find(item=>item.id===projectId)?.name||'合同审核项目';
    let body='';if(stage==='materials')body=materials(files,main);else if(stage==='analysis')body=analysisWithProgress(main,files,preferences,review);else if(stage==='risks')body=risksPage(risks,review,writingGuidance);else body=deliverables(files,review,risks);
    const pageTitle=`${LEGAL_REVIEW_WORKBENCH_TITLE} · ${projectName}`;
    const pageShell=stage==='risks'?'':`${header(pageTitle,companyName,review)}${nav(Boolean(main),hasSavedPreferences(taskId,projectId,stageSnapshot),review)}`;
    renderLayout(root,auth,`<div class="cr-page">${pageShell}<div class="cr-stage-content">${body}</div></div>`);bindShared(root);const message=root.querySelector<HTMLElement>('#reviewMessage');
    if(stage==='materials'){
      const mainInput=root.querySelector<HTMLInputElement>('#contractMainFile'),attachmentInput=root.querySelector<HTMLInputElement>('#contractAttachmentFiles');root.querySelector('#pickMainFile')?.addEventListener('click',()=>mainInput?.click());root.querySelector('#emptyMainUpload')?.addEventListener('click',()=>mainInput?.click());root.querySelector('#pickAttachmentFiles')?.addEventListener('click',()=>attachmentInput?.click());root.querySelector('#emptyAttachmentUpload')?.addEventListener('click',()=>attachmentInput?.click());
      const uploadMaterialsAttachments=async(selected:File[])=>{
        if(!selected.length)return;
        try{if(message)message.textContent=`正在上传 ${selected.length} 份附件并归档至个人空间…`;for(const file of selected)await uploadFile(taskId,projectId,file,'attachment');await renderLegalContractReviewPage(root,auth)}catch(error){if(message)message.textContent=error instanceof Error?error.message:String(error)}finally{if(attachmentInput)attachmentInput.value=''}
      };
      mainInput?.addEventListener('change',async()=>{const file=mainInput.files?.[0];if(!file)return;try{if(message)message.textContent='正在上传合同正文并归档至个人空间…';const uploaded=await uploadFile(taskId,projectId,file,'main');localStorage.setItem(mainKey(taskId,projectId),uploaded.id);if(message)message.textContent='正在快速识别合同类型与当事方…';try{await ensurePreviewInspect(taskId,projectId,uploaded.id)}catch{if(message)message.textContent='合同已上传；快速预审将在接口发布后可用。'}await renderLegalContractReviewPage(root,auth)}catch(error){if(message)message.textContent=error instanceof Error?error.message:String(error)}finally{mainInput.value=''}});
      attachmentInput?.addEventListener('change',async()=>{await uploadMaterialsAttachments(Array.from(attachmentInput.files??[]))});
      const materialsDrop=root.querySelector<HTMLElement>('#attachmentDropzone');
      if(materialsDrop)bindFileDropZone(materialsDrop,files=>{void uploadMaterialsAttachments(files)});
      root.querySelectorAll<HTMLButtonElement>('[data-select-main]').forEach(button=>button.addEventListener('click',async()=>{if(button.dataset.selectMain)localStorage.setItem(mainKey(taskId,projectId),button.dataset.selectMain);await renderLegalContractReviewPage(root,auth)}));
      root.querySelectorAll<HTMLButtonElement>('.legal-review-file-delete').forEach(button=>button.addEventListener('click',async()=>{const fileId=button.dataset.fileId;if(!fileId||!window.confirm('确定从个人空间中删除该待审核文件吗？'))return;try{button.disabled=true;await api.delete(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/files/${encodeURIComponent(fileId)}?projectId=${encodeURIComponent(projectId)}`));if(localStorage.getItem(mainKey(taskId,projectId))===fileId)localStorage.removeItem(mainKey(taskId,projectId));await renderLegalContractReviewPage(root,auth)}catch(error){if(message)message.textContent=error instanceof Error?error.message:String(error);button.disabled=false}}));
      root.querySelector('#gotoProjectSettings')?.addEventListener('click',()=>navigateToProjectSettingsOnOverview(taskId,projectId));
    }
    if(stage==='analysis')root.querySelector<HTMLButtonElement>('#startReview')?.addEventListener('click',async()=>{const button=root.querySelector<HTMLButtonElement>('#startReview');if(!main)return;if(!isLegalFeatureEnabled('legal-ai-review-v1')){if(message)message.textContent='智能审核已关闭';return}let phase=0;const timer=window.setInterval(()=>{phase=Math.min(4,phase+1);setPhase(root,phase)},18000);try{if(button){button.disabled=true;button.textContent='审核已在后台运行…'}if(message)message.textContent='审核已在后台启动，可以离开页面；返回工作台即可继续查看进度';setPhase(root,0);await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/review`),{projectId,fileId:main.id,attachmentFileIds:files.attachmentFiles.map(file=>file.id),reviewPositionCompany:preferences.reviewPositionCompany,reviewerName:preferences.reviewerName||auth.user.displayName||auth.user.email||'法务审核',contractType:preferences.contractType,reviewDepth:preferences.reviewDepth,reviewFocus:preferences.reviewFocus,scopes:preferences.scopes,complianceLibraryScope:preferences.complianceLibraryScope},{timeoutMs:30000});setPhase(root,5);navigate(pathFor('overview'))}catch(error){if(message)message.textContent=error instanceof Error?error.message:String(error);if(button){button.disabled=false;button.textContent='重新开始审查'}}finally{window.clearInterval(timer)}});
    if(stage==='risks'){
      let filter='all';
      const checkedRiskIds=new Set<string>();
      const decisionByRisk=new Map<string,string>();
      risks.forEach(risk=>{if(risk.userDecision)decisionByRisk.set(risk.id,risk.userDecision)});
      const riskMap=new Map(risks.map(risk=>[risk.id,risk]));
      root.querySelectorAll<HTMLButtonElement>('[data-result-view]').forEach(button=>{
        button.addEventListener('click',()=>{
          const writing=button.getAttribute('data-result-view')==='writing';
          const page=root.querySelector<HTMLElement>('.cr-result-page');
          page?.classList.toggle('is-writing-view',writing);
          root.querySelector<HTMLElement>('.content.legal-review-result-content')?.classList.toggle('legal-review-writing-view',writing);
          const panel=root.querySelector<HTMLElement>('.cr-writing-guidance-panel');
          if(panel)panel.hidden=!writing;
          root.querySelectorAll<HTMLButtonElement>('[data-result-view]').forEach(item=>{
            item.classList.toggle('is-active',item===button);
          });
          if(writing)bindWritingGuidanceScrollLayout(root);
        });
      });
      bindWritingGuidanceScrollLayout(root);
      root.querySelectorAll<HTMLButtonElement>('[data-writing-select]').forEach(button=>{
        button.addEventListener('click',()=>{
          const id=button.getAttribute('data-writing-select')||'';
          root.querySelectorAll<HTMLButtonElement>('[data-writing-select]').forEach(item=>item.classList.toggle('is-current',item===button));
          root.querySelectorAll<HTMLElement>('[data-writing-id]').forEach(card=>card.classList.toggle('is-current',card.getAttribute('data-writing-id')===id));
        });
      });
      root.querySelectorAll<HTMLButtonElement>('[data-copy-writing]').forEach(button=>{
        button.addEventListener('click',async()=>{
          const text=button.closest('article')?.querySelector('.cr-writing-copy-source')?.textContent||button.getAttribute('data-copy-writing')||'';
          if(!text)return;
          try{
            await navigator.clipboard.writeText(text);
            const prev=button.textContent;
            button.textContent='已复制';
            window.setTimeout(()=>{button.textContent=prev||'复制建议'},1600);
          }catch{
            window.alert('复制失败，请手动选择文本');
          }
        });
      });
      root.querySelector<HTMLButtonElement>('[data-complete-user-review]')?.addEventListener('click',async()=>{
        const button=root.querySelector<HTMLButtonElement>('[data-complete-user-review]');
        try{
          if(button){button.disabled=true;button.textContent='正在生成批注版与修改版…'}
          const result=await api.post<{annotatedFileId:string;revisedFileId?:string;revisedWarning?:string}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/complete-user-review`),{},{timeoutMs:600000});
          if(button){button.textContent='已完成复核';button.disabled=true}
          root.querySelectorAll<HTMLButtonElement>('[data-legal-review-annotated-export]').forEach(downloadButton=>{
            downloadButton.disabled=!result.annotatedFileId;
            downloadButton.dataset.userReviewCompleted='true';
            downloadButton.textContent='下载批注版';
          });
          root.querySelectorAll<HTMLButtonElement>('[data-legal-review-revised-export]').forEach(downloadButton=>{
            downloadButton.disabled=false;
            downloadButton.dataset.userReviewCompleted='true';
          });
          if(result.revisedWarning){
            window.alert(`批注版已生成，修改版未能生成：${result.revisedWarning}`);
          }
        }catch(error){
          window.alert(error instanceof Error?error.message:'完成复核失败');
          if(button){button.disabled=false;button.textContent='完成复核'}
        }
      });
      bindLegalReviewAnnotatedExport(root,taskId,projectId,Boolean(review?.userReviewCompletedAt));
      bindLegalReviewRevisedExport(root,taskId,projectId);
      const documentBody=root.querySelector<HTMLElement>('[data-result-document]');
      const hydrateResultDocument=async():Promise<void>=>{
        if(!documentBody)return;
        let previewId=quickProfile?.previewFileId||'';
        if(!previewId){try{previewId=await resolvePreviewFileId(taskId,projectId,quickProfile?.fileId||'')}catch{previewId=''}}
        if(previewId){try{const payload=await fetchPreviewPayload(previewId);const text=String(payload?.text??'').trim();if(text){documentBody.innerHTML=renderResultDocumentHtml(text,risks);return}}catch{/** 使用风险原文兜底 */}}
        documentBody.innerHTML=renderResultDocumentHtml('',risks);
      };
      let annotateDraftTimer=0;
      const persistRiskReviewDraft=async(riskId:string)=>{
        const suggestion=root.querySelector<HTMLTextAreaElement>('[data-risk-detail-suggestion]')?.value??'';
        const parts=readRiskAnnotateParts(root);
        try{
          const payload=await api.post<{risks:LegalRisk[]}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/risk-decisions`),{
            riskId,
            suggestedRevision:suggestion,
            annotateIncludeDescription:parts.annotateIncludeDescription,
            annotateIncludeSuggestion:parts.annotateIncludeSuggestion,
          });
          const updated=payload.risks?.find(item=>item.id===riskId);
          if(updated)riskMap.set(riskId,updated);
        }catch{/** 草稿保存失败时静默，采纳/不采纳会再次提交 */}}
      const queueRiskReviewDraft=(riskId:string)=>{
        window.clearTimeout(annotateDraftTimer);
        annotateDraftTimer=window.setTimeout(()=>{void persistRiskReviewDraft(riskId)},400);
      };
      root.querySelector<HTMLTextAreaElement>('[data-risk-detail-suggestion]')?.addEventListener('input',()=>{
        const selected=root.querySelector<HTMLElement>('[data-risk-select].is-selected');
        const riskId=selected?.dataset.riskSelect??'';
        if(riskId)queueRiskReviewDraft(riskId);
      });
      root.querySelectorAll<HTMLInputElement>('[data-annotate-include-description],[data-annotate-include-suggestion]').forEach(input=>{
        input.addEventListener('change',()=>{
          const selected=root.querySelector<HTMLElement>('[data-risk-select].is-selected');
          const riskId=selected?.dataset.riskSelect??'';
          if(riskId)void persistRiskReviewDraft(riskId);
        });
      });
      const syncRiskBatchUi=()=>{
        const summary=root.querySelector<HTMLElement>('#riskBatchSummary');
        const acceptBtn=root.querySelector<HTMLButtonElement>('[data-risk-batch-accept]');
        const dismissBtn=root.querySelector<HTMLButtonElement>('[data-risk-batch-dismiss]');
        const selectAll=root.querySelector<HTMLInputElement>('#riskSelectAllVisible');
        const visible=visibleRiskRows(root);
        const visibleIds=new Set(visible.map(row=>row.dataset.riskSelect??'').filter(Boolean));
        for(const id of [...checkedRiskIds])if(!visibleIds.has(id))checkedRiskIds.delete(id);
        visible.forEach(row=>{
          const id=row.dataset.riskSelect??'';
          const box=row.querySelector<HTMLInputElement>('[data-risk-check]');
          if(box)box.checked=checkedRiskIds.has(id);
        });
        const count=[...checkedRiskIds].filter(id=>visibleIds.has(id)).length;
        if(summary)summary.textContent=`已选 ${count} 条`;
        if(acceptBtn)acceptBtn.disabled=count===0;
        if(dismissBtn)dismissBtn.disabled=count===0;
        if(selectAll){
          const selectedVisible=visible.filter(row=>checkedRiskIds.has(row.dataset.riskSelect??'')).length;
          selectAll.indeterminate=selectedVisible>0&&selectedVisible<visible.length;
          selectAll.checked=visible.length>0&&selectedVisible===visible.length;
        }
      };
      const apply=()=>{
        const query=root.querySelector<HTMLInputElement>('#riskSearch')?.value.trim().toLowerCase()??'';
        const items=[...root.querySelectorAll<HTMLElement>('[data-risk-select]')];
        let pendingCount=0,doneCount=0;
        items.forEach(item=>{
          const decision=decisionByRisk.get(item.dataset.riskSelect??'')??'pending';
          if(decision==='pending')pendingCount++;else doneCount++;
          const matchesFilter=filter==='all'||(filter==='pending'&&decision==='pending')||(filter==='done'&&decision!=='pending');
          item.hidden=!(matchesFilter&&(!query||(item.dataset.riskText??'').includes(query)));
        });
        root.querySelectorAll<HTMLElement>('[data-risk-filter]').forEach(btn=>{
          const f=btn.dataset.riskFilter;
          if(f==='all')btn.textContent=`全部 ${items.length}`;
          if(f==='pending')btn.textContent=`待确认 ${pendingCount}`;
          if(f==='done')btn.textContent=`已处理 ${doneCount}`;
        });
        syncRiskBatchUi();
      };
      const submitBatchRiskDecisions=async(decision:'accepted'|'dismissed')=>{
        const riskIds=[...checkedRiskIds].filter(id=>visibleRiskRows(root).some(row=>row.dataset.riskSelect===id));
        if(!riskIds.length)return;
        const acceptBtn=root.querySelector<HTMLButtonElement>('[data-risk-batch-accept]');
        const dismissBtn=root.querySelector<HTMLButtonElement>('[data-risk-batch-dismiss]');
        try{
          if(acceptBtn)acceptBtn.disabled=true;
          if(dismissBtn)dismissBtn.disabled=true;
          const payload=await api.post<{risks:LegalRisk[]}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/risk-decisions`),{riskIds,decision});
          payload.risks?.forEach(risk=>{
            riskMap.set(risk.id,risk);
            if(risk.userDecision)decisionByRisk.set(risk.id,risk.userDecision);
          });
          riskIds.forEach(id=>{
            decisionByRisk.set(id,decision);
            const row=root.querySelector<HTMLElement>(`[data-risk-select="${CSS.escape(id)}"]`);
            if(row)applyRiskRowDecisionUi(row,decision);
          });
          checkedRiskIds.clear();
          apply();
          const nextPending=visibleRiskRows(root).find(row=>decisionByRisk.get(row.dataset.riskSelect??'')==='pending');
          if(nextPending)selectRisk(nextPending.dataset.riskSelect??'');
        }catch(error){window.alert(error instanceof Error?error.message:'批量保存复核结论失败')}
        finally{syncRiskBatchUi()}
      };
      const selectRisk=(riskId:string)=>{
        const risk=riskMap.get(riskId);
        if(!risk)return;
        root.querySelectorAll<HTMLElement>('[data-risk-select]').forEach(item=>item.classList.toggle('is-selected',item.dataset.riskSelect===riskId));
        root.querySelectorAll<HTMLElement>('[data-risk-clause]').forEach(item=>item.classList.toggle('is-highlighted',item.dataset.riskClause===riskId));
        const documentScroller=root.querySelector<HTMLElement>('.cr-result-document-scroll');
        const target=documentScroller?.querySelector<HTMLElement>(`.cr-result-document-line[data-risk-clause="${CSS.escape(riskId)}"]`)
          ??documentScroller?.querySelector<HTMLElement>(`[data-risk-clause="${CSS.escape(riskId)}"]`);
        if(target&&documentScroller)scrollClauseToScrollerTop(target,documentScroller);
        const title=root.querySelector<HTMLElement>('[data-risk-detail-title]'),meta=root.querySelector<HTMLElement>('[data-risk-detail-meta]'),description=root.querySelector<HTMLElement>('[data-risk-detail-description]'),suggestion=root.querySelector<HTMLTextAreaElement>('[data-risk-detail-suggestion]'),indexEl=root.querySelector<HTMLElement>('.cr-result-detail-index'),descInclude=root.querySelector<HTMLInputElement>('[data-annotate-include-description]'),suggInclude=root.querySelector<HTMLInputElement>('[data-annotate-include-suggestion]');
        const currentIndex=risks.findIndex(r=>r.id===riskId);
        if(indexEl&&currentIndex>=0)indexEl.textContent=String(currentIndex+1).padStart(2,'0');
        if(title)title.textContent=risk.category;
        if(meta)meta.textContent=risk.reviewType?typeLabel(risk.reviewType):'综合审查';
        if(description)description.textContent=risk.description;
        if(suggestion)suggestion.value=risk.userSuggestedRevision||risk.suggestedRevision||'请结合本项目交易安排补充具体修改表述。';
        if(descInclude)descInclude.checked=risk.annotateIncludeDescription!==false;
        if(suggInclude)suggInclude.checked=risk.annotateIncludeSuggestion!==false;
      };
      root.querySelectorAll<HTMLElement>('[data-risk-select]').forEach(item=>item.addEventListener('click',event=>{
        if((event.target as HTMLElement).closest('.cr-result-risk-check'))return;
        selectRisk(item.dataset.riskSelect??'');
      }));
      root.querySelectorAll<HTMLInputElement>('[data-risk-check]').forEach(box=>{
        box.addEventListener('click',event=>event.stopPropagation());
        box.addEventListener('change',()=>{
          const id=box.dataset.riskCheck??'';
          if(box.checked)checkedRiskIds.add(id);else checkedRiskIds.delete(id);
          syncRiskBatchUi();
        });
      });
      root.querySelector<HTMLInputElement>('#riskSelectAllVisible')?.addEventListener('change',event=>{
        const input=event.currentTarget as HTMLInputElement;
        visibleRiskRows(root).forEach(row=>{
          const id=row.dataset.riskSelect??'';
          if(input.checked)checkedRiskIds.add(id);else checkedRiskIds.delete(id);
        });
        syncRiskBatchUi();
      });
      root.querySelector<HTMLButtonElement>('[data-risk-batch-accept]')?.addEventListener('click',()=>{void submitBatchRiskDecisions('accepted')});
      root.querySelector<HTMLButtonElement>('[data-risk-batch-dismiss]')?.addEventListener('click',()=>{void submitBatchRiskDecisions('dismissed')});
      root.querySelectorAll<HTMLButtonElement>('[data-risk-filter]').forEach(button=>button.addEventListener('click',()=>{filter=button.dataset.riskFilter??'all';root.querySelectorAll('[data-risk-filter]').forEach(item=>item.classList.remove('is-active'));button.classList.add('is-active');apply()}));
      root.querySelector<HTMLInputElement>('#riskSearch')?.addEventListener('input',apply);
      const reviewBasisCache=new Map<string,ReviewBasisPayload>();
      const openBasisForRisk=async(risk:LegalRisk):Promise<void>=>{
        const basisButton=root.querySelector<HTMLButtonElement>('[data-risk-view-basis]');
        const basisButtonLabel=basisButton?.textContent??'查看依据';
        try{
          if(basisButton){basisButton.disabled=true;basisButton.textContent='正在生成依据…';}
          let basis=reviewBasisCache.get(risk.id);
          if(!basis){
            basis=await api.get<ReviewBasisPayload>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/review-basis?riskId=${encodeURIComponent(risk.id)}`));
            reviewBasisCache.set(risk.id,basis);
          }
          openReviewBasisModal(root,risk,basis);
        }catch(error){window.alert(error instanceof Error?error.message:'审核依据暂时无法加载')}
        finally{if(basisButton){basisButton.disabled=false;basisButton.textContent=basisButtonLabel;}}
      };
      root.querySelector<HTMLButtonElement>('[data-risk-view-basis]')?.addEventListener('click',()=>{
        const selected=root.querySelector<HTMLElement>('[data-risk-select].is-selected');
        const risk=riskMap.get(selected?.dataset.riskSelect??'');
        if(risk)void openBasisForRisk(risk);
      });
      root.querySelectorAll<HTMLButtonElement>('[data-risk-action]').forEach(button=>button.addEventListener('click',()=>{
        void (async()=>{
          const selected=root.querySelector<HTMLElement>('[data-risk-select].is-selected');
          const riskId=selected?.dataset.riskSelect??'';
          if(!riskId)return;
          const action=button.dataset.riskAction??'';
          if(action==='edit'){root.querySelector<HTMLTextAreaElement>('[data-risk-detail-suggestion]')?.focus();return}
          const suggestion=root.querySelector<HTMLTextAreaElement>('[data-risk-detail-suggestion]')?.value??'';
          const parts=readRiskAnnotateParts(root);
          const decision=action==='accept'?'accepted':'dismissed';
          const visibleBeforeDecision=visibleRiskRows(root);
          const decisionRowIndex=visibleBeforeDecision.findIndex(item=>item.dataset.riskSelect===riskId);
          try{
            button.disabled=true;
            const payload=await api.post<{risks:LegalRisk[]}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/legal-review/projects/${encodeURIComponent(projectId)}/risk-decisions`),{riskId,decision,suggestedRevision:suggestion,annotateIncludeDescription:parts.annotateIncludeDescription,annotateIncludeSuggestion:parts.annotateIncludeSuggestion});
            const updated=payload.risks?.find(item=>item.id===riskId);
            if(updated)riskMap.set(riskId,updated);
            decisionByRisk.set(riskId,decision);
            if(selected)applyRiskRowDecisionUi(selected,decision);
            apply();
            if(decisionRowIndex>=0)focusNextRiskAfterDecision(root,riskId,filter,decisionRowIndex,selectRisk);
          }catch(error){window.alert(error instanceof Error?error.message:'保存复核结论失败')}
          finally{button.disabled=false}
        })();
      }));
      apply();
      void hydrateResultDocument().then(()=>{if(risks[0])selectRisk(risks[0].id)});
    }
  }catch(error){renderLayout(root,auth,`<div class="cr-page"><section class="cr-inline-alert is-error"><div><b>合同审核工作台暂时无法加载</b><p>${escapeHtml(error instanceof Error?error.message:String(error))}</p></div><button class="cr-button cr-button-secondary" id="retryReviewPage">重新加载</button></section></div>`);root.querySelector('#retryReviewPage')?.addEventListener('click',()=>void renderLegalContractReviewPage(root,auth))}
}
